import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiError } from '@/lib/queryClient';
import { useLocation } from 'wouter';
import { Activity, Bug, ChevronDown, ChevronLeft, Sparkles, Grid3X3, User, Upload, Send, X, Check, Minus, Plus, Calendar, Users, TrendingUp, FileText, BarChart3, Globe, Settings, LogOut, Bell, Search, Command as CommandIcon, Phone, MessageSquare, ChevronRight, Loader2, Folder, MoreVertical, Edit, Edit2, Trash2, FolderOpen, FolderPlus, Receipt, CreditCard, DollarSign, Plane, Filter, LayoutGrid, Moon, ArrowUpRight, Download, Copy as CopyIcon, UserRound, Maximize2, Eye, ChevronUp, AlertCircle, BookOpen, Zap, Newspaper, Tag, PlayCircle, Inbox } from 'lucide-react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import vscDarkPlus from 'react-syntax-highlighter/dist/esm/styles/prism/vsc-dark-plus';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion } from '@/components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Command, CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { PromptLibrarySubmenu } from '@/components/threads/PromptLibrarySubmenu';
import { MyPromptsDialog } from '@/components/threads/MyPromptsDialog';
import { substituteVariables, extractProfileValues, type PromptTemplate } from '@/hooks/usePromptLibrary';
import { normalizeToTokens, rankSuggestedTemplates } from '@shared/promptSuggestionMatcher';
import { AdvancedThreadsSubmenu } from '@/components/threads/AdvancedThreadsSubmenu';
import { DefaultModelSheet } from '@/components/threads/DefaultModelSheet';
import { ExternalModeControls, ExternalModeIndicator } from '@/components/threads/ExternalModeControls';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HelpCircle, CheckCircle2 } from 'lucide-react';
import { UserAvatar } from '@/components/UserAvatar';
import { useGravatar } from '@/hooks/useGravatar';
import { AGENT_MAX_FILES, agentAttachmentLimit, formatFileSize } from '@/features/threads-agent/attachmentLimits';
import { GravatarService } from '@/lib/gravatar';
import { format } from 'date-fns';
import { Textarea } from '@/components/ui/textarea';
import CanvasDashboard from './CanvasDashboard';
import CanvasClients from './CanvasClients';
import CanvasTrips from './CanvasTrips';
import { useToast } from '@/hooks/use-toast';
import { getFeatureFlags, isFeatureEnabled } from '@/config/featureFlags';
import { useResolvedFeatureFlags } from '@/hooks/useResolvedFeatureFlags';
import { FINANCES_PATHS } from '@/lib/financesPaths';
import { DEFAULT_AI_MODEL, DEFAULT_AI_PROVIDER } from '@/config/aiDefaults';
import DeferredFeaturePlaceholder from '@/components/DeferredFeaturePlaceholder';
import { formatName, formatTripLabel } from '@/components/threads/ThreadContextPanel';
import AgenticErrorNotice from '@/components/threads/AgenticErrorNotice';
import { SlashCommandBlocks } from '@/components/threads/SlashCommandBlocks';
import { isAdminRole, isSystemAdminRole } from '@/lib/roles';
import { useAlphaFeatureGates, isFeatureGated, AlphaFeatureGates } from '@/hooks/useAlphaFeatureGates';
import { GlobalHeader, BELOW_CHROME_HEIGHT } from '@/components/GlobalHeader';
import { MobileTabBar } from '@/components/MobileTabBar';
import { Phase1Rail } from '@/components/sidebar/Phase1Rail';
import { PHASE1_FLAGS } from '@/lib/phase1Paths';
import { useIsMobile } from '@/hooks/use-mobile';
import AdminMenuLink from '@/components/admin/AdminMenuLink';
import { UserMenu } from '@/components/sidebar/UserMenu';
import { formatTraceCurrency } from '@/components/execution-trace/ExecutionTrace';
import ToolCallTimeline from '@/components/execution-trace/ToolCallTimeline';
import AgenticToolUsage from '@/components/execution-trace/AgenticToolUsage';
import { useCommandBarSearchData } from '@/hooks/useCommandBarSearchData';
import {
  useSupplierCardSearch,
  SUPPLIER_MIN_QUERY_LENGTH,
  buildSupplierWorldviaUrl,
  type SupplierCard,
} from '@/hooks/useSuppliers';
import { useThreadsStreaming, type ThreadsStreamingState } from '@/hooks/useThreadsStreaming';
import {
  AgentMessageList,
  routesToAgent,
  useAgentThread,
  useAgentWarmup,
  useThreadUrlSync,
  type AgentThreadController,
} from '@/features/threads-agent';
import type { AgentAttachment } from '@/hooks/useAgentTurnStream';
import { StreamingMessage } from '@/components/threads/StreamingMessage';
import LegacyChatImportModal from '@/components/LegacyChatImportModal';
import { useLegacyChatImport } from '@/hooks/use-legacy-chat-import';
import { StreamingMessageWrapper } from '@/components/threads/StreamingMessageWrapper';
import type {
  AgenticExecutionMetadata,
  AgenticToolCallLogEntry,
  AgenticToolTiming,
} from '@shared/agentic-execution';
import { normalizeIdentifier, coerceNumericId } from './canvas/threads/canvasShared';
import { processMessageContent } from './canvas/threads/threadParts';
import { ThreadsCanvas } from './canvas/threads/ThreadsCanvas';
export { AdminDebugPanel } from './canvas/threads/AdminDebugPanel';
export type { AdminDebugPanelProps } from './canvas/threads/AdminDebugPanel';

// Define canvas types
type CanvasType = 'home' | 'dashboard' | 'clients' | 'trips' | 'pipeline' | 'content' | 'analytics' | 'threads' | 'project' | 'proposals' | 'booking' | 'payments' | 'statements' | 'reports' | 'unclaimed';

type ToolSignal = {
  mode: 'hard' | 'soft';
  tool: string;
  source: 'slash' | 'menu';
};

const HARD_TOOL_COMMANDS = [
  // Temporarily disabled; re-enable by uncommenting the CRM entry below
  // { command: '/crm', label: 'CRM', tool: 'CRM_Tool' },
  { command: '/kb', label: 'WorldVia Knowledge Base', tool: 'Pinecone_RAG' },
  { command: '/web', label: 'Web Search', tool: 'Web_Search' },
  { command: '/scrape', label: 'Web Scrape', tool: 'Web_Content' },
  { command: '/photo', label: 'Photo Search', tool: 'Image_Search' },
];

const RESERVED_TOOL_COMMANDS = [
  '/sites',
  '/pro',
  '/trip',
  '/book',
  '/flight',
  '/cruise',
  '/hotel',
  '/tour',
  '/car',
  '/activity',
  '/insurance',
];
type ViewMode = 'canvas' | 'traditional';

// Activity Stream types - COMMENTED OUT FOR LATER
// interface ActivityItem {
//   id: string;
//   type: 'approval' | 'suggestion' | 'complete' | 'background';
//   title: string;
//   description: string;
//   timestamp: Date;
//   actions?: { label: string; type: 'primary' | 'secondary' }[];
// }

// Chat Message interface
interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  toolCalls?: any[];
}

const getContactIdentifier = (contact: any): string =>
  normalizeIdentifier(contact?.contactId ?? contact?.clientId ?? contact?.id);

const getTripIdentifier = (trip: any): string =>
  normalizeIdentifier(trip?.tripId ?? trip?.id);

// Enhanced fuzzy matching with phrase detection
const fuzzyMatch = (query: string, target: string): boolean => {
  if (!query || !target) return false;
  
  const queryLower = query.toLowerCase().trim();
  const targetLower = target.toLowerCase().trim();
  
  // Minimum 2 characters for matching
  if (queryLower.length < 2) return false;
  
  // 1. Direct inclusion (exact substring)
  if (targetLower.includes(queryLower)) return true;
  
  // 2. Word starts matching (e.g., "hom" matches "Homer")
  const targetWords = targetLower.split(/\s+/);
  const queryWords = queryLower.split(/\s+/);
  
  // Check if query starts any target word
  if (targetWords.some(word => word.startsWith(queryLower))) return true;
  
  // 3. Enhanced phrase matching - check if target words appear in query
  for (const targetWord of targetWords) {
    if (targetWord.length >= 3) {
      // Check if any part of the query contains this target word
      if (queryLower.includes(targetWord)) return true;
      // Check if target word starts with any query word
      for (const queryWord of queryWords) {
        if (queryWord.length >= 2 && targetWord.startsWith(queryWord)) return true;
      }
    }
  }
  
  // 4. Multi-word fuzzy matching (e.g., "h simpsn" matches "Homer Simpson")
  if (queryWords.length > 1) {
    let matchedWords = 0;
    for (const queryWord of queryWords) {
      if (queryWord.length >= 2) {
        const wordMatched = targetWords.some(targetWord => 
          targetWord.startsWith(queryWord) || 
          targetWord.includes(queryWord) ||
          levenshteinDistance(queryWord, targetWord) <= 1
        );
        if (wordMatched) matchedWords++;
      }
    }
    // Match if at least 50% of query words match (lowered threshold for phrase detection)
    if (matchedWords / queryWords.length >= 0.5) return true;
  }
  
  // 4. Single word with typos (e.g., "homre" matches "homer")
  if (queryWords.length === 1 && queryLower.length >= 3) {
    for (const targetWord of targetWords) {
      if (targetWord.length >= 3) {
        // Allow 1-2 character differences based on length
        const maxDistance = queryLower.length <= 5 ? 1 : 2;
        if (levenshteinDistance(queryLower, targetWord) <= maxDistance) {
          return true;
        }
        // Also check if query is a substantial substring of target word
        if (targetWord.length >= queryLower.length && targetWord.includes(queryLower.slice(0, -1))) {
          return true;
        }
      }
    }
  }
  
  // 5. Initials matching (e.g., "h s" could match "Homer Simpson")
  if (queryWords.length === 2 && queryWords.every(w => w.length === 1)) {
    if (targetWords.length >= 2) {
      return targetWords[0].startsWith(queryWords[0]) && 
             targetWords[1].startsWith(queryWords[1]);
    }
  }
  
  return false;
};

const sanitizeSupplierField = (value?: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  // Drop obvious source/url blobs that shouldn't render in badges
  if (trimmed.toLowerCase().startsWith('see supplier source')) return null;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return null;
  return trimmed;
};

const truncateText = (value: string, max = 28): string =>
  value.length > max ? `${value.slice(0, max - 1)}…` : value;

// Simple Levenshtein distance for typo tolerance
const levenshteinDistance = (str1: string, str2: string): number => {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
  
  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,     // deletion
        matrix[j - 1][i] + 1,     // insertion
        matrix[j - 1][i - 1] + indicator // substitution
      );
    }
  }
  
  return matrix[str2.length][str1.length];
};

// Enhanced entity detection for better intent matching
const detectEntities = (query: string) => {
  const queryLower = query.toLowerCase();
  
  // Only detect intents for substantial queries (not random phrases)
  const actionWords = ['email', 'call', 'message', 'contact', 'schedule', 'book', 'create', 'plan', 'draft'];
  const hasActionWord = actionWords.some(word => queryLower.includes(word));
  
  return {
    intents: hasActionWord ? actionWords.filter(word => queryLower.includes(word)) : [],
    names: [],
    locations: []
  };
};

// Contact Card Component
const ContactCard: React.FC<{ data: any }> = ({ data }) => {
  const contact = data.contact;
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-semibold">
          {contact.firstName?.[0]}{contact.lastName?.[0]}
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">
            {contact.firstName} {contact.lastName}
          </h3>
          {contact.email && (
            <p className="text-sm text-gray-600 mt-1">{contact.email}</p>
          )}
          {contact.phone && (
            <p className="text-sm text-gray-600">{contact.phone}</p>
          )}
          {contact.company && (
            <p className="text-sm text-gray-500 mt-2">{contact.company}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Contact List Component
const ContactList: React.FC<{ data: any }> = ({ data }) => {
  return (
    <div className="space-y-3">
      {data.contacts.map((contact: any, index: number) => (
        <ContactCard key={contact.id || index} data={{ contact }} />
      ))}
      {data.hasMore && (
        <p className="text-sm text-gray-500 text-center py-2">
          Showing {data.displayedCount} of {data.totalCount} contacts
        </p>
      )}
    </div>
  );
};

// Trip Card Component
const TripCard: React.FC<{ data: any }> = ({ data }) => {
  const trip = data.trip;
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">{trip.name}</h3>
          <div className="mt-2 space-y-1">
            <p className="text-sm text-gray-600">
              {trip.startDate && trip.endDate
                ? `${new Date(trip.startDate).toLocaleDateString()} - ${new Date(trip.endDate).toLocaleDateString()}`
                : "TBD"}
            </p>
            {trip.countries && (
              <p className="text-sm text-gray-600">
                Destinations: {Array.isArray(trip.countries) ? trip.countries.join(', ') : trip.countries}
              </p>
            )}
            {trip.clientName && (
              <p className="text-sm text-gray-600">Client: {trip.clientName}</p>
            )}
          </div>
        </div>
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
          <Calendar className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

// Trip List Component
const TripList: React.FC<{ data: any }> = ({ data }) => {
  return (
    <div className="space-y-3">
      {data.trips.map((trip: any, index: number) => (
        <TripCard key={trip.id || index} data={{ trip }} />
      ))}
      {data.hasMore && (
        <p className="text-sm text-gray-500 text-center py-2">
          Showing {data.displayedCount} of {data.totalCount} trips
        </p>
      )}
    </div>
  );
};

// Enhanced Command Bar with contextual search and suggestions
interface SearchResult {
  id: string;
  type: 'contact' | 'trip' | 'supplier' | 'action' | 'suggestion';
  title: string;
  subtitle?: string;
  data?: any;
  icon?: React.ReactNode;
  action?: () => void;
}

interface ContextualSuggestion {
  id: string;
  title: string;
  description: string;
  type: 'email' | 'call' | 'note' | 'trip' | 'follow-up';
  action: () => void;
  priority: number;
}

// Mock supplier data for testing
/* const mockSuppliers = [
  {
    id: 1,
    name: 'Royal Caribbean International',
    category: 'Cruise',
    primaryContact: 'Sarah Johnson',
    contactEmail: 'sarah.johnson@rccl.com',
    contactPhone: '+1 (305) 539-6000',
    portalUrl: 'https://agent.royalcaribbean.com',
    baseCommissionRate: '16%'
  },
  {
    id: 2,
    name: 'Norwegian Cruise Line',
    category: 'Cruise',
    primaryContact: 'Mike Rodriguez',
    contactEmail: 'mike.rodriguez@ncl.com',
    contactPhone: '+1 (305) 436-4000',
    portalUrl: 'https://agents.ncl.com',
    baseCommissionRate: '16%'
  },
  {
    id: 3,
    name: 'Virgin Voyages',
    category: 'Cruise',
    primaryContact: 'Emma Thompson',
    contactEmail: 'emma.thompson@virginvoyages.com',
    contactPhone: '+1 (954) 488-6000',
    portalUrl: 'https://partners.virginvoyages.com',
    baseCommissionRate: '16%'
  },
  {
    id: 4,
    name: 'Viking Cruises',
    category: 'Cruise',
    primaryContact: 'Lars Anderson',
    contactEmail: 'lars.anderson@vikingcruises.com',
    contactPhone: '+1 (877) 668-4546',
    portalUrl: 'https://agent.vikingcruises.com',
    baseCommissionRate: '16%'
  },
  {
    id: 5,
    name: 'AmaWaterways',
    category: 'River Cruise',
    primaryContact: 'Jennifer Kim',
    contactEmail: 'jennifer.kim@amawaterways.com',
    contactPhone: '+1 (818) 382-9000',
    portalUrl: 'https://agents.amawaterways.com',
    baseCommissionRate: '12%'
  },
  {
    id: 6,
    name: 'Avalon Waterways',
    category: 'River Cruise',
    primaryContact: 'David Chen',
    contactEmail: 'david.chen@avalonwaterways.com',
    contactPhone: '+1 (877) 797-8791',
    portalUrl: 'https://agents.avalonwaterways.com',
    baseCommissionRate: '12%'
  },
  {
    id: 7,
    name: 'Globus',
    category: 'Tour',
    primaryContact: 'Maria Garcia',
    contactEmail: 'maria.garcia@globusfamily.com',
    contactPhone: '+1 (866) 755-8581',
    portalUrl: 'https://agents.globusfamily.com',
    baseCommissionRate: '8%'
  },
  {
    id: 8,
    name: 'Abercrombie & Kent',
    category: 'Luxury Tour',
    primaryContact: 'Robert Wilson',
    contactEmail: 'robert.wilson@abercrombiekent.com',
    contactPhone: '+1 (800) 554-7016',
    portalUrl: 'https://agents.abercrombiekent.com',
    baseCommissionRate: '10%'
  },
  {
    id: 9,
    name: 'Kensington Tours',
    category: 'Private Tour',
    primaryContact: 'Amanda Foster',
    contactEmail: 'amanda.foster@kensingtontours.com',
    contactPhone: '+1 (888) 903-2001',
    portalUrl: 'https://agents.kensingtontours.com',
    baseCommissionRate: '12%'
  },
  {
    id: 10,
    name: 'CIE Tours',
    category: 'Tour',
    primaryContact: 'Patrick O\'Brien',
    contactEmail: 'patrick.obrien@cietours.com',
    contactPhone: '+1 (800) 243-8687',
    portalUrl: 'https://agents.cietours.com',
    baseCommissionRate: '10%'
  },
  {
    id: 11,
    name: 'Funjet',
    category: 'Package',
    primaryContact: 'Lisa Martinez',
    contactEmail: 'lisa.martinez@funjet.com',
    contactPhone: '+1 (888) 558-6654',
    portalUrl: 'https://agents.funjet.com',
    baseCommissionRate: '12%'
  },
  {
    id: 12,
    name: 'Apple Vacations',
    category: 'Package',
    primaryContact: 'Steve Adams',
    contactEmail: 'steve.adams@applevacations.com',
    contactPhone: '+1 (800) 517-2000',
    portalUrl: 'https://agents.applevacations.com',
    baseCommissionRate: '12%'
  },
  {
    id: 13,
    name: 'Hilton Hotels',
    category: 'Hotel',
    primaryContact: 'Rachel Green',
    contactEmail: 'rachel.green@hilton.com',
    contactPhone: '+1 (800) 445-8667',
    portalUrl: 'https://partners.hilton.com',
    baseCommissionRate: '8%'
  },
  {
    id: 14,
    name: 'Ritz Carlton Hotels',
    category: 'Luxury Hotel',
    primaryContact: 'James Sterling',
    contactEmail: 'james.sterling@ritzcarlton.com',
    contactPhone: '+1 (800) 542-8680',
    portalUrl: 'https://partners.ritzcarlton.com',
    baseCommissionRate: '8%'
  },
  {
    id: 15,
    name: 'Marriott Hotels',
    category: 'Hotel',
    primaryContact: 'Nicole Davis',
    contactEmail: 'nicole.davis@marriott.com',
    contactPhone: '+1 (800) 721-7033',
    portalUrl: 'https://partners.marriott.com',
    baseCommissionRate: '8%'
  }
]; */

export const CommandBar: React.FC<{
  onSubmit: (
    command: string,
    /** `explicitGlobal`: the advisor chose Global while in a project (sent once, with the next message). */
    scope?: { projectId: number | null; explicitGlobal?: boolean } | null,
    toolSignal?: ToolSignal | null,
    /** AIVIA only: attached files as extracted text (POST /api/threads-agent/attachments). */
    agentAttachments?: AgentAttachment[],
  ) => void;
  isProcessing: boolean;
  onDirectDataDisplay: (type: 'contact' | 'trip' | 'supplier', data: any) => void;
  contactsData: any[];
  tripsData: any[];
  searchDataLoaded: boolean;
  sidebarExpanded: boolean;
  activeProject?: any | null;
  projectsData?: any[];
  onSetScopeProject?: (projectId: number | null, project?: any) => void;
  centered?: boolean;
  contactOptions?: any[];
  tripOptions?: any[];
  onLinkContact?: (contactId: number) => void;
  onLinkTrip?: (tripId: number) => void;
  linkedContacts?: any[];
  linkedTrips?: any[];
  providers?: any[];
  selectedProvider?: string;
  selectedModel?: string;
  onProviderChange?: (provider: string) => void;
  onModelChange?: (model: string) => void;
  // Advanced Mode / External Mode props
  advancedModeEnabled?: boolean;
  externalModeEnabled?: boolean;
  externalModeProvider?: string;
  externalModeModel?: string;
  onOpenDefaultModel?: () => void;
  onToggleExternalMode?: () => void;
  onExternalProviderChange?: (provider: string) => void;
  onExternalModelChange?: (model: string) => void;
  // Hide footer (scope selector, shift+enter text) when in sticky mode
  hideFooter?: boolean;
  // Show or hide the session disclaimer under the footer helpers
  showFooterDisclaimer?: boolean;
}> = ({ onSubmit, isProcessing, onDirectDataDisplay, contactsData, tripsData, searchDataLoaded, sidebarExpanded, activeProject = null, projectsData = [], onSetScopeProject, centered = false, contactOptions = [], tripOptions = [], onLinkContact, onLinkTrip, linkedContacts = [], linkedTrips = [], providers = [], selectedProvider = '', selectedModel = '', onProviderChange, onModelChange, advancedModeEnabled = false, externalModeEnabled = false, externalModeProvider, externalModeModel, onOpenDefaultModel, onToggleExternalMode, onExternalProviderChange, onExternalModelChange, hideFooter = false, showFooterDisclaimer = true }) => {
  const { user } = useAuth();
  const draftKey = user?.id ? `aivia_command_draft_${user.id}` : null;
  const [command, setCommand] = useState(() => {
    if (!draftKey) return '';
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        const content = parsed?.content;
        const timestamp = parsed?.timestamp;
        if (typeof content === 'string' && content.length > 0 &&
            typeof timestamp === 'number' && Date.now() - timestamp < 60 * 60 * 1000) {
          return content;
        }
        localStorage.removeItem(draftKey);
      }
    } catch (e) {
      console.warn('Failed to restore draft:', e);
    }
    return '';
  });

  const draftSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (draftSaveTimeoutRef.current) {
      clearTimeout(draftSaveTimeoutRef.current);
    }
    if (!draftKey || !command.trim()) {
      if (draftKey) { try { localStorage.removeItem(draftKey); } catch {} }
      return;
    }
    draftSaveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify({ content: command, timestamp: Date.now() }));
      } catch (e) {
        console.warn('Failed to save draft:', e);
      }
    }, 500);
    return () => { if (draftSaveTimeoutRef.current) clearTimeout(draftSaveTimeoutRef.current); };
  }, [command, draftKey]);

  // `text` is set for AIVIA attachments: the file was read on upload and only its text is sent.
  // `uploading` marks an AIVIA attachment still being uploaded and read.
  const [attachments, setAttachments] = useState<Array<{ id: string; path: string; filename: string; text?: string; uploading?: boolean }>>([]);
  const agentAttachmentsEnabled = useResolvedFeatureFlags().isEnabled('agentcoreThreads');
  const [showLinkClientDialog, setShowLinkClientDialog] = useState(false);
  const [showLinkTripDialog, setShowLinkTripDialog] = useState(false);
  const [showChangeModelDialog, setShowChangeModelDialog] = useState(false);
  const [showMyPromptsDialog, setShowMyPromptsDialog] = useState(false);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [tempProvider, setTempProvider] = useState(selectedProvider);
  const [tempModel, setTempModel] = useState(selectedModel);
  const { toast } = useToast();
  const [activeToolSignal, setActiveToolSignal] = useState<ToolSignal | null>(null);
  const [slashPreview, setSlashPreview] = useState<string | null>(null);
  const [promptLibraryMeta, setPromptLibraryMeta] = useState<{ templateId: number; scope: 'system' | 'user' } | null>(null);
  const trimmedCommand = command.trim();
  const { data: supplierSearchResultsRaw } = useSupplierCardSearch(trimmedCommand);
  const supplierSearchResults = supplierSearchResultsRaw ?? EMPTY_SUPPLIER_RESULTS;

  const { data: userProfileData } = useQuery({
    queryKey: ['/api/profile'],
    queryFn: () => apiRequest('GET', '/api/profile'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // T-380: Fetch enabled orchestrator tool modes (respects admin MCP tool toggles)
  const { data: enabledModesData } = useQuery<{ modes: Record<string, boolean> }>({
    queryKey: ['/api/mcp/enabled-modes'],
    queryFn: () => apiRequest('GET', '/api/mcp/enabled-modes'),
    staleTime: 30 * 1000, // 30 seconds - refresh fairly often to pick up admin changes
  });

  // T-379: Fetch suggested prompts for the threads surface
  const { data: suggestedPromptsData } = useQuery<{ templates: Array<{
    id: number;
    title: string;
    description: string | null;
    bodyTemplate: string;
    categoryId: number | null;
    categorySlug: string | null;
    categoryLabel: string | null;
    scope: 'system' | 'user';
    lastUsedAt: string | null;
  }> }>({
    queryKey: ['/api/prompt-library/suggested', 'threads', user?.id ?? 'anonymous'],
    queryFn: () => apiRequest('GET', '/api/prompt-library/suggested?surface=threads&limit=10'),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!user,
  });
  // Only SYSTEM_ADMIN should see client/trip context cards
  const isAdmin = useMemo(() => isSystemAdminRole(user?.role), [user?.role]);
  const profileValues = useMemo(() => extractProfileValues(user, userProfileData), [user, userProfileData]);

  // Handle prompt selection from the Prompt Library
  const handleSelectPrompt = useCallback((template: PromptTemplate) => {
    // Substitute variables in the template
    const resolvedContent = substituteVariables(template.bodyTemplate, profileValues);
    
    // Replace the current command with the resolved prompt
    setCommand(resolvedContent);
    
    // Store metadata for the outgoing message
    setPromptLibraryMeta({ templateId: template.id, scope: template.scope });
    
    // Close the menu
    setPlusMenuOpen(false);
    
    // Focus the textarea
    setTimeout(() => {
      textareaRef.current?.focus();
      // Move cursor to end
      if (textareaRef.current) {
        textareaRef.current.selectionStart = textareaRef.current.value.length;
        textareaRef.current.selectionEnd = textareaRef.current.value.length;
      }
    }, 0);
  }, [profileValues]);

  const linkedContactIds = useMemo(() => {
    const ids = new Set<string>();
    (linkedContacts || []).forEach((contact) => {
      const id = getContactIdentifier(contact);
      if (id) ids.add(id);
    });
    return ids;
  }, [linkedContacts]);

  const linkedTripIds = useMemo(() => {
    const ids = new Set<string>();
    (linkedTrips || []).forEach((trip) => {
      const id = getTripIdentifier(trip);
      if (id) ids.add(id);
    });
    return ids;
  }, [linkedTrips]);

  const providersWithAvailableModels = useMemo(
    () => providers.filter((provider: any) => provider?.models?.some((model: any) => model.available)),
    [providers]
  );

  // T-380: Filter tool commands to only include enabled tools
  const enabledToolCommands = useMemo(() => {
    const modes = enabledModesData?.modes;
    if (!modes) return HARD_TOOL_COMMANDS; // Default to all if not loaded yet
    return HARD_TOOL_COMMANDS.filter((cmd) => modes[cmd.tool] !== false);
  }, [enabledModesData?.modes]);

  const hardToolLookup = useMemo(
    () => new Map(enabledToolCommands.map((cmd) => [cmd.command, cmd])),
    [enabledToolCommands],
  );
  const toolLabelById = useMemo(
    () => new Map(enabledToolCommands.map((cmd) => [cmd.tool, cmd.label])),
    [enabledToolCommands],
  );
  const activeToolLabel = activeToolSignal
    ? toolLabelById.get(activeToolSignal.tool) ?? activeToolSignal.tool
    : null;
  const slashMatches = useMemo(() => {
    if (!slashPreview) return [];
    return enabledToolCommands.filter((cmd) =>
      cmd.command.startsWith(slashPreview),
    );
  }, [slashPreview, enabledToolCommands]);
  const reservedMatches = useMemo(() => {
    if (!slashPreview) return [];
    return RESERVED_TOOL_COMMANDS.filter((cmd) => cmd.startsWith(slashPreview));
  }, [slashPreview]);
  const hasProvidersWithModels = providersWithAvailableModels.length > 0;
  const lastValidSelectionRef = useRef<{ provider: string; model: string } | null>(null);
  const prevSelectedRef = useRef({ provider: selectedProvider, model: selectedModel });

  const getProviderConfig = (providerId: string) =>
    providers.find((provider: any) => provider.provider === providerId);

  const getFirstAvailableModelId = (providerId: string) => {
    const providerConfig = getProviderConfig(providerId);
    return providerConfig?.models?.find((model: any) => model.available)?.id ?? '';
  };

  const openChangeModelDialog = () => {
    const providerIsValid = providersWithAvailableModels.some(
      (provider: any) => provider.provider === selectedProvider
    );
    const initialProviderId = providerIsValid
      ? selectedProvider
      : providersWithAvailableModels[0]?.provider ?? '';

    let initialModelId = '';
    if (initialProviderId) {
      const providerConfig = getProviderConfig(initialProviderId);
      const selectedModelIsAvailable =
        initialProviderId === selectedProvider &&
        providerConfig?.models?.some((model: any) => model.available && model.id === selectedModel);

      initialModelId = selectedModelIsAvailable
        ? selectedModel
        : getFirstAvailableModelId(initialProviderId);
    }

    setTempProvider(initialProviderId);
    setTempModel(initialModelId);
    lastValidSelectionRef.current = initialProviderId && initialModelId
      ? { provider: initialProviderId, model: initialModelId }
      : null;
    setShowChangeModelDialog(true);
  };

  const closeChangeModelDialog = () => {
    setShowChangeModelDialog(false);
    setTempProvider(selectedProvider);
    setTempModel(selectedModel);
  };

  const tempProviderConfig = getProviderConfig(tempProvider);
  const availableModelsForTempProvider = useMemo(
    () => tempProviderConfig?.models?.filter((model: any) => model.available) ?? [],
    [tempProviderConfig],
  );
  const isTempModelValid = Boolean(
    tempModel && availableModelsForTempProvider.some((model: any) => model.id === tempModel)
  );
  const canApplyChangeModel = Boolean(
    onProviderChange &&
    onModelChange &&
    tempProvider &&
    isTempModelValid &&
    availableModelsForTempProvider.length > 0
  );

  const handleApplyChangeModel = () => {
    if (!canApplyChangeModel || !onProviderChange || !onModelChange || !tempModel) {
      return;
    }

    if (tempProvider !== selectedProvider) {
      onProviderChange(tempProvider);
    }
    if (tempModel !== selectedModel) {
      onModelChange(tempModel);
    }

    closeChangeModelDialog();
  };

  useEffect(() => {
    const previous = prevSelectedRef.current;
    const providerChanged = previous.provider !== selectedProvider;
    const modelChanged = previous.model !== selectedModel;

    if (providerChanged || modelChanged) {
      setTempProvider(selectedProvider);
      setTempModel(selectedModel);
    }

    prevSelectedRef.current = { provider: selectedProvider, model: selectedModel };

    const selectedConfig = getProviderConfig(selectedProvider);
    const selectedIsValid = selectedConfig?.models?.some(
      (model: any) => model.available && model.id === selectedModel
    );
    if (selectedIsValid) {
      lastValidSelectionRef.current = { provider: selectedProvider, model: selectedModel };
    }
  }, [selectedProvider, selectedModel, providers]);

  useEffect(() => {
    if (availableModelsForTempProvider.length > 0 && isTempModelValid) {
      lastValidSelectionRef.current = { provider: tempProvider, model: tempModel };
    }
  }, [availableModelsForTempProvider, isTempModelValid, tempProvider, tempModel]);

  useEffect(() => {
    if (!showChangeModelDialog) return;

    const currentProviderHasModels = availableModelsForTempProvider.length > 0;

    if (!currentProviderHasModels) {
      const fallbackFromLastValid =
        lastValidSelectionRef.current &&
        lastValidSelectionRef.current.provider !== tempProvider &&
        providersWithAvailableModels.some(
          (provider: any) => provider.provider === lastValidSelectionRef.current?.provider
        );

      if (fallbackFromLastValid) {
        const fallbackConfig = getProviderConfig(lastValidSelectionRef.current?.provider ?? '');
        const fallbackModelId =
          fallbackConfig?.models?.find(
            (model: any) =>
              model.available && model.id === lastValidSelectionRef.current?.model
          )?.id ?? getFirstAvailableModelId(lastValidSelectionRef.current?.provider ?? '');

        if (fallbackConfig) {
          setTempProvider(fallbackConfig.provider);
          setTempModel(fallbackModelId);
          return;
        }
      }

      const nextProvider = providersWithAvailableModels.find(
        (provider: any) => provider.provider !== tempProvider
      );

      if (nextProvider) {
        const nextModelId = getFirstAvailableModelId(nextProvider.provider);
        setTempProvider(nextProvider.provider);
        setTempModel(nextModelId);
      } else {
        setTempProvider('');
        setTempModel('');
      }

      return;
    }

    if (!isTempModelValid && availableModelsForTempProvider.length > 0) {
      setTempModel(availableModelsForTempProvider[0].id);
    }
  }, [
    showChangeModelDialog,
    availableModelsForTempProvider,
    isTempModelValid,
    providersWithAvailableModels,
    tempProvider,
    providers
  ]);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const outerRef = useRef<HTMLDivElement | null>(null);
  // Measures the area that should affect page layout: input + help line + the
  // expanded suggestions panel (it renders below the input, so its height has to
  // count toward the clearance the canvas leaves at the bottom).
  const occupiedRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Track contact IDs already fetched to prevent duplicate API calls (ERR_INSUFFICIENT_RESOURCES fix)
  const fetchedContactDetailsRef = useRef<Set<number>>(new Set());
  const [maxInputHeight] = useState<number>(160);

  // maxInputHeight is a fixed 160px cap to ensure the send button
  // stays visible even when long prompts fill the textarea.

  // Expose a CSS variable for the command bar height (including bottom offset)
  // so canvases can reserve exactly that much bottom clearance.
  useEffect(() => {
    const el = occupiedRef.current;
    if (!el) return;
    const setVar = () => {
      const bottomOffsetPx = 20; // matches bottom-[20px]
      const heightWithOffset = el.offsetHeight + bottomOffsetPx;
      document.documentElement.style.setProperty('--command-bar-height', `${heightWithOffset}px`);
    };
    setVar();
    const ro = new ResizeObserver(() => setVar());
    ro.observe(el);
    window.addEventListener('resize', setVar);
    return () => {
      window.removeEventListener('resize', setVar);
      ro.disconnect();
    };
  }, []);

  // Auto-resize the textarea within its max height
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, maxInputHeight);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > next ? 'auto' : 'hidden';
  }, [command, maxInputHeight]);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [contextualSuggestions, setContextualSuggestions] = useState<ContextualSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  // AR-33: collapse toggle for the suggested-results panel
  const [suggestionsCollapsed, setSuggestionsCollapsed] = useState(false);
  const [runScope, setRunScope] = useState<'global' | 'project'>('global');
  // Set when the advisor picks Global while a project is active; reported with the next submit.
  const explicitGlobalRef = useRef(false);
  const chooseGlobal = () => {
    if (activeProject?.id) explicitGlobalRef.current = true;
    setRunScope('global');
  };
  const [scopeRestored, setScopeRestored] = useState(false);

  // Sync internal runScope state with activeProject prop from parent
  // This ensures scope follows when user selects a project from sidebar
  useEffect(() => {
    if (activeProject?.id) {
      setRunScope('project');
      // Update localStorage to match current project
      localStorage.setItem('commandBarScope', JSON.stringify({
        scope: 'project',
        projectId: activeProject.id,
        projectName: activeProject.name
      }));
    }
  }, [activeProject?.id, activeProject?.name]);

  // Restore persisted context when projects are loaded (only if no active project already set)
  useEffect(() => {
    // Skip if already restored, no callback, no projects data, or parent already set a project
    if (scopeRestored || !onSetScopeProject || !projectsData || projectsData.length === 0) return;
    
    // If parent already has an active project, respect that instead of restoring from localStorage
    if (activeProject?.id) {
      setRunScope('project');
      setScopeRestored(true);
      return;
    }

    try {
      const savedScope = localStorage.getItem('commandBarScope');
      if (savedScope) {
        const parsed = JSON.parse(savedScope);
        if (parsed.scope === 'project' && parsed.projectId) {
          // Validate project still exists
          const project = projectsData.find((p: any) => p.id === parsed.projectId);
          if (project) {
            setRunScope('project');
            onSetScopeProject(parsed.projectId, project);
            setScopeRestored(true);
          } else {
            // Project no longer exists, fall back to global
            console.warn(`Saved project ${parsed.projectId} not found, falling back to global scope`);
            setRunScope('global');
            onSetScopeProject(null);
            localStorage.setItem('commandBarScope', JSON.stringify({ scope: 'global' }));
            setScopeRestored(true);
          }
        } else if (parsed.scope === 'global') {
          setRunScope('global');
          onSetScopeProject(null);
          setScopeRestored(true);
        }
      } else {
        // No saved scope, mark as restored to prevent future checks
        setScopeRestored(true);
      }
    } catch (e) {
      console.warn('Failed to restore command bar scope:', e);
      setScopeRestored(true);
    }
  }, [projectsData, onSetScopeProject, scopeRestored, activeProject?.id]);

  // Note: runScope syncs with activeProject prop and persists user's explicit choice

  // Enhanced contextual search with strict matching
  const performContextualSearch = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      setContextualSuggestions([]);
      return;
    }

    if (!searchDataLoaded) return;

    setIsSearching(true);
    const entities = detectEntities(query);
    const results: SearchResult[] = [];
    const suggestions: ContextualSuggestion[] = [];
    const detailsToFetch: Array<{type: 'contact' | 'trip', id: number, data: any}> = [];
    let matchingContacts: any[] = [];
    let matchingTrips: any[] = [];
    const canShowContactTripCards = isAdmin;

    try {
      // ⚡ STRICT CLIENT-SIDE FUZZY MATCHING

      if (canShowContactTripCards) {
        // Search contacts with stricter criteria
                      matchingContacts = (Array.isArray(contactsData) ? contactsData : []).filter(contact => {
              // Create full name for matching
              const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
              
              // Only search name fields (no email for privacy)
              const nameMatch = fuzzyMatch(query, fullName);
              const firstNameMatch = contact.firstName && fuzzyMatch(query, contact.firstName);
              const lastNameMatch = contact.lastName && fuzzyMatch(query, contact.lastName);
              const preferredNameMatch = contact.preferredName && fuzzyMatch(query, contact.preferredName);
              
              const matches = nameMatch || firstNameMatch || lastNameMatch || preferredNameMatch;
              
              return matches;
            }).slice(0, 5);
          
        // Add contact preview results and queue for detail fetching
        matchingContacts.forEach((contact: any) => {
          const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
          results.push({
            id: `contact-${contact.id}`,
            type: 'contact',
            title: fullName || 'Contact',
            subtitle: 'Loading details...',
            data: contact, // Basic contact data (name only)
            icon: <Users className="w-4 h-4 text-orange-600" />,
            action: async () => {
              // Fetch full contact details when clicked
              try {
                const response = await fetch(`/api/contacts/${contact.id}`, {
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (response.ok) {
                  const fullContact = await response.json();
                  onDirectDataDisplay('contact', fullContact);
                } else {
                  onDirectDataDisplay('contact', contact);
                }
              } catch (error) {
                console.warn('Failed to fetch full contact details:', error);
                onDirectDataDisplay('contact', contact);
              }
              setSearchResults([]);
              setContextualSuggestions([]);
              setCommand('');
            }
          });
          detailsToFetch.push({type: 'contact', id: contact.id, data: contact});
        });
      }

      if (canShowContactTripCards) {
        // Search trips with stricter criteria and meaningful queries only
        const meaningfulTripQuery = query.length >= 3 && 
          !['go for', 'find the', 'the', 'for', 'and', 'but', 'yes', 'no', 'ok', 'okay'].some(stopWord => 
            query.toLowerCase().includes(stopWord)
          );
          
                      matchingTrips = meaningfulTripQuery ? (Array.isArray(tripsData) ? tripsData : []).filter(trip => {
            const nameMatch = fuzzyMatch(query, trip.name || '');
            const clientNameMatch = trip.clientName && fuzzyMatch(query, trip.clientName);
            const countriesMatch = Array.isArray(trip.countries) 
              ? trip.countries.some((country: string) => fuzzyMatch(query, country))
              : fuzzyMatch(query, (trip.countries as string) || '');
            const matches = nameMatch || clientNameMatch || countriesMatch;
           
           return matches;
         }).slice(0, 5) : [];
        
        // Add trip results immediately (trips have enough data already)
        matchingTrips.forEach((trip: any) => {
          results.push({
            id: `trip-${trip.id}`,
            type: 'trip',
            title: trip.name,
            subtitle: `${new Date(trip.startDate).toLocaleDateString()} - ${Array.isArray(trip.countries) ? trip.countries.join(', ') : trip.countries}`,
            data: trip,
            icon: <Calendar className="w-4 h-4 text-blue-600" />,
            action: () => {
              onDirectDataDisplay('trip', trip);
              setSearchResults([]);
              setContextualSuggestions([]);
              setCommand('');
            }
          });
        });
      }

      // Search suppliers via canonical API (min query length enforced in hook)
      const supplierQuery = query.trim();
      const canSearchSuppliers =
        supplierQuery.length >= SUPPLIER_MIN_QUERY_LENGTH;
      const matchingSuppliers = canSearchSuppliers
        ? (Array.isArray(supplierSearchResults)
            ? supplierSearchResults
            : []
          ).slice(0, 3)
        : [];

      // Suppliers are already constrained client-side; no verbose logging needed

      matchingSuppliers.forEach((supplier: SupplierCard) => {
        const categoryLabel = sanitizeSupplierField(supplier.supplierCategory);
        const worldviaUrl = supplier.slug ? buildSupplierWorldviaUrl(supplier.slug) : null;

        results.push({
          id: `supplier-${supplier.id ?? supplier.slug ?? supplier.supplierName}`,
          type: 'supplier',
          title: supplier.supplierName,
          subtitle: categoryLabel || 'Supplier',
          data: {
            ...supplier,
            // Map to expected field names for card display
            worldviaProUrl: worldviaUrl,
          },
          icon: <Globe className="w-4 h-4 text-purple-600" />,
          action: () => {
            if (worldviaUrl) {
              window.open(worldviaUrl, '_blank', 'noopener,noreferrer');
            }
            setSearchResults([]);
            setContextualSuggestions([]);
            setCommand('');
          },
        });
      });

      // Generate smart suggestions only for meaningful queries
      if (matchingContacts.length > 0) {
        const contact = matchingContacts[0];
        const contactName =
          contact.name ||
          contact.preferredName ||
          [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim() ||
          contact.email ||
          'contact';

        if (entities.intents.includes('email') || query.toLowerCase().includes('email')) {
          suggestions.push({
            id: `email-${contact.id}`,
            title: `Draft email to ${contactName}`,
            description: 'Create a personalized email message',
            type: 'email',
            action: () => {
              setCommand(`Draft an email to ${contactName}`);
              setSearchResults([]);
              setContextualSuggestions([]);
            },
            priority: 1
          });
        }
      }

      if (entities.intents.includes('create') && !matchingContacts.length && !matchingTrips.length) {
        suggestions.push({
          id: 'create-general',
          title: `Create new entry`,
          description: 'Add to your database',
          type: 'note',
          action: () => {
            setCommand(`Create: ${query}`);
            setSearchResults([]);
            setContextualSuggestions([]);
          },
          priority: 3
        });
      }

      // Add fallback suggestions for meaningful queries
      if (results.length === 0 && query.length >= 3) {
        suggestions.push({
          id: 'search-broader',
          title: `Search everything for "${query}"`,
          description: 'Let me search all your data and knowledge',
          type: 'follow-up',
          action: () => {
            setCommand(`Find anything related to ${query}`);
            setSearchResults([]);
            setContextualSuggestions([]);
          },
          priority: 6
        });
      }

    } catch (error) {
      console.warn('Search error:', error);
    } finally {
      setIsSearching(false);
    }

    setSearchResults(prev => results.length === 0 && prev.length === 0 ? prev : results);
    const sorted = suggestions.length > 0
      ? suggestions.sort((a, b) => a.priority - b.priority).slice(0, 4)
      : suggestions;
    setContextualSuggestions(prev => sorted.length === 0 && prev.length === 0 ? prev : sorted);

    // 🚀 AUTOMATIC PROACTIVE DETAIL FETCHING (just for display)
    // Filter out already-fetched contacts to prevent duplicate API calls
    const contactsToFetch = detailsToFetch.filter(
      detail => detail.type === 'contact' && !fetchedContactDetailsRef.current.has(detail.data.id)
    );
    
    if (contactsToFetch.length > 0) {
      // Mark as fetching immediately to prevent race conditions
      contactsToFetch.forEach(detail => fetchedContactDetailsRef.current.add(detail.data.id));
      
      // Fetch contact email/phone for subtitle display after short delay
      setTimeout(async () => {
        for (const detail of contactsToFetch) {
          try {
            const response = await fetch(`/api/contacts/${detail.data.id}?fields=email,phone`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (response.ok) {
              const contactDetails = await response.json();
              
              // Update result subtitle with email/phone
              setSearchResults(prevResults => 
                prevResults.map(result => 
                  result.id === `contact-${detail.id}` 
                    ? {
                        ...result,
                        subtitle: contactDetails.email || contactDetails.phone || 'Contact'
                      }
                    : result
                )
              );
            }
          } catch (error) {
            console.warn('Failed to fetch contact details:', error);
          }
        }
      }, 150);
    }
  }, [
    contactsData,
    tripsData,
    searchDataLoaded,
    isAdmin,
    onDirectDataDisplay,
    supplierSearchResults,
  ]);

  const handleCommandChange = (value: string) => {
    const nextValue = processSlashInput(value);
    setCommand(nextValue);

    if (nextValue.trim().length > 1) {
      performContextualSearch(nextValue.trim());
    } else {
      setSearchResults([]);
      setContextualSuggestions([]);
    }
  };

  // Re-run search when supplier results update (but NOT when performContextualSearch changes to avoid loop)
  const performContextualSearchRef = useRef(performContextualSearch);
  performContextualSearchRef.current = performContextualSearch;
  
  useEffect(() => {
    if (trimmedCommand.length > 1) {
      performContextualSearchRef.current(trimmedCommand);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierSearchResults, trimmedCommand]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (attachments.some(a => a.uploading)) {
      toast({ title: 'Still uploading', description: 'Send once your files have finished uploading.' });
      return;
    }
    let workingCommand = command;
    let toolSignalForSubmit = activeToolSignal;
    if (!toolSignalForSubmit) {
      const trimmedStart = workingCommand.trimStart();
      const slashMatch = trimmedStart.match(/^\/(\S+)/);
      if (slashMatch) {
        const token = `/${slashMatch[1].toLowerCase()}`;
        const def = hardToolLookup.get(token);
        if (def) {
          toolSignalForSubmit = { mode: 'hard', tool: def.tool, source: 'slash' };
          workingCommand = removeLeadingCommand(workingCommand, token);
        } else if (RESERVED_TOOL_COMMANDS.includes(token)) {
          handleComingSoon(token);
          workingCommand = removeLeadingCommand(workingCommand, token);
        }
      }
    }
    const base = workingCommand.trim();
    const tokens = attachments.filter(a => a.text === undefined && !a.uploading).map(a => `[Attachment: ${a.path}]`).join(' ');
    const agentAttachments = attachments
      .filter((a): a is typeof a & { text: string } => a.text !== undefined)
      .map(a => ({ name: a.filename, text: a.text }));
    const contentToSend = (base + ' ' + tokens).trim() || (agentAttachments.length ? 'Please review the attached file.' : '');
    if (contentToSend && !isProcessing) {
      if (draftSaveTimeoutRef.current) {
        clearTimeout(draftSaveTimeoutRef.current);
        draftSaveTimeoutRef.current = null;
      }
      setCommand('');
      if (draftKey) { try { localStorage.removeItem(draftKey); } catch {} }
      
      if (runScope === 'project' && activeProject?.id) {
        explicitGlobalRef.current = false;
        onSubmit(contentToSend, { projectId: activeProject.id }, toolSignalForSubmit, agentAttachments);
        try {
          localStorage.setItem('commandBarScope', JSON.stringify({
            scope: 'project',
            projectId: activeProject.id,
            projectName: activeProject.name
          }));
        } catch (e) {
          console.warn('Failed to persist scope:', e);
        }
      } else {
        const explicitGlobal = explicitGlobalRef.current;
        explicitGlobalRef.current = false;
        onSubmit(contentToSend, { projectId: null, ...(explicitGlobal ? { explicitGlobal } : {}) }, toolSignalForSubmit, agentAttachments);
        try {
          localStorage.setItem('commandBarScope', JSON.stringify({ scope: 'global' }));
        } catch (e) {
          console.warn('Failed to persist scope:', e);
        }
      }
      setAttachments([]);
      setSearchResults([]);
      setContextualSuggestions([]);
      setActiveToolSignal(null);
      setSlashPreview(null);
      setPromptLibraryMeta(null);
    }
  };

  const getIconForSuggestionType = (type: string) => {
    switch (type) {
      case 'email': return <Send className="w-4 h-4 text-blue-600" />;
      case 'call': return <Phone className="w-4 h-4 text-green-600" />;
      case 'note': return <Plus className="w-4 h-4 text-purple-600" />;
      case 'trip': return <Calendar className="w-4 h-4 text-orange-600" />;
      case 'follow-up': return <Bell className="w-4 h-4 text-yellow-600" />;
      case 'content': return <FileText className="w-4 h-4 text-green-600" />;
      case 'finance': return <TrendingUp className="w-4 h-4 text-blue-600" />;
      default: return <Sparkles className="w-4 h-4 text-gray-600" />;
    }
  };

  // Separate contact, trip, and supplier results
  const contactResults = isAdmin ? searchResults.filter(r => r.type === 'contact') : [];
  const tripResults = isAdmin ? searchResults.filter(r => r.type === 'trip') : [];
  const supplierResults = searchResults.filter(r => r.type === 'supplier');

  // T-379: Generate suggested actions from Prompt Library
  type SuggestionType = 'prompt' | 'finance' | 'communication' | 'content' | 'email' | 'call' | 'note' | 'trip' | 'follow-up';

  const getStandardSuggestions = (query: string, contacts: any[], trips: any[]): Array<{
    id: string;
    title: string;
    description: string;
    type: SuggestionType;
    action: () => void;
  }> => {
    // Get the suggested prompts from the API
    const suggestedTemplates = suggestedPromptsData?.templates ?? [];

    // If no input or no prompts configured, return empty
    if (query.length < 2 || suggestedTemplates.length === 0) {
      return [];
    }

    // Normalize the query to tokens for matching
    const contextTokens = normalizeToTokens(query);

    // Rank templates by keyword match score and recency
    const ranked = rankSuggestedTemplates(
      suggestedTemplates,
      contextTokens,
      (t) => t.title,
      (t) => t.lastUsedAt,
      (t) => t.id,
      5 // limit to 5 suggestions
    );

    // Map to suggestion card format
    return ranked.map(({ template }) => ({
      id: `prompt-${template.id}`,
      title: template.title,
      description: template.description || template.categoryLabel || 'Prompt Library',
      type: 'prompt' as SuggestionType,
      action: () => {
        // Substitute variables and insert into command
        const resolvedContent = substituteVariables(template.bodyTemplate, profileValues);
        setCommand(resolvedContent);
        setSearchResults([]);
        setContextualSuggestions([]);
        // Store metadata for tracking
        setPromptLibraryMeta({ templateId: template.id, scope: template.scope });
        // Record usage for ranking (fire and forget)
        apiRequest('POST', `/api/prompt-library/templates/${template.id}/record-usage`).catch(() => {});
      },
    }));
  };

  const handleComingSoon = (label: string) => {
    const trimmed = typeof label === 'string' ? label.trim() : '';
    const token = trimmed.startsWith('/') ? trimmed : `/${trimmed || 'feature'}`;
    toast({
      title: 'Coming soon',
      description: `${token} integration coming soon.`,
    });
  };

  const removeLeadingCommand = (value: string, commandToken: string) => {
    const pattern = new RegExp(`^\\s*${commandToken}\\s*`, 'i');
    return value.replace(pattern, '').trimStart();
  };

  const processSlashInput = (value: string) => {
    const trimmed = value.replace(/^\s+/, '');
    const match = trimmed.match(/^\/(\S*)/);
    if (!match) {
      setSlashPreview(null);
      return value;
    }
    const token = match[0];
    const lower = token.toLowerCase();
    const hasSpace = /^\/(\S+)\s/.test(trimmed);
    if (hasSpace) {
      const remainder = trimmed.replace(/^\/(\S+)\s/, '');
      const commandDef = hardToolLookup.get(lower);
      if (commandDef) {
        setActiveToolSignal({ mode: 'hard', tool: commandDef.tool, source: 'slash' });
        setSlashPreview(null);
        return remainder.trimStart();
      }
      if (RESERVED_TOOL_COMMANDS.includes(lower)) {
        handleComingSoon(lower);
        setSlashPreview(null);
        return remainder.trimStart();
      }
      setSlashPreview(null);
      return trimmed;
    }
    setSlashPreview(lower);
    return value;
  };

  const applyToolSelection = (
    def: { command: string; tool: string },
    source: 'slash' | 'menu',
  ) => {
    setActiveToolSignal({ mode: 'hard', tool: def.tool, source });
    setSlashPreview(null);
  };

  const standardSuggestions = getStandardSuggestions(command, contactResults, tripResults);

  // AR-33: commission_base is stored as a fraction (e.g. 0.16). Render it as a
  // whole percentage ("16% commission") instead of the raw "0.16% commission".
  const formatCommission = (raw: unknown): string | null => {
    if (raw === null || raw === undefined || raw === '') return null;
    const n = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
    if (!isFinite(n) || n <= 0) return null;
    const pct = n <= 1 ? n * 100 : n;
    const rounded = Math.round(pct * 100) / 100;
    return `${rounded}% commission`;
  };

  // AR-33: collapse every match into a single vertical "suggested results" list
  // shown in one contained panel under the input, rather than separate cards
  // floating over the chat thread.
  type SuggestedRow = {
    key: string;
    icon: React.ReactNode;
    title: string;
    meta?: string;
    typeLabel: string;
    typeClass: string;
    href?: string;
    onClick?: () => void;
  };
  const suggestedRows: SuggestedRow[] = [
    ...standardSuggestions.slice(0, 3).map((s) => ({
      key: s.id,
      icon: <BookOpen className="w-5 h-5 text-purple-600" />,
      title: s.title,
      meta: s.description,
      typeLabel: 'Prompt Library',
      typeClass: 'text-purple-600',
      onClick: s.action,
    })),
    ...contextualSuggestions.slice(0, 3).map((s) => ({
      key: s.id,
      icon: getIconForSuggestionType(s.type),
      title: s.title,
      meta: s.description,
      typeLabel: 'Suggestion',
      typeClass: 'text-gray-600',
      onClick: s.action,
    })),
    ...supplierResults.slice(0, 3).map((supplier) => {
      const commission = formatCommission(supplier.data?.commissionBase);
      // Sanitize: raw supplierCategory can carry "See supplier source"/URL blobs.
      const category = sanitizeSupplierField(supplier.data?.supplierCategory);
      const meta = [category, commission].filter(Boolean).join(' · ');
      const worldviaUrl = supplier.data?.worldviaProUrl;
      return {
        key: supplier.id,
        icon: <Globe className="w-5 h-5 text-purple-600" />,
        title: supplier.title,
        meta: meta || undefined,
        typeLabel: 'Supplier',
        typeClass: 'text-purple-600',
        href: worldviaUrl || undefined,
        // Without a link the row still needs to clear the search state.
        onClick: worldviaUrl ? undefined : supplier.action,
      };
    }),
    ...tripResults.slice(0, 3).map((trip) => ({
      key: trip.id,
      icon: <Plane className="w-5 h-5 text-blue-600" />,
      title: trip.title,
      meta: [trip.subtitle, trip.data?.clientName].filter(Boolean).join(' · ') || undefined,
      typeLabel: 'Trip',
      typeClass: 'text-blue-600',
      onClick: trip.action,
    })),
    ...contactResults.slice(0, 3).map((contact) => ({
      key: contact.id,
      icon: <User className="w-5 h-5 text-orange-600" />,
      title: contact.title,
      meta: [contact.subtitle, contact.data?.phone].filter(Boolean).join(' · ') || undefined,
      typeLabel: 'Client',
      typeClass: 'text-orange-600',
      onClick: contact.action,
    })),
  ];
  const hasSuggestedRows = suggestedRows.length > 0;

  // Calculate left margin to center on canvas area (excluding sidebar)
  const sidebarWidth = sidebarExpanded ? 256 : 48; // w-64 = 256px, w-12 = 48px
  const canvasLeftOffset = sidebarWidth;
  /* AR-316: below `md` the rail is not rendered, so centring on "viewport
     minus sidebar" pushed the bar 24px off the right edge; and the bar is
     position:fixed, so it has to clear the 56px tab bar itself. */
  const isMobile = useIsMobile();

  return (
    <div 
      ref={outerRef}
      className={centered 
        ? "relative w-full px-0 z-50" 
        : `fixed w-full max-w-screen-2xl px-4 z-50 ${isMobile ? 'bottom-[76px]' : 'bottom-[20px]'}`
      }
      data-command-bar="true"
      style={!centered
        ? (isMobile
            ? { left: 0, transform: 'none' }
            // Spans the canvas between the sidebar and any docked AIVIA draft panel, so the input
            // (90% of this, up to 700px) never runs under the panel.
            : {
                left: '50%',
                width: `calc(100vw - ${canvasLeftOffset}px - var(--aivia-side-panel, 0px))`,
                transform: `translateX(calc(-50% + ${canvasLeftOffset / 2}px - var(--aivia-side-panel, 0px) / 2))`,
              })
        : {}}
    >
      {/* Command bar (existing UI remains here) */}

      
      {/* Occupied layout block (input + help line + expanded suggestions) */}
      <div ref={occupiedRef}>
        {/* Command Input (measured — see --command-bar-height) */}
        <form onSubmit={handleSubmit} className="relative" style={{ zIndex: 50 }}>
          {/* External Mode Controls - positioned above command bar */}
          {externalModeEnabled && onExternalProviderChange && onExternalModelChange && onToggleExternalMode && (
            <div className="flex justify-start mb-2 mx-auto" style={{ maxWidth: '700px', width: '90%' }}>
              <ExternalModeControls
                provider={externalModeProvider || 'ANTHROPIC'}
                model={externalModeModel || 'claude-haiku-4-5'}
                onProviderChange={onExternalProviderChange}
                onModelChange={onExternalModelChange}
                onExitExternalMode={onToggleExternalMode}
              />
            </div>
          )}
          {slashPreview && (slashMatches.length > 0 || reservedMatches.length > 0) && (
            <div className="absolute left-4 bottom-full mb-2 w-64 max-h-72 overflow-y-auto rounded-md border bg-white text-gray-900 dark:text-gray-900 shadow-lg z-50">
              {slashMatches.map((cmd) => (
                <button
                  type="button"
                  key={cmd.command}
                  className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-100 flex items-center gap-2 text-gray-900"
                  onClick={() => {
                    applyToolSelection(cmd, 'slash');
                    setCommand((prev: string) => removeLeadingCommand(prev, cmd.command));
                    setSlashPreview(null);
                  }}
                >
                  <Sparkles className="w-3 h-3 text-orange-600" />
                  <span>
                    {cmd.command} — {cmd.label}
                  </span>
                </button>
              ))}
              {reservedMatches.length > 0 && (
                <div className="border-t border-gray-100">
                  {reservedMatches.map((cmd) => (
                    <button
                      type="button"
                      key={cmd}
                      className="w-full px-3 py-2 text-sm text-left text-gray-500 dark:text-gray-500 cursor-not-allowed"
                      onClick={() => handleComingSoon(cmd)}
                    >
                      {cmd} — Coming soon
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* AR-210 (TC-34.1): the textarea's outline is transparent and nothing drew a
              focus indicator; the rounded pill shows the brand focus ring while it is focused. */}
          <div className="relative bg-white dark:bg-gray-800 overflow-visible shadow-lg border border-gray-200 dark:border-[rgba(255,117,64,0.2)] flex flex-wrap items-center px-4 py-3 rounded-[32px] min-h-[48px] mx-auto has-[textarea:focus-visible]:ring-2 has-[textarea:focus-visible]:ring-orange-500 dark:has-[textarea:focus-visible]:ring-[#fb923c]" style={{ borderRadius: 32, maxWidth: '700px', width: '90%' }}>
            <div className="relative mr-3 flex items-center">
              {/* + menu with Prompt Library, Modes, and utility actions */}
              <DropdownMenu open={plusMenuOpen} onOpenChange={setPlusMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="p-1.5 -ml-[5px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Add"
                    aria-label="Add prompts, modes and files"
                  >
                    <Plus className="w-5 h-5" aria-hidden="true" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="start" 
                  side="top" 
                  sideOffset={8}
                  className="w-56"
                >
                  {/* Prompt Library Submenu */}
                  <PromptLibrarySubmenu 
                    onSelectPrompt={handleSelectPrompt}
                    onManageMyPrompts={() => {
                      setPlusMenuOpen(false);
                      setShowMyPromptsDialog(true);
                    }}
                  />

                  {/* Modes Submenu */}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-orange-600" />
                      <span>Modes</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="w-48">
                      <DropdownMenuLabel className="text-xs text-gray-500 uppercase">
                        Active Tools
                      </DropdownMenuLabel>
                      {enabledToolCommands.map((cmd) => (
                        <DropdownMenuItem
                          key={cmd.command}
                          onClick={() => {
                            applyToolSelection(cmd, 'menu');
                            setPlusMenuOpen(false);
                          }}
                          className="flex items-center gap-2"
                        >
                          <Sparkles className="w-3.5 h-3.5 text-orange-600" />
                          <span>{cmd.label}</span>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-gray-500 uppercase">
                        Coming Soon
                      </DropdownMenuLabel>
                      {RESERVED_TOOL_COMMANDS.map((cmd) => (
                        <DropdownMenuItem
                          key={cmd}
                          disabled
                          className="text-gray-400"
                          onClick={() => handleComingSoon(cmd)}
                        >
                          {cmd}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>

                  {/* Advanced Submenu - only visible when advancedMode is enabled */}
                  {advancedModeEnabled && onOpenDefaultModel && onToggleExternalMode && (
                    <AdvancedThreadsSubmenu
                      externalModeEnabled={externalModeEnabled}
                      externalProvider={externalModeProvider}
                      externalModel={externalModeModel}
                      onOpenDefaultModel={onOpenDefaultModel}
                      onToggleExternalMode={onToggleExternalMode}
                      onCloseMenu={() => setPlusMenuOpen(false)}
                    />
                  )}

                  <DropdownMenuSeparator />

                  {/* Add photos & files */}
                  <DropdownMenuItem
                    onClick={() => {
                      (document.activeElement as HTMLElement | null)?.blur();
                      fileInputRef.current?.click();
                      setPlusMenuOpen(false);
                    }}
                    className="flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4 text-gray-500" />
                    <span>Add photos & files</span>
                  </DropdownMenuItem>

                  {/* Link Client */}
                  {onLinkContact && (
                    <DropdownMenuItem
                      onClick={() => {
                        setShowLinkClientDialog(true);
                        setPlusMenuOpen(false);
                      }}
                      className="flex items-center gap-2"
                    >
                      <UserRound className="w-4 h-4 text-gray-500" />
                      <span>Link Client</span>
                    </DropdownMenuItem>
                  )}

                  {/* Link Trip */}
                  {onLinkTrip && (
                    <DropdownMenuItem
                      onClick={() => {
                        setShowLinkTripDialog(true);
                        setPlusMenuOpen(false);
                      }}
                      className="flex items-center gap-2"
                    >
                      <Plane className="w-4 h-4 text-gray-500" />
                      <span>Link Trip</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Attachment pills: a full-width line above the input, so any number of them leaves room to type */}
            {attachments.length > 0 && (
              <div className="order-first basis-full flex items-center gap-2 flex-wrap mb-2">
                {attachments.map(att => (
                  <span key={att.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200" aria-busy={att.uploading || undefined}>
                    {att.uploading && <Loader2 className="w-3 h-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
                    <span className="max-w-[200px] truncate" title={att.filename}>{att.filename}</span>
                    {att.uploading && <span className="text-gray-500 dark:text-gray-400">Uploading…</span>}
                    <button
                      type="button"
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      onClick={async () => {
                        try {
                          if (att.text === undefined && !att.uploading && att.path) await fetch(`/api/smart-chat/upload/${att.id}`, { method: 'DELETE', credentials: 'include' });
                        } catch {}
                        setAttachments(prev => prev.filter(a => a.id !== att.id));
                      }}
                      aria-label={`Remove ${att.filename}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {activeToolSignal && (
              <div className="flex items-center gap-1 mr-3 px-2 py-1 rounded-full text-xs bg-orange-50 text-orange-700 border border-orange-100">
                <Sparkles className="w-3 h-3" />
                <span>{activeToolLabel || 'Selected tool'}</span>
                <button
                  type="button"
                  aria-label="Clear tool selection"
                  className="text-orange-700 hover:text-orange-900"
                  onClick={() => setActiveToolSignal(null)}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            <textarea
              ref={textareaRef}
              data-command-input="true"
              value={command}
              onChange={(e) => handleCommandChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !(e.ctrlKey || e.metaKey) && !e.shiftKey) {
                  e.preventDefault();
                  if (command.trim() && !isProcessing) {
                    handleSubmit(e as unknown as React.FormEvent);
                  }
                }
                // Ctrl/Cmd+Enter (or Shift+Enter) will insert a newline naturally
              }}
              rows={1}
              placeholder={(() => {
                try {
                  const loc = window.location.pathname;
                  if (loc.startsWith('/trips')) return 'Tell me how you\'d like to modify the trip or Ask anything else...';
                  if (loc.startsWith('/threads')) return 'Ask anything...';
                } catch {}
                return 'Ask anything...';
              })()}
              aria-label="Message AIVIA"
              className="flex-1 min-w-[8rem] border-none outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 resize-none leading-6 max-h-[160px] bg-transparent text-left align-middle"
              disabled={isProcessing}
            />
            <div className="flex items-end space-x-2 ml-3 flex-shrink-0 self-end">
              {activeProject?.id ? (
                <div className="hidden md:flex items-center text-sm text-gray-600 dark:text-gray-400 mr-2">
                  <button type="button" className={`px-2 py-1 rounded-l border ${runScope==='global'?'bg-orange-50 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800':'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'}`} onClick={chooseGlobal}>Global</button>
                  <button type="button" className={`px-2 py-1 rounded-r border -ml-px ${runScope==='project'?'bg-orange-50 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800':'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'}`} onClick={()=>{ explicitGlobalRef.current = false; setRunScope('project'); }}>Project</button>
                </div>
              ) : null}
              {/* Remove right-side + menu (submit-only area now) */}
              <button
                type="submit"
                aria-label={isProcessing ? "Sending message" : "Send message"}
                disabled={!command.trim() || isProcessing}
                className="p-2 bg-orange-600 text-white rounded-full hover:bg-orange-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessing ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                ) : (
                  <Send className="w-5 h-5" aria-hidden="true" />
                )}
              </button>
            </div>
            {/* Hidden file input for uploads */}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              onChange={async (e) => {
                const target = e.currentTarget as HTMLInputElement | null;
                const files = Array.from(target?.files || []);
                if (files.length === 0) return;
                if (agentAttachmentsEnabled) {
                  // AIVIA reads each file now and keeps only its text, sent with the message. Each file
                  // shows an "Uploading…" pill at once. Too-large files are refused before uploading, and
                  // AIVIA reads up to AGENT_MAX_FILES files per message (the server drops the rest), so
                  // extra files are refused here by name instead of disappearing silently. The toast
                  // shows one message at a time, so problems known up front show at once and the same
                  // toast is updated with any upload failures.
                  const problems: string[] = [];
                  const notes: string[] = [];
                  const fitting = files.filter(file => {
                    const limit = agentAttachmentLimit(file);
                    if (file.size <= limit) return true;
                    problems.push(`${file.name} is ${formatFileSize(file.size)}; AIVIA can read files up to ${formatFileSize(limit)}.`);
                    return false;
                  });
                  const slots = Math.max(0, AGENT_MAX_FILES - attachments.filter(a => a.text !== undefined || a.uploading).length);
                  const skipped = fitting.slice(slots);
                  if (skipped.length) {
                    problems.push(`AIVIA reads up to ${AGENT_MAX_FILES} files per message. Not attached: ${skipped.map(f => f.name).join(', ')}. Send them in your next message.`);
                  }
                  const problemToast = (list: string[]) => ({
                    title: list.length === 1 ? "A file wasn't attached" : "Some files weren't attached",
                    description: [...list, ...notes].join(' '),
                    variant: 'destructive' as const,
                  });
                  const known = problems.length;
                  const shown = known ? toast(problemToast(problems)) : null;
                  await Promise.all(fitting.slice(0, slots).map(async (file, i) => {
                    const id = `agent-${Date.now()}-${i}`;
                    setAttachments(prev => [...prev, { id, path: '', filename: file.name, uploading: true }]);
                    try {
                      const form = new FormData();
                      form.append('file', file);
                      const resp = await fetch('/api/threads-agent/attachments', { method: 'POST', body: form, credentials: 'include' });
                      const data = await resp.json().catch(() => ({}));
                      if (!resp.ok) throw new Error(typeof data?.message === 'string' ? data.message : "That file couldn't be read.");
                      // A pill removed while uploading stays removed.
                      setAttachments(prev => prev.map(a => (a.id === id ? { id, path: '', filename: data.name, text: data.text } : a)));
                      if (data.truncated) notes.push(`${data.name} is long; AIVIA will read the first part of it.`);
                    } catch (err) {
                      setAttachments(prev => prev.filter(a => a.id !== id));
                      problems.push(`${file.name}: ${(err as Error).message}`);
                    }
                  }));
                  if (shown && (problems.length > known || notes.length)) {
                    shown.update({ id: shown.id, open: true, ...problemToast(problems) });
                  } else if (problems.length > known) {
                    toast(problemToast(problems));
                  } else if (!shown && notes.length) {
                    toast({ title: 'Attached', description: notes.join(' ') });
                  }
                  if (target) target.value = '';
                  return;
                }
                for (const file of files) {
                  try {
                    const form = new FormData();
                    form.append('file', file);
                    const resp = await fetch('/api/smart-chat/upload', {
                      method: 'POST',
                      body: form,
                      credentials: 'include'
                    });
                    if (!resp.ok) throw new Error('Upload failed');
                    const data = await resp.json();
                    const id = (data.path as string).split('/').pop();
                    setAttachments(prev => [...prev, { id: id || data.path, path: data.path, filename: data.filename }]);
                  } catch (err) {
                    console.error('Upload error:', err);
                    // Quiet failure to avoid linter complaining about duplicate hook scope
                  }
                }
                // Reset the input so selecting the same file again triggers change
                if (target) target.value = '';
              }}
            />
          </div>
        </form>
        {/* Scope/help line below the command bar - hidden when hideFooter is true */}
        {!hideFooter && (
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mt-2 mx-auto px-4 relative" style={{ maxWidth: '700px', width: '90%', zIndex: 51 }}>
            <div className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-2">
              <span>Working in:</span>
              {onSetScopeProject && projectsData && projectsData.length > 0 ? (
                <select
                  aria-label="Working in"
                  value={runScope === 'global' ? 'global' : (activeProject?.id?.toString() || 'global')}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'global') {
                      chooseGlobal();
                      onSetScopeProject(null);
                      localStorage.setItem('commandBarScope', JSON.stringify({ scope: 'global' }));
                    } else {
                      const projectId = parseInt(value, 10);
                      const selectedProject = projectsData.find((p: any) => p.id === projectId);
                      if (selectedProject) {
                        explicitGlobalRef.current = false;
                        setRunScope('project');
                        onSetScopeProject(projectId, selectedProject);
                        localStorage.setItem('commandBarScope', JSON.stringify({
                          scope: 'project',
                          projectId,
                          projectName: selectedProject.name
                        }));
                      }
                    }
                  }}
                  className="font-medium text-xs bg-transparent dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded px-2 py-0.5 cursor-pointer hover:border-orange-500 dark:hover:border-orange-400 focus:outline-none focus:border-orange-500 dark:focus:border-orange-400 focus:ring-1 focus:ring-orange-500 dark:focus:ring-orange-400"
                >
                  <option value="global">Global</option>
                  {projectsData
                    .sort((a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
                    .map((project: any) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                </select>
              ) : (
                <span className="font-medium">{runScope === 'project' && activeProject?.name ? activeProject.name : 'Global'}</span>
              )}
            </div>
            <div className="flex min-w-0 items-center gap-3">
              {/* AR-316: a physical-keyboard affordance, and on a phone it was
                  squeezing this row into three cramped wrapping columns. */}
              <div className="hidden text-xs text-gray-500 dark:text-gray-400 sm:block">Shift+Enter for new line</div>
              {showFooterDisclaimer && Array.isArray((window as any).__currentSessionMessages) && (window as any).__currentSessionMessages.length > 0 && (
                <div className="text-xs italic text-gray-500 dark:text-gray-400">AIVIA can make mistakes. Check important info.</div>
              )}
            </div>
          </div>
        )}

        {/* AR-33: unified "suggested results" panel — a single contained list shown
            under the input instead of separate cards floating over the chat thread. */}
        <div className="mt-3" style={{ position: 'relative', zIndex: 50 }}>
          {hasSuggestedRows && (
            <div className="mx-auto" style={{ maxWidth: '700px', width: '90%' }}>
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                {/* Header: Suggestions pill + collapse toggle */}
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                  <span className="inline-block px-3 py-1 bg-orange-500 text-white text-xs font-semibold rounded-full">
                    Suggestions
                  </span>
                  <button
                    type="button"
                    aria-label={suggestionsCollapsed ? 'Expand suggestions' : 'Collapse suggestions'}
                    aria-expanded={!suggestionsCollapsed}
                    onClick={() => setSuggestionsCollapsed((v) => !v)}
                    className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    {suggestionsCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  </button>
                </div>

                {/* Suggested results as rows */}
                {!suggestionsCollapsed && (
                  <div className="max-h-[280px] overflow-y-auto divide-y divide-gray-100">
                    {suggestedRows.map((row) => {
                      const content = (
                        <div className="flex items-start gap-3 px-4 py-3 text-left w-full hover:bg-gray-50 transition-colors">
                          <div className="flex-shrink-0 mt-0.5">{row.icon}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1">
                              <p className="text-sm font-semibold text-gray-900 truncate" title={row.title}>
                                {row.title}
                              </p>
                              {row.href && <ArrowUpRight className="w-3 h-3 shrink-0 text-gray-400" />}
                            </div>
                            {row.meta && <p className="text-sm text-gray-500 mt-0.5 truncate">{row.meta}</p>}
                            <span className={`inline-block mt-1 text-xs ${row.typeClass}`}>{row.typeLabel}</span>
                          </div>
                        </div>
                      );
                      return row.href ? (
                        <a
                          key={row.key}
                          href={row.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full"
                          onClick={() => {
                            // Cleanup only — native href/target handles navigation
                            setSearchResults([]);
                            setContextualSuggestions([]);
                            setCommand('');
                          }}
                        >
                          {content}
                        </a>
                      ) : (
                        <button key={row.key} type="button" onClick={row.onClick} className="block w-full">
                          {content}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Link Client Dialog with Autocomplete Search */}
      <CommandDialog open={showLinkClientDialog} onOpenChange={setShowLinkClientDialog}>
        <CommandInput placeholder="Search clients..." />
        <CommandList>
          <CommandEmpty>No clients found.</CommandEmpty>
          <CommandGroup heading="Available Clients">
            {contactOptions
              .filter((c: any) => !linkedContactIds.has(getContactIdentifier(c)))
              .map((contact: any) => {
                const name = contact.preferredName || [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.email || 'Unknown';
                return (
                  <CommandItem
                    key={contact.id}
                    value={`${name} ${contact.email || ''}`}
                    onSelect={() => {
                      if (!onLinkContact) return;
                      const numericId = coerceNumericId(contact.id ?? contact.contactId);
                      if (numericId === null) {
                        console.warn('Unable to parse contact id for linking', contact);
                        return;
                      }
                      onLinkContact(numericId);
                      setShowLinkClientDialog(false);
                    }}
                  >
                    <UserRound className="mr-2 h-4 w-4" />
                    <div className="flex flex-col">
                      <span className="font-medium">{name}</span>
                      {contact.email && <span className="text-xs text-gray-500">{contact.email}</span>}
                    </div>
                  </CommandItem>
                );
              })}
          </CommandGroup>
        </CommandList>
      </CommandDialog>

      {/* Link Trip Dialog with Autocomplete Search */}
      <CommandDialog open={showLinkTripDialog} onOpenChange={setShowLinkTripDialog}>
        <CommandInput placeholder="Search trips..." />
        <CommandList>
          <CommandEmpty>No trips found.</CommandEmpty>
          <CommandGroup heading="Available Trips">
            {tripOptions
              .filter((t: any) => !linkedTripIds.has(getTripIdentifier(t)))
              .map((trip: any) => {
                const tripName = trip.name || 'Untitled trip';
                const client = trip.clientName || '';
                return (
                  <CommandItem
                    key={trip.id}
                    value={`${tripName} ${client}`}
                    onSelect={() => {
                      if (!onLinkTrip) return;
                      const numericId = coerceNumericId(trip.id ?? trip.tripId);
                      if (numericId === null) {
                        console.warn('Unable to parse trip id for linking', trip);
                        return;
                      }
                      onLinkTrip(numericId);
                      setShowLinkTripDialog(false);
                    }}
                  >
                    <Plane className="mr-2 h-4 w-4" />
                    <div className="flex flex-col">
                      <span className="font-medium">{tripName}</span>
                      {client && <span className="text-xs text-gray-500">{client}</span>}
                    </div>
                  </CommandItem>
                );
              })}
          </CommandGroup>
        </CommandList>
      </CommandDialog>

      {/* Change Model Dialog */}
      {onProviderChange && onModelChange && (
        <Dialog
          open={showChangeModelDialog}
          onOpenChange={(open) => {
            if (!open) {
              closeChangeModelDialog();
            }
          }}
        >
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Change AI Model</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-2">
              <div>
                <h4 className="text-sm font-medium mb-3 text-gray-700">AI Provider</h4>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {providers.length > 0 ? (
                    providers.map((provider: any) => {
                      const availableCount = provider.models?.filter((model: any) => model.available).length || 0;
                      const isSelected = tempProvider === provider.provider;
                      const isUnavailable = availableCount === 0;

                      return (
                        <button
                          key={provider.provider}
                          type="button"
                          disabled={isUnavailable}
                          aria-disabled={isUnavailable}
                          className={`w-full text-left px-4 py-3 rounded border transition-colors ${
                            isSelected
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:border-orange-300 hover:bg-gray-50'
                          } ${isUnavailable ? 'opacity-60 cursor-not-allowed hover:border-gray-200 hover:bg-white' : ''}`}
                          onClick={() => {
                            if (isUnavailable) return;
                            setTempProvider(provider.provider);
                            const nextModelId = getFirstAvailableModelId(provider.provider);
                            setTempModel(nextModelId);
                          }}
                        >
                          <div className="font-medium">{provider.name}</div>
                          <div
                            className={`text-xs ${isUnavailable ? 'text-red-500' : 'text-gray-500'}`}
                          >
                            {isUnavailable
                              ? 'No models available'
                              : `${availableCount} model${availableCount === 1 ? '' : 's'} available`}
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="flex flex-col items-center justify-center rounded border border-dashed border-gray-200 py-6 text-sm text-gray-500">
                      No providers available
                    </div>
                  )}
                  {!hasProvidersWithModels && providers.length > 0 && (
                    <div className="rounded border border-dashed border-gray-200 py-6 text-center text-sm text-gray-500">
                      No providers currently have models available. Please try again later.
                    </div>
                  )}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-3 text-gray-700">Model</h4>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {availableModelsForTempProvider.length > 0 ? (
                    availableModelsForTempProvider.map((model: any) => (
                      <button
                        key={model.id}
                        type="button"
                        className={`w-full text-left px-4 py-3 rounded border transition-colors ${
                          tempModel === model.id
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-orange-300 hover:bg-gray-50'
                        }`}
                        onClick={() => setTempModel(model.id)}
                      >
                        <div className="font-medium">{model.name}</div>
                        {model.description && (
                          <div className="text-sm text-gray-500 mt-1">{model.description}</div>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center rounded border border-dashed border-gray-200 py-6 text-sm text-gray-500">
                      No models available for this provider
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter className="flex gap-3">
              <Button type="button" variant="outline" onClick={closeChangeModelDialog}>
                Cancel
              </Button>
              <Button type="button" onClick={handleApplyChangeModel} disabled={!canApplyChangeModel}>
                Apply
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* My Prompts Management Dialog */}
      <MyPromptsDialog
        open={showMyPromptsDialog}
        onOpenChange={setShowMyPromptsDialog}
      />
    </div>
  );
};

// Simple Canvas - shows just the heading and centered command bar when no active thread/project
const SampleQueriesCanvas: React.FC<{ 
  onQuerySelect: (query: string, toolSignal?: ToolSignal | null, agentAttachments?: AgentAttachment[]) => void;
  messageInput: string;
  setMessageInput: (value: string) => void;
  isProcessing: boolean;
  sidebarExpanded: boolean;
  onDirectDataDisplay: (type: 'contact' | 'trip' | 'supplier', data: any) => void;
  contactsData: any[];
  tripsData: any[];
  searchDataLoaded: boolean;
  providers?: any[];
  selectedProvider?: string;
  selectedModel?: string;
  onProviderChange?: (provider: string) => void;
  onModelChange?: (model: string) => void;
  // Advanced mode props
  advancedModeEnabled?: boolean;
  externalModeEnabled?: boolean;
  externalModeProvider?: string;
  externalModeModel?: string;
  onOpenDefaultModel?: () => void;
  onToggleExternalMode?: () => void;
  onExternalProviderChange?: (provider: string) => void;
  onExternalModelChange?: (model: string) => void;
}> = ({
  onQuerySelect,
  messageInput,
  setMessageInput,
  isProcessing,
  sidebarExpanded,
  onDirectDataDisplay,
  contactsData,
  tripsData,
  searchDataLoaded,
  providers = [],
  selectedProvider = '',
  selectedModel = '',
  onProviderChange,
  onModelChange,
  advancedModeEnabled = false,
  externalModeEnabled = false,
  externalModeProvider,
  externalModeModel,
  onOpenDefaultModel,
  onToggleExternalMode,
  onExternalProviderChange,
  onExternalModelChange,
}) => {
  
  // Calculate the offset for centering based on sidebar state
  const sidebarWidth = sidebarExpanded ? 240 : 48; // w-60 = 240px, w-12 = 48px
  const canvasLeftOffset = sidebarWidth;
  /* AR-316: this nudge centres the empty state over "viewport minus sidebar".
     Below `md` the sidebar is not rendered, so the nudge just pushed the
     greeting and the composer 24px off-centre — 24px of gap on the left
     against 72px on the right. */
  const isMobile = useIsMobile();

  return (
    <div className="flex-1 flex items-center justify-center">
      <div 
        className="text-center w-full max-w-4xl px-8"
        style={{
          transform: isMobile ? 'none' : `translateX(${-canvasLeftOffset / 2}px)`
        }}
      >
        <MessageSquare className="w-16 h-16 text-orange-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8">
          What can I help you with today?
        </h1>
        
        {/* Centered Command Bar */}
        <div className="w-full max-w-2xl mx-auto">
          <CommandBar 
            centered={true}
            onSubmit={(cmd, scope, toolSignal, agentAttachments) => {
              onQuerySelect(cmd, toolSignal ?? null, agentAttachments);
            }}
            isProcessing={isProcessing}
            onDirectDataDisplay={onDirectDataDisplay}
            contactsData={contactsData}
            tripsData={tripsData}
            searchDataLoaded={searchDataLoaded}
            sidebarExpanded={sidebarExpanded}
            activeProject={null}
            projectsData={EMPTY_PROJECTS}
            providers={providers}
            selectedProvider={selectedProvider}
            selectedModel={selectedModel}
            onProviderChange={onProviderChange}
            onModelChange={onModelChange}
            advancedModeEnabled={advancedModeEnabled}
            externalModeEnabled={externalModeEnabled}
            externalModeProvider={externalModeProvider}
            externalModeModel={externalModeModel}
            onOpenDefaultModel={onOpenDefaultModel}
            onToggleExternalMode={onToggleExternalMode}
            onExternalProviderChange={onExternalProviderChange}
            onExternalModelChange={onExternalModelChange}
          />
        </div>
        
      </div>
    </div>
  );
};

// T-396: Homepage post panel item type
interface HomepagePostItem {
  id: number;
  title: string;
  summary?: string;
  slug: string;
  imageFeatured?: string;
  eventDate?: string;
}

// T-396: DashboardCanvas props including command bar integration
interface DashboardCanvasProps {
  onNavigate?: (canvas: CanvasType) => void;
  // Command bar props for inline rendering
  onCommand: (command: string, toolSignal?: ToolSignal | null, agentAttachments?: AgentAttachment[]) => void;
  isProcessing: boolean;
  onDirectDataDisplay: (type: 'contact' | 'trip' | 'supplier', data: any) => void;
  contactsData: any[];
  tripsData: any[];
  searchDataLoaded: boolean;
  sidebarExpanded: boolean;
  providers?: any[];
  selectedProvider?: string;
  selectedModel?: string;
  onProviderChange?: (provider: string) => void;
  onModelChange?: (model: string) => void;
  advancedModeEnabled?: boolean;
  externalModeEnabled?: boolean;
  externalModeProvider?: string;
  externalModeModel?: string;
  onOpenDefaultModel?: () => void;
  onToggleExternalMode?: () => void;
  onExternalProviderChange?: (provider: string) => void;
  onExternalModelChange?: (model: string) => void;
  // Callback when inline command bar visibility changes (for showing global command bar)
  onInlineCommandBarVisibilityChange?: (visible: boolean) => void;
}

// Canvas Components
const DashboardCanvas: React.FC<DashboardCanvasProps> = ({
  onNavigate,
  onCommand,
  isProcessing,
  onDirectDataDisplay,
  contactsData,
  tripsData,
  searchDataLoaded,
  sidebarExpanded,
  providers,
  selectedProvider,
  selectedModel,
  onProviderChange,
  onModelChange,
  advancedModeEnabled,
  externalModeEnabled,
  externalModeProvider,
  externalModeModel,
  onOpenDefaultModel,
  onToggleExternalMode,
  onExternalProviderChange,
  onExternalModelChange,
  onInlineCommandBarVisibilityChange,
}) => {
  const [postsData, setPostsData] = React.useState<{
    // Legacy fields
    news: any[];
    event: any | null;
    // T-396: New structured panels
    ticker?: { items: any[] };
    panels?: {
      news: HomepagePostItem[];
      promotions: HomepagePostItem[];
      episodes: HomepagePostItem[];
      events: HomepagePostItem[];
    };
  }>({ news: [], event: null });
  const [postsLoading, setPostsLoading] = React.useState(true);
  const { toast } = useToast();
  const { isEnabled: isResolvedEnabled, isLoading: flagsLoading } = useResolvedFeatureFlags();

  // Fetch posts for homepage
  React.useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch('/api/posts/homepage');
        if (response.ok) {
          const data = await response.json();
          setPostsData(data);
        } else {
          console.error('Failed to fetch homepage posts:', response.statusText);
          // Set empty data on error to show fallback UI
          setPostsData({ news: [], event: null, panels: { news: [], promotions: [], episodes: [], events: [] } });
        }
      } catch (error) {
        console.error('Error fetching homepage posts:', error);
        // Set empty data on error to show fallback UI
        setPostsData({ news: [], event: null, panels: { news: [], promotions: [], episodes: [], events: [] } });
      } finally {
        setPostsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Helper function to open post in new tab
  const openPost = (slug: string) => {
    const url = `https://worldvia.pro/pro-post/${slug}`;
    window.open(url, '_blank');
  };

  type DashboardNavItem = {
    title: string;
    description: string;
    icon: JSX.Element;
    canvas?: CanvasType;
    gated?: boolean;
    nonInteractive?: boolean;
  };

  const dashboardItems: DashboardNavItem[] = [
    {
      title: 'Threads',
      description: 'AI conversation workspaces',
      icon: <MessageSquare className="w-6 h-6 text-orange-600" />,
      canvas: 'threads',
      gated: false,
    },
    ...(flagsLoading || !isResolvedEnabled('invoicingMvp')
      ? []
      : [
          {
            title: 'Finances',
            description: 'Track advisor payouts and receivables',
            icon: <Receipt className="w-6 h-6 text-orange-600" />,
            canvas: 'payments' as CanvasType,
            gated: false,
          },
        ]),
    {
      title: 'CRM (Soon)',
      description: 'Search and organize your CRM',
      icon: <Users className="w-6 h-6 text-gray-400" />,
      gated: true,
      nonInteractive: true,
    },
  ];

  /* FUTURE: Role-based full home nav for admin roles — uncomment and replace dashboardItems above.
  const { user } = useAuth();
  const showFullNav = isAdminRole(user?.role);
  const { gates: alphaGates } = useAlphaFeatureGates();
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const featureFlags = getFeatureFlags();

  let dashboardItems: DashboardNavItem[];
  if (!showFullNav) {
    dashboardItems = [ ... current simplified array ... ];
  } else {
    const dashboardCore: DashboardNavItem[] = [
      {
        title: 'Threads',
        description: 'AI conversation workspaces',
        icon: <MessageSquare className="w-6 h-6 text-orange-600" />,
        canvas: 'threads' as CanvasType,
        gated: false,
      },
      {
        title: 'Pipeline',
        description: 'Track your opportunities',
        icon: <Filter className={`w-6 h-6 ${isFeatureGated(alphaGates, 'pipeline') ? 'text-gray-400' : 'text-orange-600'}`} />,
        canvas: 'pipeline' as CanvasType,
        gated: isFeatureGated(alphaGates, 'pipeline'),
      },
      ...(currentPath === '/threads'
        ? []
        : [
            {
              title: 'Clients',
              description: 'Search and organize your CRM',
              icon: <Users className={`w-6 h-6 ${isFeatureGated(alphaGates, 'clients') ? 'text-gray-400' : 'text-orange-600'}`} />,
              canvas: 'clients' as CanvasType,
              gated: isFeatureGated(alphaGates, 'clients'),
            },
          ]),
      {
        title: 'Trips',
        description: 'Create amazing travel experiences',
        icon: <Calendar className={`w-6 h-6 ${isFeatureGated(alphaGates, 'trips') ? 'text-gray-400' : 'text-orange-600'}`} />,
        canvas: 'trips' as CanvasType,
        gated: isFeatureGated(alphaGates, 'trips'),
      },
      {
        title: 'Proposals',
        description: 'Create and manage proposals',
        icon: <FileText className={`w-6 h-6 ${isFeatureGated(alphaGates, 'proposals') ? 'text-gray-400' : 'text-orange-600'}`} />,
        canvas: 'proposals' as CanvasType,
        gated: isFeatureGated(alphaGates, 'proposals'),
      },
    ];
    const dashboardDeferred = [
      featureFlags.booking
        ? {
            title: 'Booking',
            description: 'Handle reservations and bookings',
            icon: <Plane className="w-6 h-6 text-orange-600" />,
            canvas: 'booking' as CanvasType,
          }
        : null,
      featureFlags.commissions
        ? {
            title: 'Commissions',
            description: 'Track advisor payouts and receivables',
            icon: <CreditCard className="w-6 h-6 text-orange-600" />,
            canvas: 'payments' as CanvasType,
          }
        : null,
      featureFlags.analytics
        ? {
            title: 'Analytics',
            description: 'Insights and reporting',
            icon: <BarChart3 className="w-6 h-6 text-orange-600" />,
            canvas: 'analytics' as CanvasType,
          }
        : null,
      featureFlags.digitalPresence
        ? {
            title: 'Digital Presence',
            description: 'Manage content, web and social',
            icon: <Globe className="w-6 h-6 text-orange-600" />,
            canvas: 'content' as CanvasType,
          }
        : null,
    ].filter(Boolean) as DashboardNavItem[];
    dashboardItems = [...dashboardCore, ...dashboardDeferred.map((item) => ({ ...item, gated: false }))];
  }
  */

  // T-396: Get ticker items from new structure or fallback to legacy
  const tickerItems = postsData.ticker?.items || 
    [...(postsData.news || []), ...(postsData.event ? [postsData.event] : [])];

  // News story carousel state
  const [currentStoryIndex, setCurrentStoryIndex] = React.useState(0);
  const goToNextStory = () => {
    if (tickerItems.length > 0) {
      setCurrentStoryIndex((prev) => (prev + 1) % tickerItems.length);
    }
  };
  const goToPrevStory = () => {
    if (tickerItems.length > 0) {
      setCurrentStoryIndex((prev) => (prev - 1 + tickerItems.length) % tickerItems.length);
    }
  };
  const currentStory = tickerItems[currentStoryIndex] || null;

  // T-396: Get panel data (now includes events)
  const panels = postsData.panels || { news: [], promotions: [], episodes: [], events: [] };

  // Track inline command bar visibility - when it scrolls out of view, show global command bar
  const commandBarRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const commandBarEl = commandBarRef.current;
    if (!commandBarEl) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Report visibility to parent - true when visible, false when scrolled out
        onInlineCommandBarVisibilityChange?.(entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px' }
    );

    observer.observe(commandBarEl);
    return () => observer.disconnect();
  }, [onInlineCommandBarVisibilityChange]);

  // Helper to render a panel column
  const renderPanelColumn = (
    title: string, 
    icon: React.ReactNode, 
    items: HomepagePostItem[], 
    emptyText: string,
    seedPrefix: string
  ) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          {icon}
          {title}
        </h3>
      </div>
      <div className="max-h-[280px] overflow-y-auto">
        {postsLoading ? (
          <div className="p-4 space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="animate-pulse flex gap-3">
                <div className="w-14 h-10 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {items.map((post) => (
              <button
                key={post.id}
                onClick={() => openPost(post.slug)}
                className="w-full p-2.5 flex gap-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
              >
                <div className="w-14 h-10 rounded bg-gray-100 dark:bg-gray-700 overflow-hidden shrink-0">
                  {post.imageFeatured ? (
                    <img 
                      src={post.imageFeatured} 
                      alt="" 
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.src = `https://picsum.photos/seed/${seedPrefix}-${post.id}/56/40`; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {icon}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 dark:text-gray-100 line-clamp-2">{post.title}</p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="p-4 text-center text-xs text-gray-500 dark:text-gray-400">
            {emptyText}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="w-full h-full flex flex-col overflow-y-auto">
      {/* 1. News story carousel (static, with navigation) */}
      <div className="py-3 px-4">
        <div className="w-[80%] mx-auto">
          <div className="flex items-center bg-black/5 dark:bg-white/10 rounded-full px-4 py-2">
            <span className="text-xs font-medium text-orange-600 dark:text-orange-400 mr-3 shrink-0">📈 BREAKING:</span>
            <div className="flex-1 min-w-0">
              {postsLoading ? (
                <span className="text-xs text-gray-700 dark:text-gray-300 animate-pulse">Loading latest updates...</span>
              ) : currentStory ? (
                <div className="text-xs text-gray-700 dark:text-gray-300 truncate">
                  <span className="font-bold">{currentStory.title}</span>
                  {currentStory.slug && (
                    <a 
                      href={`https://worldvia.pro/pro-post/${currentStory.slug}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="ml-2 text-orange-600 hover:text-orange-700 underline font-medium"
                    >
                      read more
                      <span className="sr-only">: {currentStory.title} (opens in a new tab)</span>
                    </a>
                  )}
                </div>
              ) : (
                <span className="text-xs text-gray-700 dark:text-gray-300">Stay tuned for the latest updates</span>
              )}
            </div>
            {/* Navigation arrows */}
            {tickerItems.length > 1 && (
              <div className="flex items-center gap-1 ml-3 shrink-0">
                <button
                  onClick={goToPrevStory}
                  className="p-1 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                  aria-label="Previous story"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </button>
                <span className="text-xs text-gray-400 dark:text-gray-500 min-w-[28px] text-center">
                  {currentStoryIndex + 1}/{tickerItems.length}
                </span>
                <button
                  onClick={goToNextStory}
                  className="p-1 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                  aria-label="Next story"
                >
                  <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 2. Icon + "What can I help you with today?" */}
      <div className="pt-10 pb-6 text-center">
        <MessageSquare className="w-16 h-16 text-orange-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          What can I help you with today?
        </h1>
      </div>

      {/* 3. Inline Command Bar (not sticky - global footer bar shows when this scrolls out of view) */}
      <div ref={commandBarRef} className="py-3 px-4">
        <div className="w-full max-w-2xl mx-auto">
          <CommandBar 
            centered={true}
            showFooterDisclaimer={false}
            onSubmit={(cmd, _scope, toolSignal, agentAttachments) => {
              onCommand(cmd, toolSignal, agentAttachments);
            }}
            isProcessing={isProcessing}
            onDirectDataDisplay={onDirectDataDisplay}
            contactsData={contactsData}
            tripsData={tripsData}
            searchDataLoaded={searchDataLoaded}
            sidebarExpanded={sidebarExpanded}
            activeProject={null}
            projectsData={EMPTY_PROJECTS}
            providers={providers}
            selectedProvider={selectedProvider}
            selectedModel={selectedModel}
            onProviderChange={onProviderChange}
            onModelChange={onModelChange}
            advancedModeEnabled={advancedModeEnabled}
            externalModeEnabled={externalModeEnabled}
            externalModeProvider={externalModeProvider}
            externalModeModel={externalModeModel}
            onOpenDefaultModel={onOpenDefaultModel}
            onToggleExternalMode={onToggleExternalMode}
            onExternalProviderChange={onExternalProviderChange}
            onExternalModelChange={onExternalModelChange}
          />
          <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-2">
            AIVIA can make mistakes, so double-check it
          </p>
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 py-6 px-4">
        {/* 4. Navigation cards - up to 5 per row */}
        <div className="max-w-5xl mx-auto mt-[30px] mb-[60px]">
          <div className="flex flex-wrap justify-center gap-3">
            {dashboardItems.map((item, index) => (
              <button
                key={index}
                aria-disabled={item.gated ? true : undefined}
                onClick={() => {
                  if (item.gated) {
                    if (item.nonInteractive) return;
                    toast({
                      title: 'Coming soon...',
                      description: 'This feature will be available in a future update.',
                      duration: 4000,
                    });
                    return;
                  }
                  if (item.canvas) onNavigate?.(item.canvas);
                }}
                className={`w-[calc(50%-0.5rem)] sm:w-[calc(33.333%-0.5rem)] md:w-[calc(20%-0.6rem)] md:min-w-[120px] p-3 rounded-lg text-left transition-colors border focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                  item.gated
                    ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-60 border-gray-200 dark:border-gray-700'
                    : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-transparent hover:border-orange-200 dark:hover:border-orange-700'
                }`}
              >
                <div className="flex items-center gap-2">
                  {item.icon}
                  {item.gated && (
                    <Badge aria-hidden="true" variant="secondary" className="text-xs px-1.5 py-0 h-4 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                      Soon
                    </Badge>
                  )}
                </div>
                <p className={`text-sm font-medium mt-2 ${item.gated ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>{item.title}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{item.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* 5. Four-column content section - News / Episodes / Events / Promotions */}
        <div className="max-w-6xl mx-auto mb-[500px]">
          <h2 className="sr-only">Latest from WorldVia</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {renderPanelColumn('News', <Newspaper className="w-4 h-4 text-orange-600" />, panels.news || [], 'No news available', 'news')}
            {renderPanelColumn('Episodes', <PlayCircle className="w-4 h-4 text-orange-600" />, panels.episodes || [], 'No episodes available', 'episode')}
            {renderPanelColumn('Events', <Calendar className="w-4 h-4 text-orange-600" />, panels.events || [], 'No events available', 'event')}
            {renderPanelColumn('Promotions', <Tag className="w-4 h-4 text-orange-600" />, panels.promotions || [], 'No promotions available', 'promo')}
          </div>
        </div>
      </div>
    </div>
  );
};

// Add CanvasPipeline component
const CanvasPipeline: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const { isDarkMode, setIsDarkMode } = useTheme();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [expandedStages, setExpandedStages] = React.useState<Set<string>>(new Set(['lead', 'consultation']));

  // Define pipeline stages with subtle orange accents and white backgrounds
  const PIPELINE_STAGES = [
    {
      id: 'lead',
      title: 'Lead',
      description: 'Initial inquiry or interest',
      color: 'text-orange-700',
      bgColor: 'bg-white border-gray-200',
      headerBg: 'bg-gray-50',
      count: 0,
      value: 0
    },
    {
      id: 'consultation',
      title: 'Consultation',
      description: 'First meeting scheduled/completed',
      color: 'text-orange-700',
      bgColor: 'bg-white border-gray-200',
      headerBg: 'bg-gray-50',
      count: 0,
      value: 0
    },
    {
      id: 'proposal',
      title: 'Proposal',
      description: 'Proposal sent, awaiting response',
      color: 'text-orange-700',
      bgColor: 'bg-white border-gray-200',
      headerBg: 'bg-gray-50',
      count: 0,
      value: 0
    },
    {
      id: 'deposit',
      title: 'Under Deposit',
      description: 'Deposit received, booking in progress',
      color: 'text-orange-700',
      bgColor: 'bg-white border-gray-200',
      headerBg: 'bg-gray-50',
      count: 0,
      value: 0
    },
    {
      id: 'booked',
      title: 'Booked',
      description: 'Trip confirmed and booked',
      color: 'text-orange-700',
      bgColor: 'bg-white border-gray-200',
      headerBg: 'bg-gray-50',
      count: 0,
      value: 0
    }
  ];

  const toggleStage = (stageId: string) => {
    const newExpanded = new Set(expandedStages);
    if (expandedStages.has(stageId)) {
      newExpanded.delete(stageId);
    } else {
      newExpanded.add(stageId);
    }
    setExpandedStages(newExpanded);
  };

  const handleTripClick = (tripId?: number) => {
    if (tripId) {
      navigate(`/trips/${tripId}`);
    } else {
      // For mock data, navigate to trips list
      navigate('/trips');
    }
  };

  // Fetch trips data
  const { data: tripsData, isLoading } = useQuery({
    queryKey: ["/api/trips"],
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  // Mock pipeline data with realistic distribution
  const pipelineData = React.useMemo(() => {
    if (!tripsData || !Array.isArray(tripsData)) return PIPELINE_STAGES;

    // Simulate trip distribution across pipeline stages
    const stages = [...PIPELINE_STAGES];
    const totalTrips = tripsData.length;
    
    // Distribute trips across stages with realistic percentages
    stages[0].count = Math.ceil(totalTrips * 0.25); // 25% leads
    stages[1].count = Math.ceil(totalTrips * 0.20); // 20% consultation
    stages[2].count = Math.ceil(totalTrips * 0.15); // 15% proposal
    stages[3].count = Math.ceil(totalTrips * 0.25); // 25% deposit
    stages[4].count = Math.ceil(totalTrips * 0.15); // 15% booked

    // Simulate values ($2K-$8K per trip average)
    stages.forEach(stage => {
      stage.value = stage.count * (2000 + Math.random() * 6000);
    });

    return stages;
  }, [tripsData]);

  // Calculate totals
  const totalTrips = pipelineData.reduce((sum, stage) => sum + stage.count, 0);
  const totalValue = pipelineData.reduce((sum, stage) => sum + stage.value, 0);
  const avgDealSize = totalTrips > 0 ? totalValue / totalTrips : 0;

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">Loading pipeline data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Sales Pipeline</h1>
            <p className="text-gray-600 mt-1">Track opportunities through your sales process</p>
          </div>
          <Button className="bg-orange-600 hover:bg-orange-700 text-white">
            <Plus className="h-4 w-4 mr-2" />
            New Opportunity
          </Button>
        </div>

        {/* Pipeline Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-gradient-to-r from-orange-700 to-orange-800 rounded-lg p-4 text-white">
            <div className="flex items-center">
              <TrendingUp className="h-5 w-5 mr-3" />
              <div>
                <p className="text-orange-100 text-sm">Total Pipeline</p>
                <p className="text-xl font-semibold">${Math.round(totalValue).toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-4 text-white">
            <div className="flex items-center">
              <Users className="h-5 w-5 mr-3" />
              <div>
                <p className="text-orange-100 text-sm">Active Opportunities</p>
                <p className="text-xl font-semibold">{totalTrips}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-orange-300 to-orange-400 rounded-lg p-4 text-white">
            <div className="flex items-center">
              <DollarSign className="h-5 w-5 mr-3" />
              <div>
                <p className="text-orange-100 text-sm">Avg Deal Size</p>
                <p className="text-xl font-semibold">${Math.round(avgDealSize).toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border-2 border-orange-500 rounded-lg p-4">
            <div className="flex items-center">
              <BarChart3 className="h-5 w-5 mr-3 text-orange-600" />
              <div>
                <p className="text-orange-600 text-sm">Conversion Rate</p>
                <p className="text-xl font-semibold text-orange-700">67%</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline Stages */}
      <div className="flex-1 p-8 overflow-hidden">
        <div className="flex gap-3 h-full">
          {pipelineData.map((stage, index) => {
            const isExpanded = expandedStages.has(stage.id);
            const expandedCount = expandedStages.size;
            const totalStages = pipelineData.length;
            const collapsedWidth = 64; // 16 * 4 = 64px
            const availableWidth = `calc((100% - ${(totalStages - expandedCount) * collapsedWidth}px - ${totalStages * 12}px) / ${expandedCount})`;
            
            return (
              <div 
                key={stage.id} 
                className="transition-all duration-300"
                style={{
                  width: isExpanded 
                    ? expandedCount > 0 ? availableWidth : '200px'
                    : '64px',
                  minWidth: isExpanded ? '200px' : '64px'
                }}
              >
                <div className={`bg-white rounded-lg border-2 h-full ${stage.bgColor} hover:shadow-lg transition-shadow relative overflow-hidden`}>
                  {/* Stage Header */}
                  <div 
                    className={`${stage.headerBg} cursor-pointer ${isExpanded ? 'p-4 border-b border-gray-200' : 'p-2 border-b border-gray-200'}`}
                    onClick={() => toggleStage(stage.id)}
                  >
                    <div className="flex items-center justify-between">
                      {isExpanded ? (
                        <>
                          <div className="flex items-center">
                            <h3 className={`font-semibold ${stage.color}`}>{stage.title}</h3>
                            <span className={`ml-2 text-sm px-2 py-1 rounded-full bg-white ${stage.color}`}>
                              {stage.count}
                            </span>
                          </div>
                          <ChevronRight className={`h-4 w-4 ${stage.color} transform rotate-90`} />
                        </>
                      ) : (
                        <div className="flex flex-col items-center">
                          <ChevronRight className={`h-4 w-4 ${stage.color} mb-2`} />
                          <span className={`text-xs px-2 py-1 rounded-full bg-orange-100 ${stage.color} font-medium`}>
                            {stage.count}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {isExpanded && (
                      <>
                        <p className="text-sm text-gray-600 mb-2 mt-2">{stage.description}</p>
                        <p className="text-sm font-medium text-gray-900">
                          ${Math.round(stage.value).toLocaleString()}
                        </p>
                      </>
                    )}
                  </div>

                  {/* Collapsed Stage Content - Label at bottom */}
                  {!isExpanded && (
                    <div className="flex-1 flex items-end justify-center pb-6">
                      <div className="transform -rotate-90 origin-center">
                        <h3 className={`font-semibold text-sm ${stage.color} whitespace-nowrap`}>
                          {stage.title}
                        </h3>
                      </div>
                    </div>
                  )}

                  {/* Stage Content - Only show when expanded */}
                  {isExpanded && (
                    <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
                      {stage.count === 0 ? (
                        <div className="text-center py-8">
                          <div className="w-12 h-12 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <Users className="h-6 w-6 text-gray-400" />
                          </div>
                          <p className="text-sm text-gray-500">No opportunities</p>
                        </div>
                      ) : (
                        // Generate mock opportunity cards
                        Array.from({ length: Math.min(stage.count, 5) }, (_, i) => (
                          <div 
                            key={i} 
                            className="bg-white p-3 rounded-lg border border-gray-200 hover:border-orange-300 transition-colors cursor-pointer hover:shadow-sm"
                            onClick={() => handleTripClick()}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-medium text-sm text-gray-900">
                                {['Johnson Family', 'Smith Anniversary', 'Davis Wedding', 'Wilson Reunion', 'Brown Honeymoon'][i % 5]}
                              </h4>
                              <span className="text-xs text-gray-500">
                                {Math.floor(Math.random() * 30) + 1}d
                              </span>
                            </div>
                            <p className="text-xs text-gray-600 mb-2">
                              {['European Cruise', 'Paris & London', 'Italy Honeymoon', 'Family Reunion', 'Caribbean'][i % 5]}
                            </p>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-900">
                                ${(2000 + Math.random() * 6000).toFixed(0)}
                              </span>
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                                {Math.floor(Math.random() * 40) + 40}%
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                      
                      {stage.count > 5 && (
                        <div className="text-center py-2">
                          <span className="text-sm text-gray-500">
                            +{stage.count - 5} more opportunities
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CRMCanvas: React.FC<{ data?: any }> = ({ data }) => (
  <div className="flex-1 p-8">
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Client Management</h1>
      {data ? (
        <div className="space-y-6">
          {data.contacts && <ContactList data={data} />}
          {data.contact && <ContactCard data={data} />}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Use the command bar to search for contacts or create new ones</p>
        </div>
      )}
    </div>
  </div>
);

const ItineraryCanvas: React.FC<{ data?: any }> = ({ data }) => (
  <div className="flex-1 p-8">
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Trip Planning</h1>
      {data ? (
        <div className="space-y-6">
          {data.trips && <TripList data={data} />}
          {data.trip && <TripCard data={data} />}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Create beautiful itineraries for your clients</p>
        </div>
      )}
    </div>
  </div>
);

const AnalyticsCanvas: React.FC<{ data?: any }> = ({ data }) => (
  <div className="flex-1 p-8">
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Analytics & Insights</h1>
      {data ? (
        <div className="prose max-w-none">
          <ReactMarkdown>{data.response}</ReactMarkdown>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">$12,450</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Active Trips</p>
                  <p className="text-2xl font-bold text-gray-900">23</p>
                </div>
                <Calendar className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Clients</p>
                  <p className="text-2xl font-bold text-gray-900">156</p>
                </div>
                <Users className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          </div>
          <div className="text-center py-8 text-gray-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Ask me about your performance, commissions, or any business metrics</p>
          </div>
        </div>
      )}
    </div>
  </div>
);

const PipelineCanvas: React.FC<{ data?: any }> = ({ data }) => {
  return <CanvasPipeline />;
};

const ContentCanvas: React.FC<{ data?: any }> = ({ data }) => (
  <div className="flex-1 p-8">
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Digital Presence</h1>
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center py-8 text-gray-500">
          <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Manage content, web and social media tools here</p>
        </div>
      </div>
    </div>
  </div>
);

const ProposalsCanvas: React.FC<{ data?: any }> = ({ data }) => (
  <div className="flex-1 p-8">
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Proposals</h1>
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Create and manage client proposals here</p>
        </div>
      </div>
    </div>
  </div>
);

const BookingCanvas: React.FC<{ data?: any }> = ({ data }) => (
  <div className="flex-1 p-8">
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Booking</h1>
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center py-8 text-gray-500">
          <Plane className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Handle reservations and booking management here</p>
        </div>
      </div>
    </div>
  </div>
);

const PaymentsCanvas: React.FC<{ data?: any }> = ({ data }) => (
  <div className="flex-1 p-8">
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Commissions</h1>
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center py-8 text-gray-500">
          <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Track advisor payouts, receivables, and commission reconciliations here</p>
        </div>
      </div>
    </div>
  </div>
);

// Individual Thread Item component with action menu
const ThreadItem: React.FC<{
  session: any;
  activeSessionId: number | null;
  onSessionSelect: (sessionId: number) => void;
  onMenuOpenChange?: (open: boolean) => void;
  onSessionUpdate?: (sessionId: number, newTitle: string) => void;
  onSessionDelete?: (sessionId: number) => void;
  projects?: any[];
  onMoveToProject?: (sessionId: number, projectId: number | null) => void;
  onHoverClickLock?: (ms: number) => void;
  onCreateProjectFromThread?: (session: any) => void;
  onHoverRegionEnter?: () => void;
  onHoverRegionLeave?: (e?: React.MouseEvent) => void;
}> = ({ session, activeSessionId, onSessionSelect, onMenuOpenChange, onSessionUpdate, onSessionDelete, projects = [], onMoveToProject, onHoverClickLock, onCreateProjectFromThread, onHoverRegionEnter, onHoverRegionLeave }) => {
  const [showActionMenu, setShowActionMenu] = useState(false);
  const actionMenuOpenedByPointerRef = useRef(true);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState(session.title);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showMoveToProjectDialog, setShowMoveToProjectDialog] = useState(false);

  const handleRename = () => {
    setIsRenaming(true);
    setNewTitle(session.title); // Reset to current title
    setShowActionMenu(false);
  };

  const handleSaveRename = async () => {
    const trimmedTitle = newTitle.trim();
    
    if (trimmedTitle && trimmedTitle !== session.title) {
      try {
        onSessionUpdate?.(session.id, trimmedTitle);
      } catch (error) {
        console.error('Failed to rename session:', error);
        // Reset to original title on error
        setNewTitle(session.title);
      }
    }
    setIsRenaming(false);
  };

  const handleCancelRename = () => {
    setNewTitle(session.title);
    setIsRenaming(false);
  };

  const handleMoveToProject = () => {
    setShowMoveToProjectDialog(true);
    setShowActionMenu(false);
  };

  const handleCreateProjectFromThread = () => {
    setShowActionMenu(false);
    onCreateProjectFromThread?.(session);
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
    setShowActionMenu(false);
  };

  const handleConfirmDelete = () => {
    onSessionDelete?.(session.id);
    setShowDeleteDialog(false);
  };

  const handleMoveToProjectConfirm = (projectId: number | null) => {
    onMoveToProject?.(session.id, projectId);
    setShowMoveToProjectDialog(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleSaveRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      handleCancelRename();
    }
  };

  return (
    <>
      <div
        data-thread-id={session.id}
        className={`group relative px-3 py-2 rounded-lg transition-colors ${
          activeSessionId === session.id
            ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300'
            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
        }`}
      >
        <div className="flex items-start justify-between">
          {/* AR-210 (TC-35.1): the row was a click-only div, so no thread could be
              opened from the keyboard. Focusable, Enter/Space to open, current marked. */}
          <div 
            className={`flex-1 min-w-0 pl-2.5 ${!isRenaming ? 'cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 dark:focus-visible:ring-[#fb923c]' : ''}`}
            onClick={!isRenaming ? () => onSessionSelect(session.id) : (e) => e.stopPropagation()}
            role={!isRenaming ? 'button' : undefined}
            tabIndex={!isRenaming ? 0 : undefined}
            aria-current={!isRenaming && activeSessionId === session.id ? 'page' : undefined}
            onKeyDown={
              !isRenaming
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSessionSelect(session.id);
                    }
                  }
                : undefined
            }
          >
            {isRenaming ? (
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleSaveRename}
                onClick={(e) => e.stopPropagation()}
                className="w-full text-sm font-normal h-7 px-2 -mx-2"
                autoFocus
              />
            ) : (
              <h4 className={`text-sm truncate ${
                activeSessionId === session.id ? 'font-medium' : 'font-normal'
              }`}>
                {session.title}
              </h4>
            )}

          </div>

          {!isRenaming && (
            <div className="flex items-center gap-2 ml-2">
              {activeSessionId === session.id && (
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              )}
              
              <DropdownMenu
                open={showActionMenu}
                onOpenChange={(open) => {
                  setShowActionMenu(open);
                  onMenuOpenChange?.(open);
                  if (open) {
                    // Apply a long hover lock when menu opens to keep sidebar open
                    onHoverClickLock?.(5000);
                    onHoverRegionEnter?.();
                  } else {
                    // When menu closes, reset hover lock to allow normal hover behavior
                    // Don't call onHoverRegionLeave without an event - let natural mouse events handle closing
                    onHoverClickLock?.(0);
                  }
                }}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1 h-6 w-6 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]:opacity-100 data-[state=open]:opacity-100 transition-opacity"
                    aria-label={`Actions for ${session.title}`}
                    onMouseDown={() => {
                      actionMenuOpenedByPointerRef.current = true;
                      onHoverClickLock?.(800);
                    }}
                    onKeyDown={() => {
                      actionMenuOpenedByPointerRef.current = false;
                    }}
                    data-menu-trigger
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent triggering session selection
                    }}
                  >
                    <MoreVertical className="w-3 h-3" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-40"
                  data-threads-hover-region="true"
                  onMouseEnter={() => {
                    onHoverRegionEnter?.();
                  }}
                  onMouseLeave={(event) => {
                    // Notify when mouse leaves dropdown area (for panel close logic)
                    // The hover lock mechanism protects against premature closing while interacting
                    onHoverRegionLeave?.(event);
                  }}
                  onCloseAutoFocus={(event) => {
                    // Prevent focus from jumping around when a pointer-opened menu
                    // closes. AR-210: a keyboard-opened menu returns focus to its trigger.
                    if (actionMenuOpenedByPointerRef.current) event.preventDefault();
                  }}
                >
                  <DropdownMenuItem onClick={handleRename}>
                    <Edit className="w-3 h-3 mr-2" />
                    Rename
                  </DropdownMenuItem>
                  {onCreateProjectFromThread && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreateProjectFromThread();
                      }}
                    >
                      <FolderPlus className="w-3 h-3 mr-2" />
                      Create Project from Thread
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleMoveToProject}>
                    <FolderOpen className="w-3 h-3 mr-2" />
                    Move to Project
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={handleDeleteClick}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="w-3 h-3 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Thread</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{session.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move to Project Dialog */}
      <AlertDialog open={showMoveToProjectDialog} onOpenChange={setShowMoveToProjectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move to Project</AlertDialogTitle>
            <AlertDialogDescription>
              Choose a project for "{session.title}" or remove it from any project.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {/* No Project Option */}
              <button
                onClick={() => handleMoveToProjectConfirm(null)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  !session.chatProjectId 
                    ? 'border-orange-500 bg-orange-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-gray-500" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">No Project</div>
                    <div className="text-xs text-gray-500">Remove from any project</div>
                  </div>
                </div>
              </button>

              {/* Project Options */}
              {projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => handleMoveToProjectConfirm(project.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    session.chatProjectId === project.id 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: project.color + '20', color: project.color }}
                    >
                      <Folder className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-medium text-sm">{project.name}</div>
                      {project.description && (
                        <div className="text-xs text-gray-500 truncate">{project.description}</div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// Helper function to group sessions by date - shared between components
const groupSessionsByDate = (sessions: any[]) => {
  // Always return the proper structure, even if empty
  const groups = {
    today: [] as any[],
    yesterday: [] as any[],
    last7Days: [] as any[],
    last30Days: [] as any[],
    older: [] as any[]
  };

  if (!sessions || sessions.length === 0) {
    return groups;
  }

  // Sort sessions by updatedAt or createdAt, newest first
  const sortedSessions = [...sessions].sort((a, b) => {
    const aDate = new Date(a.updatedAt || a.createdAt);
    const bDate = new Date(b.updatedAt || b.createdAt);
    return bDate.getTime() - aDate.getTime();
  });

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const last7Days = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const last30Days = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

  sortedSessions.forEach(session => {
    const sessionDate = new Date(session.updatedAt || session.createdAt);
    const sessionDateOnly = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());

    if (sessionDateOnly.getTime() === today.getTime()) {
      groups.today.push(session);
    } else if (sessionDateOnly.getTime() === yesterday.getTime()) {
      groups.yesterday.push(session);
    } else if (sessionDate >= last7Days && sessionDate < yesterday) {
      groups.last7Days.push(session);
    } else if (sessionDate >= last30Days && sessionDate < last7Days) {
      groups.last30Days.push(session);
    } else {
      groups.older.push(session);
    }
  });

  return groups;
};

// Simple New Project Form component
const NewProjectForm: React.FC<{
  onSuccess: (project: any) => void;
  onCancel: () => void;
  initialName?: string;
  initialDescription?: string;
  initialType?: string;
  initialColor?: string;
  submitLabel?: string;
  onAfterCreate?: (project: any) => Promise<void> | void;
}> = ({ onSuccess, onCancel, initialName = '', initialDescription = '', initialType = 'GENERAL', initialColor = '#3B82F6', submitLabel, onAfterCreate }) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [projectType, setProjectType] = useState(initialType);
  const [color, setColor] = useState(initialColor);
  const [isLoading, setIsLoading] = useState(false);
  // AR-210 (TC-35.4): label/field wiring and swatch names.
  const fieldId = React.useId();
  const colorNames: Record<string, string> = {
    '#3B82F6': 'Blue', '#10B981': 'Green', '#8B5CF6': 'Purple', '#F59E0B': 'Amber',
    '#EF4444': 'Red', '#EC4899': 'Pink', '#06B6D4': 'Cyan', '#84CC16': 'Lime',
  };

  const colors = [
    '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', 
    '#EF4444', '#EC4899', '#06B6D4', '#84CC16'
  ];

  const projectTypes = [
    { value: 'CLIENT', label: 'Client' },
    { value: 'DESTINATION', label: 'Destination' },
    { value: 'SUPPLIER', label: 'Supplier' },
    { value: 'TRAINING', label: 'Training' },
    { value: 'PERSONAL', label: 'Personal' },
    { value: 'GENERAL', label: 'General' }
  ];

  useEffect(() => {
    setName(initialName);
  }, [initialName]);

  useEffect(() => {
    setDescription(initialDescription);
  }, [initialDescription]);

  useEffect(() => {
    setProjectType(initialType);
  }, [initialType]);

  useEffect(() => {
    setColor(initialColor);
  }, [initialColor]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsLoading(true);
    try {
      const newProject = await apiRequest('POST', '/api/chat-projects', {
        name: name.trim(),
        description: description.trim() || null,
        projectType,
        color
      });
      if (onAfterCreate) {
        await onAfterCreate(newProject);
      }
      onSuccess(newProject);
    } catch (error) {
      console.error('Failed to create project:', error);
      if (!(error as any)?.__handled) {
        alert('Failed to create project. Please try again.');
      }
      return;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor={`${fieldId}-name`} className="block text-sm font-medium text-gray-700 mb-1">
          Project Name *
        </label>
        <Input
          id={`${fieldId}-name`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter project name"
          required
          aria-required="true"
          autoFocus
        />
      </div>

      <div>
        <label htmlFor={`${fieldId}-description`} className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <Input
          id={`${fieldId}-description`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description"
        />
      </div>

      <div>
        <label htmlFor={`${fieldId}-type`} className="block text-sm font-medium text-gray-700 mb-1">
          Type
        </label>
        <Select value={projectType} onValueChange={setProjectType}>
          <SelectTrigger id={`${fieldId}-type`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {projectTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label id={`${fieldId}-color`} className="block text-sm font-medium text-gray-700 mb-2">
          Color
        </label>
        <div className="flex gap-2" role="group" aria-labelledby={`${fieldId}-color`}>
          {colors.map((colorOption) => (
            <button
              key={colorOption}
              type="button"
              aria-label={colorNames[colorOption] ?? colorOption}
              aria-pressed={color === colorOption}
              onClick={() => setColor(colorOption)}
              className={`w-6 h-6 rounded-full border-2 ${
                color === colorOption ? 'border-gray-400' : 'border-gray-200'
              }`}
              style={{ backgroundColor: colorOption }}
            />
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-4">
        <Button type="submit" disabled={!name.trim() || isLoading}>
          {isLoading ? 'Creating...' : (submitLabel || 'Create Project')}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Threads Panel component for showing chat sessions and projects
const ThreadsPanel: React.FC<{
  sessions: any[];
  activeSessionId: number | null;
  activeProjectId: number | null;
  onSessionSelect: (sessionId: number) => void;
  onProjectSelect: (projectId: number | null, project?: any) => void;
  onNewThread: () => void;
  onOpenSearch: () => void;
  onClose?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: (e?: React.MouseEvent) => void;
  onNavigateToDashboard?: () => void;
  onRenameStart?: () => void;
  onRenameEnd?: () => void;
  onHoverClickLock?: (ms: number) => void;
  /** AR-316: lets the mobile sheet drop the fixed width and the divider. */
  className?: string;
}> = ({ sessions, activeSessionId, activeProjectId, onSessionSelect, onProjectSelect, onNewThread, onOpenSearch, onClose, onMouseEnter, onMouseLeave, onNavigateToDashboard, onRenameStart, onRenameEnd, onHoverClickLock, className }) => {
  const { user } = useAuth();
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [projectsExpanded, setProjectsExpanded] = useState(true);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [renamingProjectId, setRenamingProjectId] = useState<number | null>(null);
  const [editingProject, setEditingProject] = useState<any | null>(null);
  const [expandedProjects, setExpandedProjects] = useState<Record<number, boolean>>({});
  const [showProjectDeleteDialog, setShowProjectDeleteDialog] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<any | null>(null);
  const [threadForNewProject, setThreadForNewProject] = useState<any | null>(null);
  const lastThreadForNewProjectIdRef = useRef<unknown>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const handleDropdownOpenChange = (open: boolean) => {
    if (open) {
      onMouseEnter?.();
    }
  };

  const handleHoverRegionEnter = () => {
    onMouseEnter?.();
  };

  const handleHoverRegionLeave = (event?: React.MouseEvent) => {
    onMouseLeave?.(event);
  };
  
  // Fetch real projects data
  const { data: projects = [], isLoading: isLoadingProjects } = useQuery<any[]>({
    queryKey: ['/api/chat-projects', user?.id],
    queryFn: async () => {
      return await apiRequest('GET', `/api/chat-projects?userId=${user?.id}`);
    },
    enabled: !!user?.id,
  });



  // Sessions already have real chatProjectId from the database
  const sessionsWithProjects = sessions || [];

  // Always show only INDIVIDUAL (non-project) threads in this panel's Threads tab,
  // regardless of current project scope. Project threads are listed only inside a project view.
  const filteredSessions = sessionsWithProjects.filter(session => !session.chatProjectId);
  
  const groupedSessions = groupSessionsByDate(filteredSessions);

  // Rename session mutation
  const renameSessionMutation = useMutation({
    mutationFn: ({ sessionId, newTitle }: { sessionId: number, newTitle: string }) => {
      return apiRequest('PATCH', `/api/smart-chat/sessions/${sessionId}`, { title: newTitle });
    },
    onSuccess: (data, variables) => {
      // Refresh sessions list only (not session details, to avoid overwriting cache)
      queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions', user?.id], exact: true });
      // Refresh active session if it's the one being renamed
      if (activeSessionId) {
        queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions', activeSessionId] });
      }
    },
    onError: (error) => {
      console.error('Failed to rename session:', error);
      alert('Failed to rename thread. Please try again.');
    }
  });

  // Delete session mutation
  const deleteSessionMutation = useMutation({
    mutationFn: (sessionId: number) => {
      return apiRequest('DELETE', `/api/smart-chat/sessions/${sessionId}`);
    },
    onSuccess: (_, sessionId) => {
      // Refresh sessions list only (not session details, to avoid overwriting cache)
      queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions', user?.id], exact: true });
      // If we deleted the active session, navigate to dashboard
      if (activeSessionId === sessionId) {
        onNavigateToDashboard?.();
      }
    },
    onError: (error) => {
      console.error('Failed to delete session:', error);
      alert('Failed to delete thread. Please try again.');
    }
  });

  // Move to project mutation
  const moveToProjectMutation = useMutation({
    mutationFn: ({ sessionId, projectId }: { sessionId: number, projectId: number | null }) => {
      return apiRequest('PATCH', `/api/smart-chat/sessions/${sessionId}`, { chatProjectId: projectId });
    },
    onSuccess: () => {
      // Refresh sessions list only (not session details, to avoid overwriting cache)
      queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions', user?.id], exact: true });
    },
    onError: (error) => {
      console.error('Failed to move session to project:', error);
      alert('Failed to move thread to project. Please try again.');
    }
  });

  const handleSessionUpdate = (sessionId: number, newTitle: string) => {
    renameSessionMutation.mutate({ sessionId, newTitle });
  };

  const handleSessionDelete = (sessionId: number) => {
    deleteSessionMutation.mutate(sessionId);
  };

  const handleMoveToProject = (sessionId: number, projectId: number | null) => {
    moveToProjectMutation.mutate({ sessionId, projectId });
  };

  const handleCreateProjectFromThreadRequest = (thread: any) => {
    // AR-210 (TC-35.4): remembered for returning focus to this thread's actions button.
    lastThreadForNewProjectIdRef.current = thread?.id ?? null;
    setThreadForNewProject(thread);
    setShowNewProjectDialog(false);
  };
  
  const handleMouseLeave = (e: React.MouseEvent) => {
    const target = e.relatedTarget as Element | null;
    const stillWithin = !!(target && typeof target.closest === 'function' && (target.closest('[data-threads-panel="true"]') || target.closest('[data-threads-hover-region="true"]')));
    if (stillWithin) return;
    onMouseLeave?.(e);
  };

  if (panelCollapsed) {
    return (
      <div
        className="w-8 flex-shrink-0 flex flex-col items-center border-r border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 pt-4"
        data-threads-panel="true"
      >
        <button
          onClick={() => setPanelCollapsed(false)}
          className="rounded p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          title="Expand Threads sidebar"
          aria-label="Expand Threads sidebar"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`w-56 flex-shrink-0 bg-gray-50/50 dark:bg-gray-900/50 border-r border-gray-200 dark:border-gray-700 flex flex-col min-h-0 ${className ?? ''}`}
      data-threads-panel="true"
    >
      {/* Header */}
      <div className="px-3 pt-4 pb-2 space-y-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {/* AR-316 review: on a phone this panel lives in the tab bar's
                More sheet, where there is no sidebar to collapse — the control
                would render enabled and do nothing. */}
            <button
              onClick={() => setPanelCollapsed(true)}
              className="hidden rounded p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors md:block"
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 tracking-tight">Threads</h3>
          </div>
          <button
            onClick={onOpenSearch}
            className="rounded p-1 text-gray-400 transition-colors hover:text-gray-600 max-md:inline-flex max-md:min-h-11 max-md:min-w-11 max-md:items-center max-md:justify-center dark:text-gray-500 dark:hover:text-gray-300"
            title="Search threads"
            aria-label="Search threads and projects"
          >
            <Search className="h-4 w-4" />
          </button>
        </div>

        <button
          onClick={onNewThread}
          className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors max-md:min-h-11 ${
            !activeSessionId && !activeProjectId
              ? 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-750 hover:border-gray-300 dark:hover:border-gray-600'
          }`}
        >
          <Plus className="h-4 w-4" />
          <span>New Thread</span>
        </button>
        {/* Pin UI commented by request */}
      </div>

      {/* Removed orange context header to simplify flow. Selecting/creating a project opens Project view. */}

      {/* Tabs and content - scrollable area fills remaining space */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <ScrollArea className="h-full w-full">
          <div className="p-2">
            <Tabs defaultValue="threads">
              <div className="px-1 pb-2 flex items-center justify-between relative z-30">
                <TabsList className="bg-transparent rounded-none h-auto p-0 gap-0.5">
                  <TabsTrigger value="threads" className="max-md:min-h-11 px-3 py-1.5 rounded-lg data-[state=active]:bg-orange-100 data-[state=active]:text-orange-700 data-[state=active]:font-medium data-[state=active]:shadow-none dark:data-[state=active]:bg-orange-900/50 dark:data-[state=active]:text-orange-300 dark:data-[state=active]:border-none text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">Threads</TabsTrigger>
                  <TabsTrigger value="projects" className="max-md:min-h-11 px-3 py-1.5 rounded-lg data-[state=active]:bg-orange-100 data-[state=active]:text-orange-700 data-[state=active]:font-medium data-[state=active]:shadow-none dark:data-[state=active]:bg-orange-900/50 dark:data-[state=active]:text-orange-300 dark:data-[state=active]:border-none text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">Projects</TabsTrigger>
                </TabsList>
                <TooltipProvider>
                  <Tooltip delayDuration={150}>
                    <TooltipTrigger asChild>
                      <button aria-label="Threads vs Projects" className="ml-2 rounded p-1 hover:bg-gray-50 max-md:inline-flex max-md:min-h-11 max-md:min-w-11 max-md:items-center max-md:justify-center dark:hover:bg-gray-800" onMouseDown={() => onHoverClickLock?.(400)}>
                        <HelpCircle className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" align="center" className="w-80 z-50 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 shadow-lg">
                      Threads are individual conversations. Projects group related threads and shared context/files. Use Projects to organize work by client or deliverable; use Threads for specific questions or tasks.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <TabsContent value="threads" className="m-0">
              
              {filteredSessions?.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No threads yet</p>
                  <p className="text-sm">Start a conversation to create your first thread</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {/* Today's threads */}
                  {groupedSessions.today.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 px-2 mb-1">Today</div>
                      <div className="space-y-0.5">
                        {groupedSessions.today.map((session) => (
                          <ThreadItem
                            key={session.id}
                            session={session}
                            activeSessionId={activeSessionId}
                            onSessionSelect={onSessionSelect}
                            onMenuOpenChange={handleDropdownOpenChange}
                            onSessionUpdate={handleSessionUpdate}
                            onSessionDelete={handleSessionDelete}
                            projects={projects}
                            onMoveToProject={handleMoveToProject}
                            onHoverClickLock={(ms) => onHoverClickLock?.(ms)}
                            onCreateProjectFromThread={handleCreateProjectFromThreadRequest}
                            onHoverRegionEnter={handleHoverRegionEnter}
                            onHoverRegionLeave={handleHoverRegionLeave}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Yesterday's threads */}
                  {groupedSessions.yesterday.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 px-2 mb-1">Yesterday</div>
                      <div className="space-y-0.5">
                        {groupedSessions.yesterday.map((session) => (
                          <ThreadItem
                            key={session.id}
                            session={session}
                            activeSessionId={activeSessionId}
                            onSessionSelect={onSessionSelect}
                            onMenuOpenChange={handleDropdownOpenChange}
                            onSessionUpdate={handleSessionUpdate}
                            onSessionDelete={handleSessionDelete}
                            projects={projects}
                            onMoveToProject={handleMoveToProject}
                            onHoverClickLock={(ms) => onHoverClickLock?.(ms)}
                            onCreateProjectFromThread={handleCreateProjectFromThreadRequest}
                            onHoverRegionEnter={handleHoverRegionEnter}
                            onHoverRegionLeave={handleHoverRegionLeave}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Last 7 days */}
                  {groupedSessions.last7Days.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 px-2 mb-1">Last 7 Days</div>
                      <div className="space-y-0.5">
                        {groupedSessions.last7Days.map((session) => (
                          <ThreadItem
                            key={session.id}
                            session={session}
                            activeSessionId={activeSessionId}
                            onSessionSelect={onSessionSelect}
                            onMenuOpenChange={handleDropdownOpenChange}
                            onSessionUpdate={handleSessionUpdate}
                            onSessionDelete={handleSessionDelete}
                            projects={projects}
                            onMoveToProject={handleMoveToProject}
                            onHoverClickLock={(ms) => onHoverClickLock?.(ms)}
                            onCreateProjectFromThread={handleCreateProjectFromThreadRequest}
                            onHoverRegionEnter={handleHoverRegionEnter}
                            onHoverRegionLeave={handleHoverRegionLeave}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Last 30 days */}
                  {groupedSessions.last30Days.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 px-2 mb-1">Last 30 Days</div>
                      <div className="space-y-0.5">
                        {groupedSessions.last30Days.map((session) => (
                          <ThreadItem
                            key={session.id}
                            session={session}
                            activeSessionId={activeSessionId}
                            onSessionSelect={onSessionSelect}
                            onMenuOpenChange={handleDropdownOpenChange}
                            onSessionUpdate={handleSessionUpdate}
                            onSessionDelete={handleSessionDelete}
                            projects={projects}
                            onMoveToProject={handleMoveToProject}
                            onHoverClickLock={(ms) => onHoverClickLock?.(ms)}
                            onCreateProjectFromThread={handleCreateProjectFromThreadRequest}
                            onHoverRegionEnter={handleHoverRegionEnter}
                            onHoverRegionLeave={handleHoverRegionLeave}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Older threads */}
                  {groupedSessions.older.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 px-2 mb-1">Older</div>
                      <div className="space-y-0.5">
                        {groupedSessions.older.map((session) => (
                          <ThreadItem
                            key={session.id}
                            session={session}
                            activeSessionId={activeSessionId}
                            onSessionSelect={onSessionSelect}
                            onMenuOpenChange={handleDropdownOpenChange}
                            onSessionUpdate={handleSessionUpdate}
                            onSessionDelete={handleSessionDelete}
                            projects={projects}
                            onMoveToProject={handleMoveToProject}
                            onCreateProjectFromThread={handleCreateProjectFromThreadRequest}
                            onHoverRegionEnter={handleHoverRegionEnter}
                            onHoverRegionLeave={handleHoverRegionLeave}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              </TabsContent>

              <TabsContent value="projects" className="m-0">
                {/* New Project Button */}
                <div className="px-2 mt-1">
                  <button
                    onClick={() => setShowNewProjectDialog(true)}
                    className="w-full flex items-center gap-2 p-2 text-left text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <Plus className="w-3 h-3" />
                    </div>
                    <span>New Project</span>
                  </button>
                </div>
                <div className="mt-2 space-y-1">
                  {projects.map((project) => (
                    <div
                      key={project.id}
                      className={`w-full text-left p-2 rounded-lg transition-colors ${
                        activeProjectId === project.id
                          ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <button
                              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                              aria-label="Toggle project threads"
                              onMouseDown={() => onHoverClickLock?.(300)}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedProjects(prev => ({ ...prev, [project.id]: !prev[project.id] }));
                              }}
                            >
                              {expandedProjects[project.id] ? (
                                <FolderOpen className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                              ) : (
                                <Folder className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              )}
                            </button>

                            {/* Inline rename input (like ThreadItem) or title button */}
                            {renamingProjectId === project.id ? (
                              <Input
                                defaultValue={project.name}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const val = (e.target as HTMLInputElement).value.trim();
                                    const newName = val || project.name;
                                    if (newName !== project.name) {
                                      apiRequest('PATCH', `/api/chat-projects/${project.id}`, { name: newName })
                                        .then(() => queryClient.invalidateQueries({ queryKey: ['/api/chat-projects'] }))
                                        .finally(() => { setRenamingProjectId(null); onRenameEnd?.(); });
                                    } else {
                                      setRenamingProjectId(null);
                                      onRenameEnd?.();
                                    }
                                  } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setRenamingProjectId(null);
                                    onRenameEnd?.();
                                  }
                                }}
                                onBlur={(e) => {
                                  const val = (e.target as HTMLInputElement).value.trim();
                                  const newName = val || project.name;
                                  if (newName !== project.name) {
                                    apiRequest('PATCH', `/api/chat-projects/${project.id}`, { name: newName })
                                      .then(() => queryClient.invalidateQueries({ queryKey: ['/api/chat-projects'] }))
                                      .finally(() => { setRenamingProjectId(null); onRenameEnd?.(); });
                                  } else {
                                    setRenamingProjectId(null);
                                    onRenameEnd?.();
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-full text-sm font-medium h-7 px-2 -mx-2"
                                autoFocus
                              />
                            ) : (
                              <button onClick={() => onProjectSelect(project.id, project)} className="flex-1 min-w-0 text-left">
                                <h4 className="text-sm font-medium truncate">
                                  {project.name}
                                </h4>
                              </button>
                            )}
                          </div>

                          {expandedProjects[project.id] && (
                            <div className="mt-2 ml-6 space-y-0.5">
                              {sessionsWithProjects.filter(s => s.chatProjectId === project.id).map((thread) => (
                                <div
                                  key={thread.id}
                                  className="w-full px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-between"
                                >
                                  <button
                                    className="flex-1 text-left text-sm text-gray-700 dark:text-gray-300 truncate"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onSessionSelect(thread.id);
                                    }}
                                  >
                                    {thread.title}
                                  </button>
                                  <DropdownMenu onOpenChange={(open) => {
                                    handleDropdownOpenChange(open);
                                    if (open) {
                                      onHoverClickLock?.(5000);
                                    } else {
                                      // When menu closes, reset hover lock to allow normal hover behavior
                                      // Don't call handleHoverRegionLeave without an event - let natural mouse events handle closing
                                      onHoverClickLock?.(0);
                                    }
                                  }}>
                                    <DropdownMenuTrigger asChild>
                                      <button
                                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                                        aria-label="Thread menu"
                                        data-menu-trigger
                                        onMouseDown={() => onHoverClickLock?.(800)}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <MoreVertical className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                      </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                      align="end"
                                      className="w-44"
                                      data-threads-hover-region="true"
                                      onMouseEnter={handleHoverRegionEnter}
                                      onMouseLeave={(event) => handleHoverRegionLeave(event)}
                                    >
                                      <DropdownMenuItem onClick={(e) => {
                                        e.stopPropagation();
                                        const newTitle = prompt('Rename thread', thread.title);
                                        if (newTitle && newTitle.trim() && newTitle.trim() !== thread.title) {
                                          handleSessionUpdate(thread.id, newTitle.trim());
                                        }
                                      }}>Rename</DropdownMenuItem>
                                      <DropdownMenuItem onClick={(e) => {
                                        e.stopPropagation();
                                        handleCreateProjectFromThreadRequest(thread);
                                      }}>
                                        <FolderPlus className="w-3 h-3 mr-2" />
                                        Create Project from Thread
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={(e) => {
                                        e.stopPropagation();
                                        // Remove from project
                                        handleMoveToProject(thread.id, null);
                                      }}>Remove from project</DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem className="text-red-600 dark:text-red-400" onClick={(e) => {
                                        e.stopPropagation();
                                        handleSessionDelete(thread.id);
                                      }}>Delete</DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* 3-dot project menu */}
                        <DropdownMenu onOpenChange={(open) => {
                          if (open) {
                            onHoverClickLock?.(5000);
                          } else {
                            // When menu closes, reset hover lock to allow normal hover behavior
                            // Don't call handleHoverRegionLeave without an event - let natural mouse events handle closing
                            onHoverClickLock?.(0);
                          }
                        }}>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400" aria-label="Project menu" data-menu-trigger onMouseDown={() => onHoverClickLock?.(800)}>
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="min-w-[180px]"
                            data-threads-hover-region="true"
                            onMouseEnter={handleHoverRegionEnter}
                            onMouseLeave={(event) => handleHoverRegionLeave(event)}
                          >
                            <DropdownMenuItem onClick={() => {
                              setRenamingProjectId(project.id);
                              onRenameStart?.();
                            }}>Rename</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setEditingProject(project);
                              onRenameStart?.();
                            }}>Edit details</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-red-600 dark:text-red-400" onClick={() => {
                              setProjectToDelete(project);
                              setShowProjectDeleteDialog(true);
                            }}>Delete project</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </ScrollArea>
      </div>

      {/* Create Project From Thread Dialog */}
      <AlertDialog open={!!threadForNewProject} onOpenChange={(open) => {
        if (!open) {
          setThreadForNewProject(null);
        }
      }}>
        <AlertDialogContent
          onCloseAutoFocus={(event) => {
            // AR-210 (TC-35.4): opened from a thread's actions menu, which is gone by
            // now; return focus to that thread's actions button.
            const trigger = document.querySelector<HTMLElement>(
              `[data-thread-id="${lastThreadForNewProjectIdRef.current}"] [data-menu-trigger]`,
            );
            if (trigger) {
              event.preventDefault();
              trigger.focus();
            }
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              {threadForNewProject
                ? `Create Project from "${threadForNewProject.title || 'Untitled Thread'}"`
                : 'Create Project from Thread'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {threadForNewProject?.chatProjectId
                ? 'A new project will be created and this thread will be moved into it.'
                : 'Create a new project using this thread as the first member.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {threadForNewProject && (
            <NewProjectForm
              key={`thread-project-${threadForNewProject.id}`}
              initialName={threadForNewProject.title || ''}
              onSuccess={(project) => {
                setThreadForNewProject(null);
                setExpandedProjects((prev) => ({ ...prev, [project.id]: true }));
                toast({
                  title: 'Project created',
                  description: `"${threadForNewProject.title || 'Thread'}" is now part of ${project.name}.`
                });
                onProjectSelect(project.id, project);
              }}
              onCancel={() => setThreadForNewProject(null)}
              onAfterCreate={async (project) => {
                try {
                  await apiRequest('PATCH', `/api/smart-chat/sessions/${threadForNewProject.id}`, {
                    chatProjectId: project.id,
                  });
                  await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ['/api/chat-projects'] }),
                    queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions', user?.id], exact: true }),
                  ]);
                } catch (error) {
                  console.error('Failed to attach thread to new project:', error);
                  toast({
                    title: 'Unable to link thread',
                    description: 'We created the project but could not move the thread. Please try again.',
                    variant: 'destructive'
                  });
                  const err = error instanceof Error ? error : new Error('Failed to attach thread to new project');
                  (err as any).__handled = true;
                  throw err;
                }
              }}
            />
          )}
        </AlertDialogContent>
      </AlertDialog>

      {/* New Project Dialog */}
      <AlertDialog open={showNewProjectDialog} onOpenChange={setShowNewProjectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create New Project</AlertDialogTitle>
            <AlertDialogDescription>
              Create a project to organize related conversations with shared context and custom instructions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <NewProjectForm 
            onSuccess={async (project) => {
              // Invalidate projects query to refresh the list immediately
              await queryClient.invalidateQueries({ queryKey: ['/api/chat-projects'] });
              setShowNewProjectDialog(false);
              onProjectSelect(project.id, project);
            }}
            onCancel={() => setShowNewProjectDialog(false)}
          />
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Project Dialog */}
      <AlertDialog open={!!editingProject} onOpenChange={(open) => { if (!open) { setEditingProject(null); onRenameEnd?.(); } }}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Edit Project Details</AlertDialogTitle>
            <AlertDialogDescription>
              Update the project name, description, instructions, and files.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {editingProject && (
            <EditProjectForm 
              project={editingProject} 
              onClose={() => { setEditingProject(null); onRenameEnd?.(); queryClient.invalidateQueries({ queryKey: ['/api/chat-projects'] }); }} 
            />
          )}
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Project Confirmation Dialog */}
      <AlertDialog open={showProjectDeleteDialog} onOpenChange={setShowProjectDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the project and all of its threads. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setProjectToDelete(null); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              onClick={() => {
                if (!projectToDelete) return;
                apiRequest('DELETE', `/api/chat-projects/${projectToDelete.id}`)
                  .then(() => {
                    setShowProjectDeleteDialog(false);
                    setProjectToDelete(null);
                    queryClient.invalidateQueries({ queryKey: ['/api/chat-projects'] });
                  })
                  .catch(err => console.error('Delete project failed', err));
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const FloatingVersion: React.FC<{ message: any; onRestore: (versionIndex: number) => void }> = ({ message, onRestore }) => {
  if (!message) return null;
  const versions = Array.isArray(message?.metadata?.versions) ? message.metadata.versions : [];
  const currentIndex = typeof message?.metadata?.currentVersionIndex === 'number' ? message.metadata.currentVersionIndex : versions.length;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="px-1.5 py-0.5 rounded-full bg-orange-600 text-white text-xs flex items-center gap-1 border border-orange-500 shadow">
          v{currentIndex + 1}
          <ChevronDown className="w-3 h-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        {[...Array(versions.length + 1)].map((_, idx) => (
          <DropdownMenuItem key={idx} onClick={() => onRestore(idx)}>
            {idx === 0 ? 'v1 (original)' : `v${idx + 1}`}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ProjectCanvas component - similar to ChatGPT's project interface
const ProjectCanvas: React.FC<{
  project: any;
  sessions: any[];
  activeSessionId: number | null;
  onSessionSelect: (sessionId: number) => void;
  onNewThread: () => void;
  onBackToThreads: () => void;
}> = ({ project, sessions, activeSessionId, onSessionSelect, onNewThread, onBackToThreads }) => {
  const [systemInstructions, setSystemInstructions] = useState(project?.systemInstructions || '');
  const [files, setFiles] = useState(project?.files || []);
  const [showFilesDialog, setShowFilesDialog] = useState(false);
  const [showInstructionsDialog, setShowInstructionsDialog] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const queryClient = useQueryClient();

  // Filter sessions for this project
  const projectSessions = sessions.filter(session => session.chatProjectId === project?.id);

  // Group sessions by date
  const groupedSessions = groupSessionsByDate(projectSessions);

  // Update project mutation
  const updateProjectMutation = useMutation({
    mutationFn: async (updates: any) => {
      return await apiRequest('PATCH', `/api/chat-projects/${project.id}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat-projects'] });
    }
  });

  const handleSaveInstructions = () => {
    updateProjectMutation.mutate({ systemInstructions });
    setShowInstructionsDialog(false);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = Array.from(event.target.files || []);
    
    if (uploadedFiles.length > 0) {
      try {
        // Create FormData for file upload
        const formData = new FormData();
        uploadedFiles.forEach(file => {
          formData.append('files', file);
        });

        // Upload files to the server
        const response = await fetch(`/api/chat-projects/${project.projectId}/upload`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Failed to upload files');
        }

        const result = await response.json();
        
        // Update the local files state with the server response
        setFiles(result.project.files || []);
        
      } catch (error) {
        console.error('Error uploading files:', error);
        // Could add a toast notification here
      }
    }
    
    // Reset the input
    event.target.value = '';
  };

  const handleRemoveFile = async (fileId: string) => {
    try {
      // Remove file from local state immediately for better UX
      const updatedFiles = files.filter((file: any) => file.id !== fileId);
      setFiles(updatedFiles);
      
      // Update the project on the server
      const response = await fetch(`/api/chat-projects/${project.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ files: updatedFiles }),
      });

      if (!response.ok) {
        throw new Error('Failed to update project');
      }

    } catch (error) {
      console.error('Error removing file:', error);
      // Could revert the local state change here if the server update failed
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      try {
        // Create FormData for file upload
        const formData = new FormData();
        droppedFiles.forEach(file => {
          formData.append('files', file);
        });

        // Upload files to the server
        const response = await fetch(`/api/chat-projects/${project.projectId}/upload`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Failed to upload files');
        }

        const result = await response.json();
        
        // Update the local files state with the server response
        setFiles(result.project.files || []);
        
      } catch (error) {
        console.error('Error uploading files:', error);
        // Could add a toast notification here
      }
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={onBackToThreads}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: project?.color + '20', color: project?.color }}
          >
            <Folder className="w-5 h-5" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">{project?.name}</h1>
        </div>
        
        {project?.description && (
          <p className="text-gray-600 mb-2">{project.description}</p>
        )}
      </div>

            {/* Project Configuration - Compact Cards */}
      <div className="p-6">
        <div className="grid grid-cols-2 gap-4 max-w-[80%] mx-auto">
          {/* Files Card */}
          <div 
            className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-gray-300 transition-colors"
            onClick={() => setShowFilesDialog(true)}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-gray-900">Add files</h3>
              <Upload className="w-4 h-4 text-gray-500" />
            </div>
            <p className="text-sm text-gray-500">
              {files.length > 0 
                ? `${files.length} file${files.length === 1 ? '' : 's'}`
                : 'Chats in this project can access file content'
              }
            </p>
          </div>

          {/* Instructions Card */}
          <div 
            className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-gray-300 transition-colors"
            onClick={() => setShowInstructionsDialog(true)}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-gray-900">Add instructions</h3>
              <Edit2 className="w-4 h-4 text-gray-500" />
            </div>
            <p className="text-sm text-gray-500">
              {systemInstructions 
                ? systemInstructions.split('\n')[0].substring(0, 60) + (systemInstructions.length > 60 ? '...' : '')
                : 'Tailor the way AIVIA responds in this project'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Threads Section */}
      <div className="flex-1 overflow-hidden">
        <div className="p-6 max-w-[80%] mx-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Threads</h3>
            <Button onClick={onNewThread} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              New thread
            </Button>
          </div>

          <ScrollArea className="h-full">
            {projectSessions.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">No threads yet</h4>
                <p className="text-gray-500 mb-4">Start a conversation in this project</p>
                <Button onClick={onNewThread}>Create first thread</Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Today's threads */}
                {groupedSessions.today.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Today</h4>
                    <div className="space-y-2">
                      {groupedSessions.today.map((session) => (
                        <div
                          key={session.id}
                          onClick={() => onSessionSelect(session.id)}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            activeSessionId === session.id
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <h5 className="font-medium text-sm text-gray-900 truncate">
                            {session.title}
                          </h5>
                          <p className="text-xs text-gray-500 mt-1">
                            {format(new Date(session.updatedAt), 'h:mm a')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Yesterday's threads */}
                {groupedSessions.yesterday.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Yesterday</h4>
                    <div className="space-y-2">
                      {groupedSessions.yesterday.map((session) => (
                        <div
                          key={session.id}
                          onClick={() => onSessionSelect(session.id)}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            activeSessionId === session.id
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <h5 className="font-medium text-sm text-gray-900 truncate">
                            {session.title}
                          </h5>
                          <p className="text-xs text-gray-500 mt-1">
                            {format(new Date(session.updatedAt), 'h:mm a')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Other time periods */}
                {groupedSessions.last7Days.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Last 7 Days</h4>
                    <div className="space-y-2">
                      {groupedSessions.last7Days.map((session) => (
                        <div
                          key={session.id}
                          onClick={() => onSessionSelect(session.id)}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            activeSessionId === session.id
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <h5 className="font-medium text-sm text-gray-900 truncate">
                            {session.title}
                          </h5>
                          <p className="text-xs text-gray-500 mt-1">
                            {format(new Date(session.updatedAt), 'MMM d')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {groupedSessions.last30Days.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Last 30 Days</h4>
                    <div className="space-y-2">
                      {groupedSessions.last30Days.map((session) => (
                        <div
                          key={session.id}
                          onClick={() => onSessionSelect(session.id)}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            activeSessionId === session.id
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <h5 className="font-medium text-sm text-gray-900 truncate">
                            {session.title}
                          </h5>
                          <p className="text-xs text-gray-500 mt-1">
                            {format(new Date(session.updatedAt), 'MMM d')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {groupedSessions.older.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Older</h4>
                    <div className="space-y-2">
                      {groupedSessions.older.map((session) => (
                        <div
                          key={session.id}
                          onClick={() => onSessionSelect(session.id)}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            activeSessionId === session.id
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <h5 className="font-medium text-sm text-gray-900 truncate">
                            {session.title}
                          </h5>
                          <p className="text-xs text-gray-500 mt-1">
                            {format(new Date(session.updatedAt), 'MMM d, yyyy')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>

      {/* Files Dialog */}
      <AlertDialog open={showFilesDialog} onOpenChange={setShowFilesDialog}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Project Files</AlertDialogTitle>
            <AlertDialogDescription>
              Upload files to provide context for chats in this project
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div 
            className="py-4"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="mb-4">
              <input
                type="file"
                multiple
                className="hidden"
                onChange={handleFileUpload}
                id="file-upload-input"
                accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.xls,.ppt,.pptx,.png,.jpg,.jpeg,.gif"
              />
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={() => document.getElementById('file-upload-input')?.click()}
              >
                <Upload className="w-4 h-4" />
                Upload files
              </Button>
            </div>
            
            {files.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {files.map((file: any) => (
                  <div key={file.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                    <FileText className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{file.name}</div>
                      <div className="text-xs text-gray-500">{file.size} bytes</div>
                    </div>
                    <button
                      onClick={() => handleRemoveFile(file.id)}
                      className="p-1 hover:bg-gray-100 rounded"
                      title="Remove file"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragOver 
                  ? 'border-orange-400 bg-orange-50' 
                  : 'border-gray-200'
              }`}>
                <FileText className={`w-8 h-8 mx-auto mb-2 ${
                  isDragOver ? 'text-orange-500' : 'text-gray-400'
                }`} />
                <p className={`text-sm ${isDragOver ? 'text-orange-700' : 'text-gray-600'}`}>
                  {isDragOver ? 'Drop files here' : 'No files uploaded yet'}
                </p>
                <p className={`text-xs ${isDragOver ? 'text-orange-600' : 'text-gray-500'}`}>
                  {isDragOver ? 'Release to upload' : 'Upload files or drag and drop to provide context for this project'}
                </p>
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Instructions Dialog */}
      <AlertDialog open={showInstructionsDialog} onOpenChange={setShowInstructionsDialog}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Project Instructions</AlertDialogTitle>
            <AlertDialogDescription>
              Customize how the AI responds in this project
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <textarea
              value={systemInstructions}
              onChange={(e) => setSystemInstructions(e.target.value)}
              placeholder="Enter custom instructions for this project..."
              className="w-full h-40 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setSystemInstructions(project?.systemInstructions || '');
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSaveInstructions}
              disabled={updateProjectMutation.isPending}
            >
              {updateProjectMutation.isPending ? 'Saving...' : 'Save'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Inline form used in Edit Project Dialog
const EditProjectForm: React.FC<{ project: any; onClose: () => void }> = ({ project, onClose }) => {
  const [name, setName] = useState(project.name || '');
  const [description, setDescription] = useState(project.description || '');
  const [instructions, setInstructions] = useState(project.systemInstructions || '');
  const [isUploading, setIsUploading] = useState(false);
  const [files, setFiles] = useState<any[]>(project.files || []);
  const queryClient = useQueryClient();
  
  const handleSave = async () => {
    await apiRequest('PATCH', `/api/chat-projects/${project.id}`, {
      name: name.trim() || project.name,
      description: description.trim(),
      systemInstructions: instructions,
    });
    queryClient.invalidateQueries({ queryKey: ['/api/chat-projects'] });
    onClose();
  };
  
  const handleUpload = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const input = evt.target;
    const selected = Array.from(input.files || []);
    if (selected.length === 0) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      selected.forEach(f => formData.append('files', f));
      const resp = await fetch(`/api/chat-projects/${project.projectId}/upload`, {
        method: 'POST',
        body: formData
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'Upload failed');
      setFiles(data.project?.files || []);
      queryClient.invalidateQueries({ queryKey: ['/api/chat-projects'] });
    } catch (e) {
      console.error('Upload error', e);
    } finally {
      setIsUploading(false);
      input.value = '';
    }
  };
  
  const handleRemoveFile = async (fileId: string) => {
    const updated = files.filter(f => f.id !== fileId);
    setFiles(updated);
    try {
      await fetch(`/api/chat-projects/${project.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ files: updated })
      });
      queryClient.invalidateQueries({ queryKey: ['/api/chat-projects'] });
    } catch (e) {
      console.error('Remove file failed', e);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
        <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={6} />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">Files</label>
          <div className="flex items-center gap-2">
            <input id="project-file-upload" type="file" className="hidden" multiple onChange={handleUpload} accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.xls,.ppt,.pptx,.png,.jpg,.jpeg,.gif" />
            <Button variant="outline" size="sm" onClick={() => document.getElementById('project-file-upload')?.click()} disabled={isUploading}>
              {isUploading ? 'Uploading...' : 'Add files'}
            </Button>
          </div>
        </div>
        {files.length === 0 ? (
          <div className="text-sm text-gray-500">No files yet.</div>
        ) : (
          <div className="max-h-48 overflow-auto divide-y border rounded">
            {files.map((f) => (
              <div key={f.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <div className="truncate">
                  <div className="font-medium truncate">{f.name || f.filename || 'File'}</div>
                  <div className="text-gray-500 text-xs">{f.type} · {Math.round((f.size || 0)/1024)} KB</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleRemoveFile(f.id)}>
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave}>Save changes</Button>
      </div>
    </div>
  );
};

// Helper function for user initials
const getUserInitials = (user: any) => {
  if (!user?.email) return 'U';
  const parts = user.email.split('@')[0].split('.');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return user.email[0].toUpperCase();
};

// Traditional View Sidebar
export const TraditionalSidebar: React.FC<{
  onNavigate: (canvas: CanvasType) => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  user: any;
  userMenuOpen: boolean;
  setUserMenuOpen: (open: boolean) => void;
  logout: () => void;
  onThreadsMouseEnter: () => void;
  onThreadsMouseLeave: () => void;
  hideClients?: boolean;
  alphaGates?: AlphaFeatureGates;
  onImportLegacyChats?: () => void;
}> = ({ onNavigate, expanded, onToggleExpanded, user, userMenuOpen, setUserMenuOpen, logout, onThreadsMouseEnter, onThreadsMouseLeave, hideClients = false, alphaGates, onImportLegacyChats }) => {
  const [currentLocation, nav] = useLocation();
  const { isDarkMode, setIsDarkMode } = useTheme();

  const canvasPathMap: Record<string, string[]> = {
    threads: ['/threads'],
    payments: ['/finances', '/invoices', '/reports', '/statements', '/unclaimed'],
    clients: ['/clients'],
    trips: ['/trips'],
    pipeline: ['/pipeline'],
  };
  const isCanvasActive = (canvas: string) =>
    (canvasPathMap[canvas] || []).some(p => currentLocation === p || currentLocation.startsWith(p + '/'));

  // Header + any active notice banner, so the user menu stays in view
  const sidebarHeight = BELOW_CHROME_HEIGHT;

  // Only use email when user is fully loaded to prevent gravatar fetch with empty email
  const email = user?.email || '';

  // Only fetch gravatar when we have a valid email
  const { avatarUrl: gravatarAvatarUrl } = useGravatar(email);

  // Ensure stable avatar URL with proper fallback hierarchy
  const displayAvatar = useMemo(() => {
    // 1. First priority: user's stored avatar URL
    if (user?.avatarUrl) {
      return user.avatarUrl;
    }

    // 2. Second priority: high-quality fetched gravatar (if available)
    if (gravatarAvatarUrl) {
      return gravatarAvatarUrl;
    }

    // 3. Fallback: immediately compute gravatar URL from email (prevents flash of default)
    if (email) {
      // This provides immediate avatar on mount, before async fetch completes
      return GravatarService.getAvatarUrl(email, 200);
    }

    // 4. Last resort: empty string (will show initials)
    return '';
  }, [user?.avatarUrl, gravatarAvatarUrl, email]);
  const { isEnabled: isResolvedEnabled, isLoading: flagsLoading } = useResolvedFeatureFlags();

  // AR-445 — Threads runs on the Phase-1 navigation too while its flags are on.
  if (!flagsLoading && PHASE1_FLAGS.every((f) => isResolvedEnabled(f))) {
    return <Phase1Rail onMenuStateChange={setUserMenuOpen} onImportLegacyChats={onImportLegacyChats} />;
  }

  type SidebarMenuItem = {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    canvas?: CanvasType;
    disabled: boolean;
    comingSoon: boolean;
  };

  const menuItems: SidebarMenuItem[] = [
    { icon: MessageSquare, label: 'Threads', canvas: 'threads', disabled: false, comingSoon: false },
    ...(flagsLoading || !isResolvedEnabled('invoicingMvp')
      ? []
      : [{ icon: Receipt, label: 'Finances', canvas: 'payments' as CanvasType, disabled: false, comingSoon: false }]),
    { icon: Users, label: 'CRM (Soon)', disabled: true, comingSoon: false },
  ];

  /* FUTURE: Role-based full TraditionalSidebar nav — uncomment and replace menuItems above.
  const showFullNav = isAdminRole(user?.role);
  const featureFlags = getFeatureFlags();
  const gates = alphaGates ?? { clients: false, trips: false, pipeline: false, dashboard: false, proposals: false, isAdmin: false };
  let menuItems: SidebarMenuItem[];
  if (!showFullNav) {
    menuItems = [ ... current simplified array ... ];
  } else {
    menuItems = [
      { icon: MessageSquare, label: 'Threads', canvas: 'threads' as CanvasType, disabled: false, comingSoon: false },
      {
        icon: Filter,
        label: 'Pipeline',
        canvas: 'pipeline' as CanvasType,
        disabled: isFeatureGated(gates, 'pipeline'),
        comingSoon: isFeatureGated(gates, 'pipeline'),
      },
      {
        icon: Users,
        label: 'Clients',
        canvas: 'clients' as CanvasType,
        disabled: isFeatureGated(gates, 'clients'),
        comingSoon: isFeatureGated(gates, 'clients'),
      },
      {
        icon: Calendar,
        label: 'Trips',
        canvas: 'trips' as CanvasType,
        disabled: isFeatureGated(gates, 'trips'),
        comingSoon: isFeatureGated(gates, 'trips'),
      },
      {
        icon: FileText,
        label: 'Proposals',
        canvas: 'proposals' as CanvasType,
        disabled: isFeatureGated(gates, 'proposals'),
        comingSoon: isFeatureGated(gates, 'proposals'),
      },
      ...(featureFlags.booking
        ? [{ icon: Plane, label: 'Booking', canvas: 'booking' as CanvasType, disabled: false, comingSoon: false }]
        : []),
      ...(isResolvedEnabled('invoicingMvp')
        ? [{ icon: Receipt, label: 'Finances', canvas: 'payments' as CanvasType, disabled: false, comingSoon: false }]
        : []),
      ...(featureFlags.analytics
        ? [{ icon: BarChart3, label: 'Analytics', canvas: 'analytics' as CanvasType, disabled: false, comingSoon: false }]
        : []),
      ...(featureFlags.digitalPresence
        ? [{ icon: Globe, label: 'Digital Presence', canvas: 'content' as CanvasType, disabled: false, comingSoon: false }]
        : []),
      {
        icon: LayoutGrid,
        label: 'Dashboard',
        canvas: 'dashboard' as CanvasType,
        disabled: isFeatureGated(gates, 'dashboard'),
        comingSoon: isFeatureGated(gates, 'dashboard'),
      },
    ];
    if (hideClients) {
      menuItems = menuItems.filter(m => m.canvas !== 'clients');
    }
  }
  */

  if (!expanded) {
    // Collapsed sidebar - navigation icons only
    return (
      <div className="relative flex w-12 flex-col bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700" style={{ height: sidebarHeight }}>
        <button
          onClick={() => {
            onToggleExpanded();
          }}
          className="absolute top-1/2 right-[-15px] z-[200] flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-md transition-all hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700"
          style={{ pointerEvents: 'auto' }}
          title="Expand sidebar"
          aria-label="Expand sidebar"
        >
          <ChevronRight className="w-3 h-3 text-gray-400 dark:text-gray-300" />
        </button>

        {/* Navigation section - grows to fill space */}
        <nav aria-label="Main" className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3 space-y-2">
          {menuItems.map((item) => {
            const active = item.canvas ? isCanvasActive(item.canvas) : false;
            return (
            <button
              key={item.label}
              aria-disabled={item.disabled || undefined}
              onClick={() => {
                if (item.disabled || !item.canvas) return;
                onNavigate(item.canvas);
              }}
              onMouseEnter={() => item.canvas === 'threads' ? onThreadsMouseEnter() : undefined}
              onMouseLeave={() => item.canvas === 'threads' ? onThreadsMouseLeave() : undefined}
              className={`group relative flex h-8 w-8 items-center justify-center rounded-lg transition-colors before:absolute before:-inset-x-2 before:-inset-y-1 before:content-[''] ${
                item.disabled 
                  ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                  : active
                    ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              title={item.label + (item.comingSoon ? ' (Soon)' : '')}
            >
              <item.icon className={`h-4 w-4 ${item.disabled ? 'opacity-50' : ''}`} />
              <div className="pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 z-50">
                {item.label}{item.comingSoon && <span className="ml-1 text-gray-400">(Soon)</span>}
              </div>
            </button>
            );
          })}
        </nav>

        {/* User Menu at bottom - uses shared UserMenu component (T-383) */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-2">
          <UserMenu expanded={false} onMenuStateChange={setUserMenuOpen} onImportLegacyChats={onImportLegacyChats} />
        </div>
      </div>
    );
  }

  // Expanded sidebar - full layout
  return (
    <div className="relative flex w-56 flex-col bg-gray-50/50 dark:bg-gray-900/50 border-r border-gray-200 dark:border-gray-700" style={{ height: sidebarHeight }}>
      <button
        onClick={onToggleExpanded}
        className="absolute top-4 right-4 z-[200] rounded p-1 text-gray-400 dark:text-gray-500 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
        title="Collapse sidebar"
        aria-label="Collapse sidebar"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
      </button>

      {/* Navigation section - grows to fill space */}
      <div className="flex-1 overflow-y-auto px-3 pt-4 pb-2">
        <p className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100 tracking-tight">Navigation</p>
        <nav aria-label="Main" className="space-y-0.5">
          {menuItems.map((item) => {
            const active = item.canvas ? isCanvasActive(item.canvas) : false;
            return (
            <button
              key={item.label}
              aria-disabled={item.disabled || undefined}
              onClick={() => {
                if (item.disabled || !item.canvas) return;
                onNavigate(item.canvas);
              }}
              onMouseEnter={() => item.canvas === 'threads' ? onThreadsMouseEnter() : undefined}
              onMouseLeave={() => item.canvas === 'threads' ? onThreadsMouseLeave() : undefined}
              className={`relative flex w-full items-center space-x-3 rounded-lg px-3 py-2 text-sm text-left transition-colors ${
                item.disabled 
                  ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                  : active
                    ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <item.icon className={`w-4 h-4 ${item.disabled ? 'opacity-50' : ''}`} />
              <span className={item.disabled ? 'opacity-70' : ''}>{item.label}</span>
              {item.comingSoon && (
                <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">Soon</span>
              )}
            </button>
            );
          })}
        </nav>
      </div>

      {/* User Menu at bottom - uses shared UserMenu component (T-383) */}
      <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-3">
        <UserMenu expanded={true} onMenuStateChange={setUserMenuOpen} onImportLegacyChats={onImportLegacyChats} />
      </div>
    </div>
  );
};

const EMPTY_PROVIDERS: any[] = [];
const EMPTY_SUPPLIER_RESULTS: SupplierCard[] = [];
const EMPTY_PROJECTS: any[] = [];
const EMPTY_CONTACTS: any[] = [];
const EMPTY_TRIPS: any[] = [];
const EMPTY_LINKED: any[] = [];

// Main Canvas UI Component
const CanvasUI: React.FC<{ hideClientsInThreads?: boolean }> = ({ hideClientsInThreads = true }) => {
  const { user, logout } = useAuth();
  const queryClient = useQueryClient();
  const featureFlags = getFeatureFlags();
  const { isEnabled: isResolvedFeatureFlagEnabled, isLoading: resolvedFlagsLoading } = useResolvedFeatureFlags();
  const { toast } = useToast();

  // Clean up stale command bar draft from localStorage (was leaking across users)
  useEffect(() => {
    try { localStorage.removeItem('aivia_command_draft'); } catch {}
  }, []);

  // Alpha feature gates for non-admin users
  const { gates: alphaGates } = useAlphaFeatureGates();

  // Legacy chat import - check if user has old chats to import
  const {
    showImportModal,
    isManualRecheck,
    closeImportModal,
    openImportModalForRecheck,
    handleImportComplete,
  } = useLegacyChatImport({ 
    checkOnce: true, 
    checkDelay: 2000,
    onImportSuccess: () => {
      // Refresh sessions list only (not session details, to avoid overwriting cache)
      queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions', user?.id], exact: true });
    },
  });
  
  // UI State
  const [viewMode, setViewMode] = useState<ViewMode>('canvas');
  const [currentCanvas, setCurrentCanvas] = useState<CanvasType>('home');
  // const [activityStreamOpen, setActivityStreamOpen] = useState(false); // Activity Stream - COMMENTED OUT FOR LATER
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // Note: isProcessing removed - replaced by isProcessingMessage computed from mutation state
  const [canvasData, setCanvasData] = useState<any>(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  // Track if the homepage inline command bar is visible (for showing global command bar when it's hidden)
  const [homeInlineCommandBarVisible, setHomeInlineCommandBarVisible] = useState(true);
  
  // Chat state - use the real AIAssistant functionality
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [showThreadsPanel, setShowThreadsPanel] = useState(false);
  const threadsHoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // const [threadsPanelPinned, setThreadsPanelPinned] = useState(false); // Pin disabled (commented by request)
  const [renameLock, setRenameLock] = useState(false);
  const renameLockRef = useRef(renameLock);
  const [showThreadSearchDialog, setShowThreadSearchDialog] = useState(false);
  const [threadSearchQuery, setThreadSearchQuery] = useState('');
  const [hoverClickLockUntil, setHoverClickLockUntil] = useState<number>(0);
  const hoverClickLockUntilRef = useRef(hoverClickLockUntil);
  const [activeProjectId, setActiveProjectId] = useState<number | null>(null);
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  const [selectedProvider, setSelectedProvider] =
    useState<string>(DEFAULT_AI_PROVIDER);
  const [selectedModel, setSelectedModel] =
    useState<string>(DEFAULT_AI_MODEL);
  const [currentLocation, navigate] = useLocation();
  const [autoOpenNewTrip, setAutoOpenNewTrip] = useState<boolean>(false);

  // External Mode state - per-session settings
  const [externalModeBySession, setExternalModeBySession] = useState<
    Record<number, { enabled: boolean; provider: string; model: string }>
  >({});
  // Pending external mode - used when user enables external mode before a session exists
  const [pendingExternalMode, setPendingExternalMode] = useState<{
    enabled: boolean;
    provider: string;
    model: string;
  } | null>(null);
  const [showDefaultModelSheet, setShowDefaultModelSheet] = useState(false);

  // Streaming state for real-time AI responses
  const [streamingMessage, setStreamingMessage] = useState<string | undefined>();
  const [streamingRefetchDone, setStreamingRefetchDone] = useState(false);
  // SSE streaming is disabled - using simulated streaming instead (REST generates AI, client displays progressively)
  // These states are kept for potential future SSE implementation
  const [useStreaming] = useState(false); // Disabled - simulated streaming used instead
  const [streamingCoordinationActive, setStreamingCoordinationActive] = useState(false);
  
  // [SIMULATED_STREAMING] State for progressive text display of REST-generated AI responses
  // This provides ChatGPT-style typing effect without requiring SSE/PlannerService
  const [simulatedStreamingContent, setSimulatedStreamingContent] = useState<string | undefined>();
  const simulatedStreamingRef = useRef<number | null>(null); // requestAnimationFrame returns number

  // [AGENTCORE_THREADS] Real token streaming from the AIVIA agent on AgentCore, per-user flag.
  // Users without `agentcoreThreads` never reach this path and keep the existing engine.
  const agentcoreThreadsEnabled = isResolvedFeatureFlagEnabled('agentcoreThreads');
  // AIVIA threads show no CRM actions (Link Client / Link Trip) until CRM launches for the advisor.
  // Hidden until the flags have loaded: before that every flag reads false, which would show them.
  const crmVisible = !resolvedFlagsLoading && (!agentcoreThreadsEnabled || isResolvedFeatureFlagEnabled('agentcoreCrmTools'));
  /** Project threads where the advisor chose Global in the composer (see handleCommand). */
  const globalScopeSessions = useRef(new Set<number>());
  const agentThread = useAgentThread({
    onBusyChange: (busy, sessionId) => {
      setIsWaitingForResponse(busy);
      setWaitingForSessionId(busy ? sessionId : null);
      if (!busy) queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions', user?.id], exact: true });
    },
  });
  useThreadUrlSync(activeSessionId, agentcoreThreadsEnabled && currentCanvas === 'threads');
  useAgentWarmup(agentcoreThreadsEnabled && currentCanvas === 'threads');

  // SSE streaming hook - currently disabled (useStreaming=false)
  // Kept for potential future true streaming implementation
  const streamingResult = useThreadsStreaming({
    sessionId: activeSessionId ?? 0,
    enabled: useStreaming && isWaitingForResponse && !!activeSessionId && !!streamingMessage && streamingCoordinationActive,
    message: streamingMessage,
    projectId: activeProjectId ?? undefined,
    throttleMs: 100,
    // SSE handlers - currently unused since useStreaming=false
    onComplete: () => {
      setStreamingCoordinationActive(false);
      setStreamingMessage(undefined);
      setStreamingRefetchDone(true);
      setIsWaitingForResponse(false);
    },
    onError: () => {
      setStreamingCoordinationActive(false);
      setStreamingMessage(undefined);
      setStreamingRefetchDone(true);
      setIsWaitingForResponse(false);
    },
  });

  // Cleanup simulated streaming animation on unmount
  useEffect(() => {
    return () => {
      if (simulatedStreamingRef.current) {
        cancelAnimationFrame(simulatedStreamingRef.current);
        simulatedStreamingRef.current = null;
      }
    };
  }, []);

  // Progressively display AI response (ChatGPT-style typing effect)
  // Uses requestAnimationFrame for smooth, frame-synchronized rendering
  const simulateStreamingDisplay = useCallback((
    content: string,
    sessionId: number,
    aiMessage: any,
    onComplete: () => void
  ) => {
    // Clear any existing simulation
    if (simulatedStreamingRef.current) {
      cancelAnimationFrame(simulatedStreamingRef.current);
      simulatedStreamingRef.current = null;
    }

    const CHARS_PER_SECOND = 800; // Target typing speed (characters per second)
    const MIN_CHUNK_SIZE = 5; // Minimum characters per frame for visibility
    let currentIndex = 0;
    let lastFrameTime = performance.now();

    setSimulatedStreamingContent('');

    const animate = (currentTime: number) => {
      // Calculate time elapsed since last frame
      const deltaTime = currentTime - lastFrameTime;
      lastFrameTime = currentTime;

      // Calculate how many characters to add based on elapsed time
      // This ensures consistent typing speed regardless of frame rate
      const charsToAdd = Math.max(
        MIN_CHUNK_SIZE,
        Math.round((CHARS_PER_SECOND * deltaTime) / 1000)
      );
      
      currentIndex = Math.min(currentIndex + charsToAdd, content.length);
      setSimulatedStreamingContent(content.slice(0, currentIndex));

      if (currentIndex >= content.length) {
        // Animation complete - don't clear simulatedStreamingContent here!
        // The onComplete callback will set streamingRefetchDone=true which hides the UI.
        // Clearing here causes a flash due to React state batching (both updates in same frame).
        simulatedStreamingRef.current = null;
        onComplete();
      } else {
        // Schedule next frame
        simulatedStreamingRef.current = requestAnimationFrame(animate);
      }
    };

    // Start animation on next frame
    simulatedStreamingRef.current = requestAnimationFrame(animate);
  }, []);

  // Track which session ID has the pending request (persists in component state)
  const [waitingForSessionId, setWaitingForSessionId] = useState<number | null>(null);
  
  // Check if there's a pending request for the current session from multiple sources:
  // 1. Client-side cache flag (for same-session navigation)
  // 2. Server-side pendingResponse flag (for page refresh - survives browser reload)
  // Note: Read from cache since activeSessionData is declared later in the component
  const cachedSessionData = activeSessionId 
    ? queryClient.getQueryData(['/api/smart-chat/sessions', activeSessionId]) as any
    : null;
  const pendingFromClientCache = cachedSessionData?.__pendingRequest === true;
  const pendingFromServer = cachedSessionData?.session?.pendingResponse === true;
  
  // Computed: should show thinking indicator for current session
  // Only show if:
  // 1. Local state says waiting AND it's for this specific session, OR
  // 2. Client cache says this session has a pending request (same-page navigation), OR
  // 3. Server says this session has a pending response (survives page refresh)
  const shouldShowThinking = 
    (isWaitingForResponse && waitingForSessionId === activeSessionId) || 
    pendingFromClientCache ||
    pendingFromServer;

  useEffect(() => {
    renameLockRef.current = renameLock;
  }, [renameLock]);

  useEffect(() => {
    hoverClickLockUntilRef.current = hoverClickLockUntil;
  }, [hoverClickLockUntil]);

  // Auto-switch to Trips canvas when path starts with /trips
  useEffect(() => {
    try {
      const path = currentLocation || window.location.pathname;
      if (path.startsWith('/trips')) {
        setCurrentCanvas('trips');
        setAutoOpenNewTrip(path === '/trips/new');
      }
    } catch {}
  }, [currentLocation]);

  // Auto-switch to Pipeline canvas when path is /pipeline
  useEffect(() => {
    try {
      const path = currentLocation || window.location.pathname;
      if (path === '/pipeline') {
        setCurrentCanvas('pipeline');
      }
    } catch {}
  }, [currentLocation]);

  // Track if we've already processed URL sessionId param to avoid redundant processing
  const urlSessionIdProcessedRef = useRef<string | null>(null);
  
  // Auto-switch to Threads canvas when path is /threads
  // Also restore sessionId from URL query param (survives route changes with different wrappers)
  useEffect(() => {
    try {
      const path = currentLocation || window.location.pathname;
      if (path === '/threads' || path.startsWith('/threads?')) {
        setCurrentCanvas('threads');
        
        // Check for sessionId in URL query params (set when navigating from other pages)
        const urlParams = new URLSearchParams(window.location.search);
        const sessionIdParam = urlParams.get('sessionId');
        
        // Only process if there's a sessionId param AND we haven't processed this exact URL yet
        if (sessionIdParam && urlSessionIdProcessedRef.current !== sessionIdParam) {
          const parsedId = parseInt(sessionIdParam, 10);
          if (!isNaN(parsedId)) {
            urlSessionIdProcessedRef.current = sessionIdParam;
            setActiveSessionId(parsedId);
            // Clean up URL after restoring state
            window.history.replaceState({}, '', '/threads');
          }
        }
      } else {
        // Reset the processed ref when navigating away from /threads
        // This allows the same sessionId to be restored on subsequent cross-route navigations
        urlSessionIdProcessedRef.current = null;
      }
    } catch {}
  }, [currentLocation]); // Removed activeSessionId - only react to URL/location changes

  // Auto-switch to Clients canvas when path starts with /clients
  useEffect(() => {
    try {
      const path = currentLocation || window.location.pathname;
      if (path.startsWith('/clients')) {
        setCurrentCanvas('clients');
      }
    } catch {}
  }, [currentLocation]);

  // (Removed enforcing redirect to /clients to avoid blocking navigation to other routes.)

  useEffect(() => {
    return () => {
      if (threadsHoverTimeoutRef.current) {
        clearTimeout(threadsHoverTimeoutRef.current);
        threadsHoverTimeoutRef.current = null;
      }
    };
  }, []);

  // Helper functions for managing threads panel hover
  const handleThreadsMouseEnter = () => {
    if (currentCanvas === 'home') return;
    if (threadsHoverTimeoutRef.current) {
      clearTimeout(threadsHoverTimeoutRef.current);
      threadsHoverTimeoutRef.current = null;
    }
    setShowThreadsPanel(true);
  };

  useEffect(() => {
    if (currentCanvas === 'home') {
      setShowThreadsPanel(false);
    }
  }, [currentCanvas]);

  const handleThreadsMouseLeave = (e?: React.MouseEvent) => {
    // Respect the click lock (prevents closing when interacting with menus/buttons)
    if (Date.now() < hoverClickLockUntilRef.current) return;

    const target = e?.relatedTarget as Element | null;
    const intoPoppers = !!(target && typeof target.closest === 'function' && target.closest('[data-radix-popper-content-wrapper]'));
    const intoPanel = !!(target && typeof target.closest === 'function' && target.closest('[data-threads-panel="true"]'));
    const intoHoverRegion = !!(target && typeof target.closest === 'function' && target.closest('[data-threads-hover-region="true"]'));
    if (intoPoppers || intoPanel || intoHoverRegion) return;

    // Small grace period to allow moving cursor from hover trigger into the panel
    if (threadsHoverTimeoutRef.current) {
      clearTimeout(threadsHoverTimeoutRef.current);
      threadsHoverTimeoutRef.current = null;
    }
    threadsHoverTimeoutRef.current = setTimeout(() => {
      threadsHoverTimeoutRef.current = null;
      // Double-check the lock hasn't been set during the timeout
      if (Date.now() < hoverClickLockUntilRef.current) return;
      setShowThreadsPanel(false);
    }, 220);
  };

  // Search data from shared hook (cached across canvases)
  const { contactsData, tripsData, searchDataLoaded } = useCommandBarSearchData();
  
  // Mock activity data - COMMENTED OUT FOR LATER
  // const [activities] = useState<ActivityItem[]>([
  //   {
  //     id: '1',
  //     type: 'suggestion',
  //     title: 'Follow-up Suggestion',
  //     description: "You haven't followed up with the Johnson family in 3 weeks. Shall I draft a check-in email?",
  //     timestamp: new Date(Date.now() - 1000 * 60 * 30),
  //     actions: [
  //       { label: 'Accept', type: 'primary' },
  //       { label: 'Dismiss', type: 'secondary' }
  //     ]
  //   }
  // ]);

  // Chat functionality queries (from AIAssistant)
  const { data: sessions } = useQuery<any[]>({
    queryKey: ['/api/smart-chat/sessions', user?.id],
    queryFn: async () => {
      try {
        const result = await apiRequest('GET', `/api/smart-chat/sessions?userId=${user?.id}`);
        return Array.isArray(result) ? result : [];
      } catch (error) {
        console.error('Error fetching sessions:', error);
        return [];
      }
    },
    enabled: !!user?.id
  });

  const { data: activeSessionData, isLoading: isLoadingSession } = useQuery({
    queryKey: ['/api/smart-chat/sessions', activeSessionId],
    queryFn: async () => {
      if (!activeSessionId) return null;
      const result = await apiRequest('GET', `/api/smart-chat/sessions/${activeSessionId}`);
      return result;
    },
    enabled: !!activeSessionId,
    staleTime: 30 * 1000, // Cache for 30 seconds to prevent aggressive refetching
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes (TanStack v5)
    // Note: Flicker prevention is handled by the loading state check at render time
    // (activeSessionId && !activeSessionData && isLoadingSession) shows a spinner
  });

  const { data: providersRaw } = useQuery<any[]>({
    queryKey: ['/api/chat/providers'],
    enabled: true
  });
  const providers = providersRaw ?? EMPTY_PROVIDERS;
  const { data: userPreferences } = useQuery<any>({
    queryKey: ['/api/user/preferences'],
    enabled: !!user,
  });

  // Initialize AI provider/model from user preferences
  const prefProvider = userPreferences?.defaultProvider;
  const prefModel = userPreferences?.defaultModel;
  useEffect(() => {
    if (prefProvider && prefModel) {
      setSelectedProvider(prefProvider);
      setSelectedModel(prefModel);
    }
  }, [prefProvider, prefModel]);

  // External Mode helpers
  // Current external mode - use session-specific if available, otherwise pending
  const currentExternalMode = activeSessionId
    ? externalModeBySession[activeSessionId]
    : pendingExternalMode;
  const isExternalModeEnabled = currentExternalMode?.enabled ?? false;

  const toggleExternalMode = useCallback(() => {
    const defaultProvider = userPreferences?.defaultProvider || DEFAULT_AI_PROVIDER;
    const defaultModel = userPreferences?.defaultModel || DEFAULT_AI_MODEL;
    
    if (activeSessionId) {
      // Session exists - toggle per-session mode
      setExternalModeBySession(prev => {
        const current = prev[activeSessionId];
        if (current?.enabled) {
          // Turning off - remove the entry
          const { [activeSessionId]: _, ...rest } = prev;
          return rest;
        } else {
          // Turning on - initialize with user's default provider/model
          return {
            ...prev,
            [activeSessionId]: {
              enabled: true,
              provider: defaultProvider,
              model: defaultModel,
            },
          };
        }
      });
    } else {
      // No session yet - toggle pending mode (will apply when session is created)
      setPendingExternalMode(prev => {
        if (prev?.enabled) {
          return null; // Turn off
        } else {
          return {
            enabled: true,
            provider: defaultProvider,
            model: defaultModel,
          };
        }
      });
    }
  }, [activeSessionId, userPreferences]);

  const updateExternalModeProvider = useCallback((provider: string) => {
    if (activeSessionId) {
      setExternalModeBySession(prev => ({
        ...prev,
        [activeSessionId]: {
          ...prev[activeSessionId],
          enabled: true,
          provider,
        },
      }));
    } else {
      setPendingExternalMode(prev => prev ? { ...prev, provider } : null);
    }
  }, [activeSessionId]);

  const updateExternalModeModel = useCallback((model: string) => {
    if (activeSessionId) {
      setExternalModeBySession(prev => ({
        ...prev,
        [activeSessionId]: {
          ...prev[activeSessionId],
          enabled: true,
          model,
        },
      }));
    } else {
      setPendingExternalMode(prev => prev ? { ...prev, model } : null);
    }
  }, [activeSessionId]);

  // Fetch projects data for the main component
  const { data: projects = [] } = useQuery<any[]>({
    queryKey: ['/api/chat-projects', user?.id],
    queryFn: async () => {
      return await apiRequest('GET', `/api/chat-projects?userId=${user?.id}`);
    },
    enabled: !!user?.id
  });

  const threadSearchResults = useMemo(() => {
    const normalizedQuery = threadSearchQuery.trim().toLowerCase();

    const threadList = (sessions || []).map((session) => {
      const title =
        (typeof session?.title === 'string' && session.title.trim().length > 0
          ? session.title.trim()
          : 'Untitled thread') || 'Untitled thread';
      const haystack = `${title} ${session?.id ?? ''}`.toLowerCase();
      return {
        ...session,
        __searchTitle: title,
        __searchHaystack: haystack,
      };
    });

    const projectList = (projects || []).map((project) => {
      const name =
        (typeof project?.name === 'string' && project.name.trim().length > 0
          ? project.name.trim()
          : typeof project?.title === 'string' && project.title.trim().length > 0
            ? project.title.trim()
            : 'Untitled project') || 'Untitled project';
      const description =
        (typeof project?.description === 'string' && project.description.trim().length > 0
          ? project.description.trim()
          : typeof project?.notes === 'string' && project.notes.trim().length > 0
            ? project.notes.trim()
            : '') || '';
      const haystack = `${name} ${description} ${project?.id ?? ''}`.toLowerCase();
      return {
        ...project,
        __searchName: name,
        __searchDescription: description,
        __searchHaystack: haystack,
      };
    });

    const filteredThreads = normalizedQuery
      ? threadList.filter((thread) => thread.__searchHaystack.includes(normalizedQuery))
      : threadList;

    const filteredProjects = normalizedQuery
      ? projectList.filter((project) => project.__searchHaystack.includes(normalizedQuery))
      : projectList;

    return {
      threads: filteredThreads.slice(0, 30),
      projects: filteredProjects.slice(0, 20),
    };
  }, [threadSearchQuery, sessions, projects]);

  const handleThreadSelect = useCallback(
    (sessionId: number, options?: { closePanel?: boolean }) => {
      const selectedSession = sessions?.find((s) => s.id === sessionId);

      if (selectedSession && selectedSession.chatProjectId !== activeProjectId) {
        setActiveProjectId(null);
        setSelectedProject(null);
      }

      setActiveSessionId(sessionId);
      setCurrentCanvas('threads');
      
      // Include sessionId in URL to survive component remounts during route changes
      // (different routes have different wrappers which cause CanvasUI to remount)
      const currentPath = window.location.pathname;
      if (currentPath !== '/threads') {
        // Navigating from another page - pass sessionId via URL
        navigate(`/threads?sessionId=${sessionId}`);
      } else {
        // Already on threads - no need to include sessionId in URL
        navigate('/threads');
      }
      
      setShowThreadSearchDialog(false);
      setThreadSearchQuery('');
    },
    [sessions, activeProjectId, navigate],
  );

  const handleProjectSelect = useCallback(
    (projectId: number | null, project?: any, options?: { closePanel?: boolean }) => {
      if (projectId === null) {
        setActiveProjectId(null);
        setSelectedProject(null);
        setCurrentCanvas('threads');
        navigate('/threads');
      } else {
        const resolvedProject =
          project || projects.find((p) => p.id === projectId) || null;
        setActiveProjectId(projectId);
        setSelectedProject(resolvedProject);
        setActiveSessionId(null);
        setCurrentCanvas('project');
      }

      setShowThreadSearchDialog(false);
      setThreadSearchQuery('');
    },
    [navigate, projects],
  );

  // Create new session mutation
  const createSessionMutation = useMutation({
    mutationFn: (newSession: { title: string, provider: string, model: string, projectId?: number | null, originalPrompt?: string }) => {
      return apiRequest('POST', '/api/smart-chat/sessions', {
        ...newSession,
        userId: user?.id,
        chatProjectId: newSession.projectId !== undefined ? newSession.projectId : activeProjectId,
        metadata: newSession.originalPrompt ? { originalPrompt: newSession.originalPrompt } : undefined,
      });
    },
    onSuccess: (data: any) => {
      setActiveSessionId(data.id);
      queryClient.setQueryData(['/api/smart-chat/sessions', data.id], {
        session: data,
        messages: [],
        linkedClients: [],
        linkedContacts: [],
        linkedTrips: [],
        toolRuns: [],
      });
      // Update sessions list directly instead of broad invalidateQueries.
      // invalidateQueries with prefix ['/api/smart-chat/sessions'] also
      // invalidates the session detail we just seeded, triggering background
      // refetches that race with sendMessageMutation's optimistic updates.
      queryClient.setQueryData(
        ['/api/smart-chat/sessions', user?.id],
        (old: any[] | undefined) => (old ? [data, ...old] : [data]),
      );
    }
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: ({
      sessionId,
      content,
      toolSignal,
      externalMode,
    }: {
      sessionId: number;
      content: string;
      toolSignal?: ToolSignal | null;
      externalMode?: { provider: string; model: string } | null;
    }) => {
      const useExternalMode = externalMode != null;
      const streamingEnabled = useStreaming && !useExternalMode;
      return apiRequest('POST', `/api/smart-chat/sessions/${sessionId}/messages`, { 
        content,
        provider: useExternalMode ? externalMode.provider : selectedProvider,
        model: useExternalMode ? externalMode.model : selectedModel,
        mode: useExternalMode ? 'direct' : 'MCP',  // 'direct' bypasses orchestrator, 'MCP' uses it
        toolSignal: toolSignal ?? undefined,
        streamingEnabled,
      });
    },
    retry: false, // Prevent duplicate messages from network retries
    onMutate: async ({ sessionId, content, externalMode }) => {
      setIsWaitingForResponse(true);
      setStreamingRefetchDone(false); // Reset for new message
      setWaitingForSessionId(sessionId); // Track which session is waiting
      
      // Start SSE streaming for real-time UI feedback (only if enabled and not in external/direct mode)
      // This sets streamingMessage which activates the useThreadsStreaming hook
      if (useStreaming && !externalMode) {
        setStreamingMessage(content);
        setStreamingCoordinationActive(true);
      }
      
      const cacheKey = ['/api/smart-chat/sessions', sessionId];
      await queryClient.cancelQueries({ queryKey: cacheKey });
      const previousData = queryClient.getQueryData(cacheKey);
      
      const tempMessage = {
        id: Date.now() * -1,
        sessionId,
        role: 'user',
        content,
        createdAt: new Date().toISOString(),
        optimistic: true,
      };
      
      // Single atomic cache update: add temp message AND set __pendingRequest flag
      // This prevents the flag from being lost if two separate setQueryData calls race
      queryClient.setQueryData(cacheKey, (old: any) => {
        if (!old) {
          const sessions = queryClient.getQueryData<any[]>(['/api/smart-chat/sessions']) || [];
          const sessionInfo = sessions.find((s) => s.id === sessionId);
          return {
            session: sessionInfo || { id: sessionId, title: 'New Conversation' },
            messages: [tempMessage],
            __pendingRequest: true, // Include pending flag in fresh data
          };
        }
        const existing = old.messages || [];
        return {
          ...old,
          messages: [...existing, tempMessage],
          __pendingRequest: true, // Ensure pending flag is set (even if already present)
        };
      });
      
      return { previousData, cacheKey };
    },
    onSuccess: (data: any) => {
      const sessionId = data.updatedSession?.id || activeSessionId;
      const aiContent = data?.aiMessage?.content;
      
      // If we have an AI message, use progressive display for typing effect
      // Check both aiMessage object AND its content to ensure all required data exists
      if (data.aiMessage && aiContent && sessionId) {
        // First, update cache with user message only (AI will appear progressively)
        queryClient.setQueryData(['/api/smart-chat/sessions', sessionId], (oldData: any) => {
          if (Array.isArray(oldData)) {
            return {
              session: data.updatedSession,
              messages: [data.userMessage].filter(Boolean),
            };
          }
          const existingMessages = oldData?.messages || [];
          const messagesWithoutTemp = existingMessages.filter((msg: any) => msg.id > 0);
          // Use .filter(Boolean) pattern to safely handle null/undefined userMessage
          // (consistent with normal flow at line 9236)
          const newMessages = [...messagesWithoutTemp, data.userMessage].filter(Boolean);
          return {
            session: data.updatedSession || oldData?.session,
            messages: newMessages,
          };
        });
        
        // Start simulated streaming display
        simulateStreamingDisplay(
          aiContent,
          sessionId,
          data.aiMessage,
          () => {
            // onComplete callback - update cache with full AI message and cleanup
            queryClient.setQueryData(['/api/smart-chat/sessions', sessionId], (oldData: any) => {
              // Guard: ensure aiMessage exists before accessing its properties
              // (aiContent check above only guarantees content exists, not the full object)
              if (!data.aiMessage) {
                return oldData;
              }
              
              if (Array.isArray(oldData)) {
                return {
                  session: data.updatedSession,
                  messages: [data.userMessage, data.aiMessage].filter(Boolean),
                };
              }
              const existingMessages = oldData?.messages || [];
              // Check if AI message already exists to avoid duplicates
              const hasAiMessage = existingMessages.some(
                (m: any) => m.id === data.aiMessage.id
              );
              if (hasAiMessage) return oldData;
              return {
                ...oldData,
                messages: [...existingMessages, data.aiMessage],
              };
            });
            
            // Cleanup state - set streamingRefetchDone first to hide streaming UI before hiding the container
            setStreamingRefetchDone(true);
            setIsWaitingForResponse(false);
            setStreamingMessage(undefined);
            setSimulatedStreamingContent(undefined); // Clear after UI is hidden to prevent flash
            
            // Refresh sessions list only (not session details, to avoid overwriting cache)
            queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions', user?.id], exact: true });
          }
        );
        return; // Exit - simulated streaming will handle the rest
      }
      
      // Normal flow (no AI message or no sessionId)
      setStreamingRefetchDone(true);
      setIsWaitingForResponse(false);
      setStreamingMessage(undefined);
      
      if (sessionId) {
        queryClient.setQueryData(['/api/smart-chat/sessions', sessionId], (oldData: any) => {
          // Handle cache corruption (oldData is array instead of session object)
          if (Array.isArray(oldData)) {
            return {
              session: data.updatedSession,
              messages: [data.userMessage, data.aiMessage].filter(Boolean),
              // Explicitly clear pending flag to prevent stuck "thinking" indicator
              __pendingRequest: undefined,
            };
          }
          
          const existingMessages = oldData?.messages || [];
          const messagesWithoutTemp = existingMessages.filter((msg: any) => msg.id > 0);
          
          // Build new messages array
          const newMessages = [data.userMessage, data.aiMessage].filter(Boolean);
          
          // Deduplicate by message ID to prevent race condition when cache
          // is updated by both refetch and mutation onSuccess
          const existingIds = new Set(messagesWithoutTemp.map((msg: any) => msg.id));
          const uniqueNewMessages = newMessages.filter((msg: any) => !existingIds.has(msg.id));
          
          // Combine and sort by createdAt to ensure correct order
          const allMessages = [...messagesWithoutTemp, ...uniqueNewMessages].sort((a: any, b: any) => {
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return dateA - dateB;
          });
          
          // Explicitly exclude __pendingRequest flag when creating updated data
          const { __pendingRequest: _pending, ...restOldData } = oldData || {};
          return {
            ...restOldData,
            session: data.updatedSession || oldData?.session,
            messages: allMessages,
          };
        });
        // Refresh sessions list only (not session details, to avoid overwriting cache)
        queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions', user?.id], exact: true });
      }
    },
    onError: async (error, variables, context) => {
      console.error('Error sending message:', error);
      
      // Check if this is a timeout/503 error - backend might still be processing
      // ApiError has a status property, regular Error doesn't
      const errorStatus = error instanceof ApiError ? error.status : (error as any)?.status;
      const errorMsg = error instanceof Error ? error.message : '';
      const isTimeoutError = 
        errorStatus === 503 ||
        errorMsg.includes('503') ||
        errorMsg.includes('Service Unavailable') ||
        errorMsg.includes('Application Error');
      
      // For timeout errors, try to poll for completion since backend often continues processing
      if (isTimeoutError && variables?.sessionId) {
        // IMPORTANT: Keep the loading UI visible during polling
        // - Keep isWaitingForResponse = true (shows "AIVIA is thinking...")
        // - Keep the optimistic user message in cache (from onMutate)
        // - Don't restore previousData or clear any state until polling completes
        
        // Cancel any background refetches that might interfere with our optimistic state
        await queryClient.cancelQueries({ queryKey: ['/api/smart-chat/sessions', variables.sessionId] });
        
        // Ensure loading state remains visible during polling
        // (onMutate already set this to true, but be explicit)
        setIsWaitingForResponse(true);
        setWaitingForSessionId(variables.sessionId);
        
        // Track the user message we sent to detect when it's been persisted
        const sentMessageContent = variables.content;
        const pollStartTime = Date.now();
        
        // Poll for completion with exponential backoff
        // Extended to handle responses that take 1-2+ minutes
        // Delays: 3s, 6s, 10s, 15s, 15s, 15s, 15s, 15s, 15s, 15s = ~2 min total
        const pollForCompletion = async (
          sessionId: number,
          attempts: number = 0,
          maxAttempts: number = 10
        ): Promise<boolean> => {
          if (attempts >= maxAttempts) return false;
          
          // More gradual backoff: 3s, 6s, 10s, then 15s intervals
          const delays = [3000, 6000, 10000, 15000, 15000, 15000, 15000, 15000, 15000, 15000];
          const delay = delays[Math.min(attempts, delays.length - 1)];
          await new Promise(resolve => setTimeout(resolve, delay));
          
          try {
            // Fetch fresh session data
            const result = await apiRequest('GET', `/api/smart-chat/sessions/${sessionId}`);
            
            // Check if we have a new AI message (the response we were waiting for)
            const messages = result?.messages || [];
            
            // Look for the persisted user message (not optimistic, has positive ID)
            const persistedUserMessage = messages.find((m: any) => 
              m.role === 'user' && 
              m.id > 0 && 
              m.content === sentMessageContent
            );
            
            // Look for an AI response that came after our user message
            const hasAiResponse = persistedUserMessage && messages.some((m: any) => {
              if (m.role !== 'assistant' || m.id <= 0) return false;
              const userMsgTime = new Date(persistedUserMessage.createdAt).getTime();
              const aiMsgTime = new Date(m.createdAt).getTime();
              return aiMsgTime >= userMsgTime;
            });
            
            if (hasAiResponse) {
              // Cancel any in-flight queries to prevent race conditions
              await queryClient.cancelQueries({ queryKey: ['/api/smart-chat/sessions', sessionId] });
              // Success! Replace cache entirely with server data (no optimistic leftovers)
              // Remove any __pendingRequest flag and ensure clean state
              const cleanResult = {
                ...result,
                __pendingRequest: undefined,
              };
              queryClient.setQueryData(['/api/smart-chat/sessions', sessionId], cleanResult);
              return true;
            }
            
            // Also check if pendingResponse is false (backend finished but maybe no new AI msg?)
            if (result?.session?.pendingResponse === false && persistedUserMessage) {
              // Cancel any in-flight queries to prevent race conditions
              await queryClient.cancelQueries({ queryKey: ['/api/smart-chat/sessions', sessionId] });
              const cleanResult = {
                ...result,
                __pendingRequest: undefined,
              };
              queryClient.setQueryData(['/api/smart-chat/sessions', sessionId], cleanResult);
              return true;
            }
            
            // Keep polling
            return pollForCompletion(sessionId, attempts + 1, maxAttempts);
          } catch (pollError) {
            console.error('Poll attempt failed:', pollError);
            return pollForCompletion(sessionId, attempts + 1, maxAttempts);
          }
        };
        
        const recovered = await pollForCompletion(variables.sessionId);
        
        if (recovered) {
          setStreamingRefetchDone(true);
          setIsWaitingForResponse(false);
          setWaitingForSessionId(null);
          setStreamingMessage(undefined);
          setSimulatedStreamingContent(undefined);
          // Refresh sessions list only (not session details, to avoid overwriting cache)
          queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions', user?.id], exact: true });
          // Don't show toast - seamless recovery is better UX
          return; // Don't show error
        }
        
        // If polling failed, fall through to normal error handling
      }
      
      // Normal error handling
      setStreamingRefetchDone(true);
      setIsWaitingForResponse(false);
      setWaitingForSessionId(null);
      setStreamingMessage(undefined);
      setSimulatedStreamingContent(undefined);
      
      // First restore previousData to undo optimistic updates (if available)
      if (context?.previousData) {
        queryClient.setQueryData(context.cacheKey, context.previousData);
      } else if (variables?.sessionId) {
        // If no previousData exists (new session), invalidate to force fresh fetch
        // This prevents showing stale optimistic data
        queryClient.invalidateQueries({ 
          queryKey: ['/api/smart-chat/sessions', variables.sessionId] 
        });
      }
      
      // Then clear BOTH pending flags from cache to prevent stuck "thinking" indicator
      // This must happen AFTER restoring previousData to avoid being overwritten
      if (variables?.sessionId) {
        queryClient.setQueryData(['/api/smart-chat/sessions', variables.sessionId], (old: any) => {
          if (!old) return old;
          // Remove client-side __pendingRequest flag
          const { __pendingRequest, ...rest } = old;
          // Also clear server-side pendingResponse flag from session object
          // (server clears this on error, but client cache may be stale)
          if (rest.session?.pendingResponse) {
            return {
              ...rest,
              session: { ...rest.session, pendingResponse: false },
            };
          }
          return rest;
        });
      }
      
      // Show appropriate error message
      const errorMessage = isTimeoutError
        ? 'The request took too long. Your message may still be processing - try refreshing in a moment.'
        : 'We could not send your message. Please try again.';
      
      toast({
        title: 'Message failed',
        description: errorMessage,
        variant: 'destructive' as any,
      });
    }
  });
  const linkContactMutation = useMutation({
    mutationFn: ({ sessionId, contactId }: { sessionId: number; contactId: number }) =>
      apiRequest('POST', `/api/smart-chat/sessions/${sessionId}/links/contacts`, {
        contactId,
      }),
    retry: 2,
    onMutate: async ({ sessionId, contactId }) => {
      const cacheKey = ['/api/smart-chat/sessions', sessionId];
      await queryClient.cancelQueries({ queryKey: cacheKey });
      const previousData = queryClient.getQueryData(cacheKey);
      const contact = contactsData.find((c: any) => c.id === contactId);
      queryClient.setQueryData(cacheKey, (old: any) => {
        if (!old) return old;
        const current = old.linkedContacts || [];
        if (current.some((c: any) => getContactIdentifier(c) === normalizeIdentifier(contactId))) {
          return old;
        }
        const optimistic = {
          ...(contact || {}),
          contactId: normalizeIdentifier(contactId),
          clientId: normalizeIdentifier(contact?.clientId ?? contactId),
          id: normalizeIdentifier(contact?.id ?? contactId),
          linkedAt: new Date().toISOString(),
        };
        return {
          ...old,
          linkedContacts: [...current, optimistic],
        };
      });
      return { previousData, cacheKey };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(context.cacheKey, context.previousData);
      }
      toast({
        title: 'Link failed',
        description: 'Could not link client to thread. Please try again.',
        variant: 'destructive' as any,
      });
    },
    onSuccess: (data, variables) => {
      const cacheKey = ['/api/smart-chat/sessions', variables.sessionId];
      queryClient.setQueryData(cacheKey, (old: any) => {
        if (!data) return old;

        const merged = old ? { ...old, ...data } : data;
        const normalizedContacts = (merged.linkedContacts || merged.linkedClients || [])
          .map((contact: any) => {
            const contactId = getContactIdentifier(contact);
            return {
              ...contact,
              linkId: contact.linkId ?? contact.id,
              contactId,
              clientId: normalizeIdentifier(contact.clientId ?? contactId),
              id: contactId,
            };
          });

        return {
          ...merged,
          linkedContacts: normalizedContacts,
          linkedClients: normalizedContacts,
        };
      });
      queryClient.invalidateQueries({ queryKey: [`/api/contacts/${variables.contactId}/threads`] });
    },
  });

  const unlinkContactMutation = useMutation({
    mutationFn: ({ sessionId, contactId }: { sessionId: number; contactId: number }) =>
      apiRequest('DELETE', `/api/smart-chat/sessions/${sessionId}/links/contacts/${contactId}`, {}),
    retry: 2,
    onMutate: async ({ sessionId, contactId }) => {
      const cacheKey = ['/api/smart-chat/sessions', sessionId];
      await queryClient.cancelQueries({ queryKey: cacheKey });
      const previousData = queryClient.getQueryData(cacheKey);
      queryClient.setQueryData(cacheKey, (old: any) => {
        if (!old) return old;
        const current = old.linkedContacts || [];
        return {
          ...old,
          linkedContacts: current.filter(
            (contact: any) => getContactIdentifier(contact) !== normalizeIdentifier(contactId),
          ),
        };
      });
      return { previousData, cacheKey };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(context.cacheKey, context.previousData);
      }
      toast({
        title: 'Unlink failed',
        description: 'Could not unlink client from thread. Please try again.',
        variant: 'destructive' as any,
      });
    },
    onSuccess: (data, variables) => {
      const cacheKey = ['/api/smart-chat/sessions', variables.sessionId];
      queryClient.setQueryData(cacheKey, (old: any) => (old ? { ...old, ...data } : data));
      queryClient.invalidateQueries({ queryKey: [`/api/contacts/${variables.contactId}/threads`] });
    },
  });

  const linkTripMutation = useMutation({
    mutationFn: ({ sessionId, tripId }: { sessionId: number; tripId: number }) =>
      apiRequest('POST', `/api/smart-chat/sessions/${sessionId}/links/trips`, {
        tripId,
      }),
    retry: 2,
    onMutate: async ({ sessionId, tripId }) => {
      const cacheKey = ['/api/smart-chat/sessions', sessionId];
      await queryClient.cancelQueries({ queryKey: cacheKey });
      const previousData = queryClient.getQueryData(cacheKey);
      const trip = tripsData.find((t: any) => t.id === tripId);
      queryClient.setQueryData(cacheKey, (old: any) => {
        if (!old) return old;
        const current = old.linkedTrips || [];
        const normalizedTripId = normalizeIdentifier(tripId);
        if (current.some((t: any) => getTripIdentifier(t) === normalizedTripId)) {
          return old;
        }
        const optimistic = {
          ...(trip || {}),
          tripId: normalizedTripId,
          id: normalizeIdentifier(trip?.id ?? tripId),
          linkId: trip?.linkId ?? trip?.id ?? null,
          linkedAt: new Date().toISOString(),
        };
        return {
          ...old,
          linkedTrips: [...current, optimistic],
        };
      });
      return { previousData, cacheKey };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(context.cacheKey, context.previousData);
      }
      toast({
        title: 'Link failed',
        description: 'Could not link trip to thread. Please try again.',
        variant: 'destructive' as any,
      });
    },
    onSuccess: (data, variables) => {
      const cacheKey = ['/api/smart-chat/sessions', variables.sessionId];
      queryClient.setQueryData(cacheKey, (old: any) => {
        if (!data) return old;
        const merged = old ? { ...old, ...data } : data;

        const normalizedTrips = (merged.linkedTrips || []).map((trip: any) => {
          const tripId = getTripIdentifier(trip);
          return {
            ...trip,
            linkId: trip.linkId ?? trip.id,
            tripId,
            id: tripId,
          };
        });

        return {
          ...merged,
          linkedTrips: normalizedTrips,
        };
      });
      queryClient.invalidateQueries({ queryKey: [`/api/trips/${variables.tripId}/threads`] });
    },
  });

  const unlinkTripMutation = useMutation({
    mutationFn: ({ sessionId, tripId }: { sessionId: number; tripId: number }) =>
      apiRequest('DELETE', `/api/smart-chat/sessions/${sessionId}/links/trips/${tripId}`, {}),
    retry: 2,
    onMutate: async ({ sessionId, tripId }) => {
      const cacheKey = ['/api/smart-chat/sessions', sessionId];
      await queryClient.cancelQueries({ queryKey: cacheKey });
      const previousData = queryClient.getQueryData(cacheKey);
      queryClient.setQueryData(cacheKey, (old: any) => {
        if (!old) return old;
        const current = old.linkedTrips || [];
        return {
          ...old,
          linkedTrips: current.filter(
            (trip: any) => getTripIdentifier(trip) !== normalizeIdentifier(tripId),
          ),
        };
      });
      return { previousData, cacheKey };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(context.cacheKey, context.previousData);
      }
      toast({
        title: 'Unlink failed',
        description: 'Could not unlink trip from thread. Please try again.',
        variant: 'destructive' as any,
      });
    },
    onSuccess: (data, variables) => {
      const cacheKey = ['/api/smart-chat/sessions', variables.sessionId];
      queryClient.setQueryData(cacheKey, (old: any) => (old ? { ...old, ...data } : data));
      queryClient.invalidateQueries({ queryKey: [`/api/trips/${variables.tripId}/threads`] });
    },
  });

  const handleLinkContact = (contactId: number) => {
    if (!activeSessionId) return;
    linkContactMutation.mutate({ sessionId: activeSessionId, contactId });
  };

  const handleUnlinkContact = (contactId: number) => {
    if (!activeSessionId) return;
    unlinkContactMutation.mutate({ sessionId: activeSessionId, contactId });
  };

  const handleLinkTrip = (tripId: number) => {
    if (!activeSessionId) return;
    linkTripMutation.mutate({ sessionId: activeSessionId, tripId });
  };

  const handleUnlinkTrip = (tripId: number) => {
    if (!activeSessionId) return;
    unlinkTripMutation.mutate({ sessionId: activeSessionId, tripId });
  };

  // Process message content to extract components
  const processMessageContent = (content: string) => {
    const parts: Array<{ type: 'text' | 'component'; content?: string; componentType?: string; data?: any; key?: string }> = [];
    
    // Remove source citations
    const cleanedContent = content.replace(/\[\d+\]/g, '').trim();
    
    // Parse CRM components
    const crmTagPattern = /<(contact-card|contact-list|trip-card|trip-list)\s*data='([^']+)'><\/\1>/g;
    let match;
    let lastIndex = 0;

    while ((match = crmTagPattern.exec(cleanedContent)) !== null) {
      if (match.index > lastIndex) {
        const textContent = cleanedContent.slice(lastIndex, match.index).trim();
        if (textContent) {
          parts.push({
            type: 'text',
            content: textContent,
            key: `text-${lastIndex}`
          });
        }
      }

      const tagName = match[1];
      const dataString = match[2];

      try {
        const data = JSON.parse(dataString);
        parts.push({
          type: 'component',
          componentType: tagName,
          data: data,
          key: `component-${match.index}`
        });
      } catch (error) {
        console.error('Error parsing CRM component data:', error);
        parts.push({
          type: 'text',
          content: match[0],
          key: `error-${match.index}`
        });
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < cleanedContent.length) {
      const remainingContent = cleanedContent.slice(lastIndex).trim();
      if (remainingContent) {
        parts.push({
          type: 'text',
          content: remainingContent,
          key: `text-${lastIndex}`
        });
      }
    }

    return parts;
  };

  // Handle direct data display (when clicking search results)
  const handleDirectDataDisplay = (type: 'contact' | 'trip' | 'supplier', data: any) => {
    if (type === 'contact') {
      // Check alpha gate for clients
      if (isFeatureGated(alphaGates, 'clients')) {
        toast({
          title: 'Coming soon...',
          description: 'The Clients feature will be available in a future update.',
          duration: 4000,
        });
        return;
      }
      setCurrentCanvas('clients');
      setCanvasData({ contact: data });
    } else if (type === 'trip') {
      // Check alpha gate for trips
      if (isFeatureGated(alphaGates, 'trips')) {
        toast({
          title: 'Coming soon...',
          description: 'The Trips feature will be available in a future update.',
          duration: 4000,
        });
        return;
      }
      setCurrentCanvas('trips');
      setCanvasData({ trip: data });
    } else if (type === 'supplier') {
      // For now, just log the supplier data - we can implement a supplier detail view later
      // Could navigate to a suppliers canvas or show detail modal
    }
  };

  // Handle command submission - use real chat functionality
  // NOTE: explicitProjectId parameter fixes a race condition where async state updates
  // from setActiveProjectId() haven't taken effect when this function reads activeProjectId.
  // Callers should pass the intended projectId explicitly rather than relying on state.
  const handleCommand = async (
    command: string,
    toolSignal?: ToolSignal | null,
    explicitProjectId?: number | null,
    agentAttachments?: AgentAttachment[],
    explicitGlobal?: boolean,
  ) => {
    // Use explicitly passed projectId if provided, otherwise fall back to state
    const currentProjectId = explicitProjectId !== undefined ? explicitProjectId : activeProjectId;
    
    // Always go to threads view when starting a conversation
    setCurrentCanvas('threads');
    
    try {
      let sessionId = activeSessionId;
      
      // Create a new session if none exists
      if (!sessionId) {
        const newSession = await createSessionMutation.mutateAsync({
          title: command.slice(0, 50) + (command.length > 50 ? '...' : ''),
          provider: selectedProvider,
          model: selectedModel,
          projectId: currentProjectId,
          originalPrompt: command,
        });
        sessionId = newSession.id;
        
        // Apply pending external mode to the new session if it exists
        if (pendingExternalMode?.enabled && sessionId) {
          const newSessionId = sessionId; // Capture for closure
          setExternalModeBySession(prev => ({
            ...prev,
            [newSessionId]: pendingExternalMode,
          }));
        }
        // Session cache is already seeded in createSessionMutation.onSuccess
        // with the complete structure. Skipping refetchQueries here to avoid
        // a race condition where the refetch returns messages:[] and a
        // background re-render overwrites the optimistic message added by
        // sendMessageMutation.onMutate.
      }
      
      // Send the message using the real chat API
      if (sessionId) {
        // Check if this session is in external mode (session-specific or pending)
        const sessionExternalMode = externalModeBySession[sessionId] || pendingExternalMode;
        const externalModePayload = sessionExternalMode?.enabled
          ? { provider: sessionExternalMode.provider, model: sessionExternalMode.model }
          : null;
        
        // Clear pending external mode after it's been used
        if (pendingExternalMode?.enabled) {
          setPendingExternalMode(null);
        }

        // AgentCore engine for flagged users. External (direct-model) mode and slash-command
        // tool signals keep their existing behaviour on the current engine.
        if (routesToAgent({ flagEnabled: agentcoreThreadsEnabled, externalMode: externalModePayload, toolSignal })) {
          // Global/Project toggle in a project thread: choosing Global (the composer reports it as
          // explicitGlobal; the footer select also clears the active project first) leaves the
          // project out of this thread until Project is chosen again. Opening a project thread from
          // the thread list clears the active project too, so that alone is not a Global choice.
          const sessionProjectId = sessions?.find((s) => s.id === sessionId)?.chatProjectId ?? null;
          if (sessionProjectId && explicitProjectId === null && (explicitGlobal || activeProjectId === sessionProjectId)) {
            globalScopeSessions.current.add(sessionId);
          } else if (sessionProjectId && explicitProjectId === sessionProjectId) {
            globalScopeSessions.current.delete(sessionId);
          }
          await agentThread.send(sessionId, command, {
            projectContext: !globalScopeSessions.current.has(sessionId),
            attachments: agentAttachments,
          });
          return;
        }

        await sendMessageMutation.mutateAsync({
          sessionId: sessionId,
          content: command,
          toolSignal: toolSignal ?? null,
          externalMode: externalModePayload,
        });
      }
    } catch (error) {
      console.error('Error in handleCommand:', error);
      setStreamingRefetchDone(true);
      setIsWaitingForResponse(false);
    }
  };

  // Render the appropriate canvas
  // [DUPLICATE_PROTECTION] Use isWaitingForResponse OR mutation.isPending to prevent double submissions
  // This replaces the static isProcessing state that was never being set
  const isProcessingMessage = isWaitingForResponse || sendMessageMutation.isPending || agentThread.isStreaming;

  const handleDashboardNavigate = useCallback(
    (canvas: CanvasType) => {
      if (canvas === 'threads') {
        setActiveSessionId(null);
        setActiveProjectId(null);
        setSelectedProject(null);
        setCurrentCanvas('threads');
        navigate('/threads');
        return;
      }
      if (canvas === 'payments') {
        navigate(FINANCES_PATHS.invoices);
        return;
      }
      setCurrentCanvas(canvas);
    },
    [navigate],
  );

  const renderCanvas = () => {
    switch (currentCanvas) {
      case 'home':
        return (
          <DashboardCanvas 
            onNavigate={handleDashboardNavigate}
            onCommand={(cmd, toolSignal, agentAttachments) => handleCommand(cmd, toolSignal, undefined, agentAttachments)}
            isProcessing={isProcessingMessage}
            onDirectDataDisplay={handleDirectDataDisplay}
            contactsData={contactsData}
            tripsData={tripsData}
            searchDataLoaded={searchDataLoaded}
            sidebarExpanded={sidebarExpanded}
            providers={providers}
            selectedProvider={selectedProvider}
            selectedModel={selectedModel}
            onProviderChange={setSelectedProvider}
            onModelChange={setSelectedModel}
            advancedModeEnabled={userPreferences?.advancedMode ?? false}
            externalModeEnabled={isExternalModeEnabled}
            externalModeProvider={currentExternalMode?.provider}
            externalModeModel={currentExternalMode?.model}
            onOpenDefaultModel={() => setShowDefaultModelSheet(true)}
            onToggleExternalMode={toggleExternalMode}
            onExternalProviderChange={updateExternalModeProvider}
            onExternalModelChange={updateExternalModeModel}
            onInlineCommandBarVisibilityChange={setHomeInlineCommandBarVisible}
          />
        );
      case 'dashboard':
        return <CanvasDashboard />;
      case 'clients':
        return <CanvasClients />;
      case 'trips':
        return <CanvasTrips />;
      case 'analytics':
        return featureFlags.analytics
          ? <AnalyticsCanvas data={canvasData} />
          : (
              <DeferredFeaturePlaceholder
                featureName="Analytics"
                description="Analytics dashboards will unlock once Phase 2 kicks off. Flip the analytics feature flag when you're ready to surface the module again."
                icon={<BarChart3 size={48} />}
              />
            );
      case 'pipeline':
        return <PipelineCanvas data={canvasData} />;
      case 'content':
        return featureFlags.digitalPresence
          ? <ContentCanvas data={canvasData} />
          : (
              <DeferredFeaturePlaceholder
                featureName="Digital Presence"
                description="The Digital Presence workspace is deferred. Enable the digitalPresence flag in configuration to bring these tools back online."
                icon={<Globe size={48} />}
              />
            );
      case 'proposals':
        return <ProposalsCanvas data={canvasData} />;
      case 'booking':
        return featureFlags.booking
          ? <BookingCanvas data={canvasData} />
          : (
              <DeferredFeaturePlaceholder
                featureName="Booking"
                description="Booking workflows are deferred to a later milestone. Re-enable the booking flag when Phase 2 begins."
                icon={<Plane size={48} />}
              />
            );
      case 'payments':
        return featureFlags.commissions
          ? <PaymentsCanvas data={canvasData} />
          : (
              <DeferredFeaturePlaceholder
                featureName="Commissions"
                description="Commission management is currently disabled. Toggle the commissions flag to reactivate payout tracking."
                icon={<CreditCard size={48} />}
              />
            );
      case 'threads':
        // If there's no active session and no active project, show sample queries
        // Check activeSessionId (intent) not just activeSessionData (loaded data) to prevent flicker during load
        if (!activeSessionId && !activeSessionData && !activeProjectId) {
          return <SampleQueriesCanvas 
            onQuerySelect={(query, toolSignal, agentAttachments) => handleCommand(query, toolSignal, undefined, agentAttachments)}
            messageInput={messageInput}
            setMessageInput={setMessageInput}
            isProcessing={isProcessingMessage}
            sidebarExpanded={sidebarExpanded}
            onDirectDataDisplay={handleDirectDataDisplay}
            contactsData={contactsData}
            tripsData={tripsData}
            searchDataLoaded={searchDataLoaded}
            providers={providers}
            selectedProvider={selectedProvider}
            selectedModel={selectedModel}
            onProviderChange={setSelectedProvider}
            onModelChange={setSelectedModel}
            advancedModeEnabled={userPreferences?.advancedMode ?? false}
            externalModeEnabled={isExternalModeEnabled}
            externalModeProvider={currentExternalMode?.provider}
            externalModeModel={currentExternalMode?.model}
            onOpenDefaultModel={() => setShowDefaultModelSheet(true)}
            onToggleExternalMode={toggleExternalMode}
            onExternalProviderChange={updateExternalModeProvider}
            onExternalModelChange={updateExternalModeModel}
          />;
        }
        // Show loading state while session data is being fetched to prevent flicker
        if (activeSessionId && !activeSessionData && isLoadingSession) {
          return (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                <span className="text-sm text-muted-foreground">Loading thread...</span>
              </div>
            </div>
          );
        }
        return <ThreadsCanvas
          sessionData={activeSessionData}
          isWaitingForResponse={shouldShowThinking}
          streamingRefetchDone={streamingRefetchDone}
          streamingState={streamingResult}
          simulatedStreamingContent={simulatedStreamingContent}
          agentThread={agentcoreThreadsEnabled ? agentThread : undefined}
          providers={providers}
          selectedProvider={selectedProvider}
          selectedModel={selectedModel}
          onProviderChange={setSelectedProvider}
          onModelChange={setSelectedModel}
          activeSessionId={activeSessionId}
          projects={projects}
          // hasActivities={(activities?.length || 0) > 0} // Activity Stream - COMMENTED OUT FOR LATER
          // onOpenActivityStream={() => setActivityStreamOpen(true)} // Activity Stream - COMMENTED OUT FOR LATER
          onBackToProject={(projectId) => {
            const project = projects.find(p => p.id === projectId);
            setActiveProjectId(projectId);
            setSelectedProject(project);
            setActiveSessionId(null); // Clear active session when going back to project
            setCurrentCanvas('project');
          }}
          contactOptions={contactsData ?? EMPTY_CONTACTS}
          tripOptions={tripsData ?? EMPTY_TRIPS}
          onLinkContact={handleLinkContact}
          onUnlinkContact={handleUnlinkContact}
          onLinkTrip={handleLinkTrip}
          onUnlinkTrip={handleUnlinkTrip}
          linkingContact={linkContactMutation.isPending}
          linkingTrip={linkTripMutation.isPending}
          onNewThread={() => {
            toast({
              title: "New thread started",
              description: "Your previous conversation is saved. Start fresh!",
            });
            setActiveSessionId(null);
            setActiveProjectId(null);
            setSelectedProject(null);
            setCurrentCanvas('threads');
            navigate('/threads');
          }}
          onResendPrompt={(prompt) => handleCommand(prompt)}
        />;
      case 'project':
        return <ProjectCanvas 
          project={selectedProject}
          sessions={sessions || []}
          activeSessionId={activeSessionId}
          onSessionSelect={(sessionId) => {
            setActiveSessionId(sessionId);
            setCurrentCanvas('threads');
            navigate('/threads');
          }}
          onNewThread={() => {
            setActiveSessionId(null);
            setCurrentCanvas('threads');
            navigate('/threads');
          }}
          onBackToThreads={() => {
            setSelectedProject(null);
            setActiveProjectId(null);
            setActiveSessionId(null); // Clear active session when leaving project
            setCurrentCanvas('threads');
          }}
        />;
      default:
        return (
          <DashboardCanvas 
            onNavigate={handleDashboardNavigate}
            onCommand={(cmd, toolSignal, agentAttachments) => handleCommand(cmd, toolSignal, undefined, agentAttachments)}
            isProcessing={isProcessingMessage}
            onDirectDataDisplay={handleDirectDataDisplay}
            contactsData={contactsData}
            tripsData={tripsData}
            searchDataLoaded={searchDataLoaded}
            sidebarExpanded={sidebarExpanded}
            providers={providers}
            selectedProvider={selectedProvider}
            selectedModel={selectedModel}
            onProviderChange={setSelectedProvider}
            onModelChange={setSelectedModel}
            advancedModeEnabled={userPreferences?.advancedMode ?? false}
            externalModeEnabled={isExternalModeEnabled}
            externalModeProvider={currentExternalMode?.provider}
            externalModeModel={currentExternalMode?.model}
            onOpenDefaultModel={() => setShowDefaultModelSheet(true)}
            onToggleExternalMode={toggleExternalMode}
            onExternalProviderChange={updateExternalModeProvider}
            onExternalModelChange={updateExternalModeModel}
            onInlineCommandBarVisibilityChange={setHomeInlineCommandBarVisible}
          />
        );
    }
  };

  // Activity Stream event listeners - COMMENTED OUT FOR LATER
  // useEffect(() => {
  //   const open = () => setActivityStreamOpen(true);
  //   const close = () => setActivityStreamOpen(false);
  //   window.addEventListener('openActivityStream', open as EventListener);
  //   window.addEventListener('closeActivityStream', close as EventListener);
  //   return () => {
  //     window.removeEventListener('openActivityStream', open as EventListener);
  //     window.removeEventListener('closeActivityStream', close as EventListener);
  //   };
  // }, []);

  // The bottom-anchored command bar overlays the canvas, so the canvas has to
  // reserve clearance for it. Home renders its own centered inline bar until it
  // scrolls out of view, and an empty thread renders one inline too.
  const showFixedCommandBar =
    currentCanvas === 'home'
      ? !homeInlineCommandBarVisible
      : !(currentCanvas === 'threads' && !activeSessionData && !activeProjectId);

  /* AR-316 — one ThreadsPanel element, rendered as the docked level-2
     column on desktop and inside the mobile tab bar's sheet below `md`.
     Hoisted so the two placements cannot drift apart. */
  const threadsPanelEl = currentCanvas !== 'home' ? (
        <ThreadsPanel
          sessions={sessions || []}
          activeSessionId={activeSessionId}
          activeProjectId={activeProjectId}
          onOpenSearch={() => {
            setThreadSearchQuery('');
            setShowThreadSearchDialog(true);
          }}
          onClose={() => {
            setActiveSessionId(null);
            setShowThreadsPanel(false);
            setCurrentCanvas('home');
          }}
          onSessionSelect={(sessionId) => {
            handleThreadSelect(sessionId, {});
          }}
          onProjectSelect={(projectId, project) => {
            handleProjectSelect(projectId, project, {});
          }}
          onNewThread={() => {
            const alreadyInNewThread = !activeSessionId && !activeProjectId && currentCanvas === 'threads';
            if (alreadyInNewThread) {
              toast({
                title: "Ready for a new conversation",
                description: "You're already on a fresh thread. Start typing!",
              });
              const commandInput = document.querySelector('[data-command-input="true"]') as HTMLTextAreaElement;
              if (commandInput) {
                commandInput.focus();
              }
              return;
            }
            setActiveSessionId(null);
            setActiveProjectId(null);
            setSelectedProject(null);
            setCurrentCanvas('threads');
            navigate('/threads');
          }}
          onNavigateToDashboard={() => {
            setActiveSessionId(null);
            setShowThreadsPanel(false);
            setCurrentCanvas('home');
          }}
          onRenameStart={() => setRenameLock(true)}
          onRenameEnd={() => setRenameLock(false)}
          onHoverClickLock={() => {}}
        />
  ) : null;

  return (
    <div className="flex h-screen flex-col bg-gray-50 dark:bg-gray-900 overflow-hidden">
      <GlobalHeader onLogoClick={() => {
        setActiveSessionId(null);
        setActiveProjectId(null);
        setSelectedProject(null);
        setShowThreadsPanel(false);
        setCurrentCanvas('home');
      }} />
      <div className="relative flex-1 overflow-hidden min-h-0">
      {/* Top Bar: Activity Stream button - COMMENTED OUT FOR LATER */}
      {/* {currentCanvas !== 'threads' && (
        <div className="absolute top-0 left-0 right-0 z-30 flex justify-end items-center p-4 pointer-events-none">
          <button
            onClick={() => setActivityStreamOpen(true)}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors relative pointer-events-auto"
            title="Activity Stream"
          >
            <Sparkles className="w-6 h-6" />
            {activities.length > 0 && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-600 rounded-full border-2 border-white"></span>
            )}
          </button>
        </div>
      )} */}

      {/* Main Content Area */}
      <div className="flex h-full min-h-0 pt-0">
        {/* Sidebar — desktop only; below `md` navigation is the bottom tab bar */}
        <div className="hidden md:flex">
        <TraditionalSidebar 
          onNavigate={(canvas) => {
            // Clear active session when navigating away from threads/project canvases
            if (canvas !== 'threads' && canvas !== 'project') {
              setActiveSessionId(null);
              setActiveProjectId(null);
              setSelectedProject(null);
            }
            
            // For certain canvases, navigate to their dedicated routes
            if (canvas === 'trips') {
              navigate('/trips');
              return;
            }
            if (canvas === 'pipeline') {
              navigate('/pipeline');
              return;
            }
            if (canvas === 'threads') {
              navigate('/threads');
              return;
            }
            if (canvas === 'clients') {
              navigate('/clients');
              return;
            }
            if (canvas === 'payments') {
              navigate(FINANCES_PATHS.invoices);
              return;
            }
            if (canvas === 'unclaimed') {
              navigate(FINANCES_PATHS.unclaimed);
              return;
            }
            if (canvas === 'statements') {
              navigate(FINANCES_PATHS.statements);
              return;
            }
            if (canvas === 'reports') {
              if (!isResolvedFeatureFlagEnabled('invoicingMvp')) {
                toast({
                  description: 'Financial Snapshot is not available.',
                });
                return;
              }
              navigate(FINANCES_PATHS.snapshots);
              return;
            }
            
            // For other canvases, just switch the view
            setCurrentCanvas(canvas);
          }}
          expanded={sidebarExpanded}
          onToggleExpanded={() => {
            setSidebarExpanded(!sidebarExpanded);
          }}
          user={user}
          userMenuOpen={userMenuOpen}
          setUserMenuOpen={setUserMenuOpen}
          logout={logout}
          onThreadsMouseEnter={handleThreadsMouseEnter}
          onThreadsMouseLeave={handleThreadsMouseLeave}
          hideClients={false}
          alphaGates={alphaGates}
          onImportLegacyChats={openImportModalForRecheck}
        />
        </div>
        
        {/* Threads Panel — desktop column; the mobile copy lives in the tab bar's sheet */}
        <div className="hidden md:flex">{threadsPanelEl}</div>

        {/* Canvas Area — when the fixed command bar overlays the canvas, clear it
            using its measured height (which now includes the expanded suggestions
            panel); 6rem matches the previous pb-24 before the first measurement.
            With the centered inline bar there is nothing to clear, so keep pb-24. */}
        {/* AR-316: `min-w-0` is load-bearing. A flex item defaults to
            `min-width: auto`, so this column refuses to shrink below the
            min-content width of the conversation inside it — on a phone that
            made it 693px wide in a 375px viewport, and since the row's ancestor
            clips rather than scrolls, everything past 375px was simply
            unreachable: message text cut mid-sentence, the rating scale and the
            feedback box running off the edge. */}
        {/* AR-210 (TC-33.1, TC-34.5): the canvas column is the page's main landmark. */}
        <main
          className={`flex-1 flex min-w-0 flex-col${showFixedCommandBar ? '' : ' pb-24'}`}
          // --aivia-side-panel: width of a docked AIVIA draft panel (AgentMarkdown), so the chat stays beside it.
          style={{
            paddingRight: 'var(--aivia-side-panel, 0px)',
            ...(showFixedCommandBar ? { paddingBottom: 'var(--command-bar-height, 6rem)' } : {}),
          }}
        >
          {renderCanvas()}
        </main>
      </div>

      {/* Command Bar - shown for home when inline bar scrolls out of view, hidden for empty threads (rendered inline) */}
      {showFixedCommandBar && (
        <CommandBar 
          onSubmit={(cmd, scope, toolSignal, agentAttachments) => {
            // Capture the explicit project ID before state updates
            const explicitProjectId = scope?.projectId !== undefined ? scope.projectId : activeProjectId;
            
            if (scope?.projectId !== undefined) {
              if (scope.projectId === null) {
                setActiveProjectId(null);
                setSelectedProject(null);
              } else {
                const p = projects.find(p => p.id === scope.projectId);
                setActiveProjectId(scope.projectId);
                setSelectedProject(p || null);
              }
            }
            // Pass explicit projectId to avoid race condition with async state updates
            handleCommand(cmd, toolSignal, explicitProjectId, agentAttachments, scope?.explicitGlobal);
          }} 
          isProcessing={isProcessingMessage} 
          onDirectDataDisplay={handleDirectDataDisplay}
          contactsData={contactsData}
          tripsData={tripsData}
          searchDataLoaded={searchDataLoaded}
          sidebarExpanded={sidebarExpanded}
          activeProject={selectedProject}
          projectsData={projects}
          onSetScopeProject={(projectId, project) => {
            if (projectId === null) {
              setActiveProjectId(null);
              setSelectedProject(null);
              return;
            }
            const p = project || projects.find(p => p.id === projectId) || null;
            setActiveProjectId(projectId);
            setSelectedProject(p);
          }}
          contactOptions={contactsData ?? EMPTY_CONTACTS}
          tripOptions={tripsData ?? EMPTY_TRIPS}
          onLinkContact={crmVisible ? handleLinkContact : undefined}
          onLinkTrip={crmVisible ? handleLinkTrip : undefined}
          linkedContacts={crmVisible ? (activeSessionData?.linkedContacts ?? EMPTY_LINKED) : EMPTY_LINKED}
          linkedTrips={crmVisible ? (activeSessionData?.linkedTrips ?? EMPTY_LINKED) : EMPTY_LINKED}
          providers={providers}
          selectedProvider={selectedProvider}
          selectedModel={selectedModel}
          onProviderChange={setSelectedProvider}
          onModelChange={setSelectedModel}
          advancedModeEnabled={userPreferences?.advancedMode ?? false}
          externalModeEnabled={isExternalModeEnabled}
          externalModeProvider={currentExternalMode?.provider}
          externalModeModel={currentExternalMode?.model}
          onOpenDefaultModel={() => setShowDefaultModelSheet(true)}
          onToggleExternalMode={toggleExternalMode}
          onExternalProviderChange={updateExternalModeProvider}
          onExternalModelChange={updateExternalModeModel}
        />
      )}

      {/* Default Model Sheet */}
      <DefaultModelSheet
        open={showDefaultModelSheet}
        onOpenChange={setShowDefaultModelSheet}
      />

      {/* Threads/Projects Search Dialog */}
      <CommandDialog
        title="Search threads and projects"
        open={showThreadSearchDialog}
        onOpenChange={(open) => {
          setShowThreadSearchDialog(open);
          if (!open) {
            setThreadSearchQuery('');
          }
        }}
      >
        <CommandInput
          value={threadSearchQuery}
          onValueChange={setThreadSearchQuery}
          placeholder="Search threads or projects..."
          aria-label="Search threads or projects"
        />
        <CommandList>
          {/* AR-210 (TC-35.2): result count for screen readers. */}
          <div className="sr-only" role="status" aria-live="polite">
            {threadSearchQuery.trim()
              ? `${threadSearchResults.projects.length} project${threadSearchResults.projects.length === 1 ? '' : 's'}, ${threadSearchResults.threads.length} thread${threadSearchResults.threads.length === 1 ? '' : 's'}`
              : ''}
          </div>
          <CommandEmpty>No threads or projects found.</CommandEmpty>

          {threadSearchResults.projects.length > 0 && (
            <CommandGroup heading="Projects">
              {threadSearchResults.projects.map((project: any) => (
                <CommandItem
                  key={`project-${project.id ?? project.projectId}`}
                  value={project.__searchName}
                  onSelect={() =>
                    handleProjectSelect(
                      project.id ?? project.projectId ?? null,
                      project,
                      { closePanel: false },
                    )
                  }
                >
                  <Folder className="mr-2 h-4 w-4 text-amber-600" />
                  <div className="flex flex-col text-left">
                    <span className="font-medium">{project.__searchName}</span>
                    {project.__searchDescription && (
                      <span className="text-xs text-muted-foreground">
                        {project.__searchDescription}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {threadSearchResults.threads.length > 0 && (
            <CommandGroup heading="Threads">
              {threadSearchResults.threads.map((thread: any) => (
                <CommandItem
                  key={`thread-${thread.id}`}
                  value={thread.__searchTitle}
                  onSelect={() =>
                    handleThreadSelect(thread.id, { closePanel: false })
                  }
                >
                  <MessageSquare className="mr-2 h-4 w-4 text-orange-600" />
                  <div className="flex flex-col text-left">
                    <span className="font-medium">{thread.__searchTitle}</span>
                    <span className="text-xs text-muted-foreground">
                      {thread.chatProjectId
                        ? `Project #${thread.chatProjectId}`
                        : 'Individual thread'}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>

      {/* Activity Stream - COMMENTED OUT FOR LATER */}
      {/* <ActivityStream
        isOpen={activityStreamOpen}
        onClose={() => setActivityStreamOpen(false)}
        activities={activities}
      /> */}

      {/* Overlay for Activity Stream - COMMENTED OUT FOR LATER */}
      {/* {activityStreamOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-30"
          onClick={() => setActivityStreamOpen(false)}
        />
      )} */}

      {/* User Menu Overlay */}
      {userMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setUserMenuOpen(false)}
        />
      )}

      {/* Legacy Chat Import Modal - shown on first login if user has legacy chats */}
      <LegacyChatImportModal
        open={showImportModal}
        onOpenChange={closeImportModal}
        onImportComplete={handleImportComplete}
        forceRecheck={isManualRecheck}
      />
    </div>
      {/* AR-316 — the v9 phone shell. The rail and the threads column leave the
          flow below `md`; the bar is a flex child so the row above simply gets
          56px less. */}
      <MobileTabBar
        footer={<UserMenu expanded inline />}
        closeKey={`${currentCanvas}:${activeSessionId ?? ''}:${activeProjectId ?? ''}`}
      >
        {() =>
          threadsPanelEl ? (
            <div className="max-h-[52vh] overflow-y-auto">
              {React.cloneElement(threadsPanelEl, {
                className: 'w-full border-r-0',
              })}
            </div>
          ) : null
        }
      </MobileTabBar>
    </div>
  );
};

export default CanvasUI; 
